/**
 * Star Map v2 — Skylit Studio
 * Clean-room rewrite. All state flows through setState → render.
 * Phase A: render pipeline (Celestial + milkyway + SVG compositor + print styles).
 * Phases B/C/D add customizer UI, cart integration, 3D preview.
 *
 * Contracts preserved from v1 (do not break, backend depends on them):
 *   - Globals: window.selectedPrintStyle, window.ribbonSelected,
 *     window.giftWrapSelected, window.bundleSelected
 *   - Hidden inputs: #custom_date, #custom_add, #custom_lat_lng, #skyMap, #qrcode
 *   - Legacy helpers called from other scripts: waitForStableRender,
 *     forceStableRedraw (re-exported on window)
 */
(function() {
  'use strict';

  // ── Constants (lifted verbatim from v1 starmap-render-engine.js) ─────────

  var PRINT_STYLES = {
    classic: {
      viewBox: '0 0 700 700',
      clip: 'M 0,350 a 350,350 0 1,1 700,0 a 350,350 0 1,1 -700,0',
      stageTransform: ''
    },
    photographer: {
      // Same 700x700 viewBox as Classic — photographer is same physical print size.
      // Rectangular star window = 5% inset on all sides of a 700x700 viewBox.
      // Zoom scale(1.6) keeps the visible 437.5² square inside the star circle.
      viewBox: '0 0 700 700',
      clip: 'M 35,35 H 665 V 665 H 35 Z',
      stageTransform: 'scale(1.6) translate(-131.25 -131.25)'
    },
    explorer: {
      // Taller print; scale(2.0) keeps visible 350×502.84 inside the star circle.
      viewBox: '0 0 700 1005.68',
      clip: 'M 0,0 H 700 V 1005.68 H 0 Z',
      stageTransform: 'scale(2.0) translate(-175 -98.58)'
    }
  };

  // tile_pos / tile_size: each theme's picker thumbnail crops a distinct
  // region of the same large milkyway image so the tiles don't all read as
  // "the same arc over black". Values are background-position / background-size
  // inline styles applied to .sm2-theme-preview.
  var THEMES = [
    {
      key: 'skylit-night', name: 'Skylit Night',
      bg: '#000000', fg: '#ffffff', stars: '#ffffff',
      milky_large: 'SKYLIT-night_milkyway-render.jpg',
      milky_small: 'SKYLIT-night_milkyway-render-web2026-seamless.webp?v=1792412067',
      no_milky_large: 'SKYLIT-night_milkyway-render2026.jpg',
      no_milky_small: 'SKYLIT-night_milkyway-render2026-web2026-seamless.webp?v=1792412066',
      hasMilkyWayOption: true,
      tile_pos: '25% 25%', tile_size: '220%'
    },
    {
      key: 'skylit-galaxy', name: 'Skylit Galaxy',
      bg: '#0a1628', fg: '#ffffff', stars: '#ffffff',
      milky_large: 're-skylit-galaxy_milkyway-render-mar17.jpg',
      milky_small: 're-skylit-galaxy_milkyway-render-mar17-web2026.webp?v=1778314466',
      no_milky_large: 'skylit-galaxy_milkyway-render-2026.jpg',
      no_milky_small: 'skylit-galaxy_milkyway-render-2026-web2026-seamless.webp?v=1792411472',
      hasMilkyWayOption: true,
      tile_pos: '75% 55%', tile_size: '260%'
    },
    {
      key: 'treeline', name: 'Treeline',
      bg: '#000000', fg: '#ffffff', stars: '#ffffff',
      milky_large: 'Skylit_studio_skylit-treeline-milkyway-render2026a.jpg',
      milky_small: 'Skylit_studio_skylit-treeline-milkyway-render2026a-web2026.webp?v=1778314446',
      no_milky_large: 'Skylit_studio_skylit-treeline-render2026a.jpg',
      no_milky_small: 'Skylit_studio_skylit-treeline-render2026a-web2026.webp?v=1778314450',
      hasMilkyWayOption: true,
      // 'cover', not a % width: the source is 2:1, so a width-relative size
      // computes a height of ~the tile height — any narrower sidebar
      // (Windows scrollbar, page zoom) left a white gap above the image.
      tile_pos: 'center 100%', tile_size: 'cover'
    },
    {
      key: 'aurora', name: 'Aurora',
      bg: '#000000', fg: '#ffffff', stars: '#ffffff',
      milky_large: 'aurora_milkyway-render6.jpg',
      milky_small: 'aurora_milkyway-render6-web2026-seamless.webp?v=1792412063',
      no_milky_large: 'aurora_milkyway-render2026.jpg',
      no_milky_small: 'aurora_milkyway-render2026-web2026-seamless.webp?v=1792412062',
      hasMilkyWayOption: true,
      tile_pos: '50% 20%', tile_size: '200%'
    },
    {
      key: 'red-velvet', name: 'Red Velvet',
      bg: '#000000', fg: '#ffffff', stars: '#ffffff',
      milky_large: 'Red-Love_milkywayy-render.jpg',
      milky_small: 'Red-Love_milkywayy-render-web2026-seamless.webp?v=1792412064',
      no_milky_large: 'Red-Love_no-milkyway-render.jpg',
      no_milky_small: 'Red-Love_no-milkyway-render-web2026-seamless.webp?v=1792412066',
      hasMilkyWayOption: true,
      tile_pos: '30% 70%', tile_size: '230%'
    }
  ];

  var CDN_BASE = 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/';

  // ── Phase C: backend / cart constants ────────────────────────────────────

  // admin.skylitstudio.co.uk is the Cloudflare Worker that owns star_maps in
  // D1 + the send-saved email queue. Same endpoints v1 (myjs.js) posts to,
  // keeps the render pipeline working byte-identically.
  var PROXY_BASE = 'https://admin.skylitstudio.co.uk/api/';
  var ADMIN_BASE = 'https://admin.skylitstudio.co.uk/';

  // Companion product variant IDs for gift wrap / ribbon / bundle lines.
  // v1 hard-codes these in starmap-gift-options.js + myjs.js; kept as-is so
  // cart payloads remain interchangeable between v1 and v2.
  var GIFT_IDS = {
    ribbon:   '55259137802617',
    giftwrap: '56160831930745',
    bundle:   '56160845988217'
  };

  // Prices in pence, parallel to GIFT_IDS. Used so the PDP "Total" row includes
  // the selected wrapping on top of the base variant price — without this the
  // label "Total" misrepresents what the customer actually pays at checkout
  // (the wrapping itself is still added as its own cart line item in
  // addGiftToCart; this is a display-only sum).
  var GIFT_PRICES = {
    none:     0,
    ribbon:   249,
    giftwrap: 369,
    bundle:   499
  };

  // Ribbon-colour swatches for the Wrapping section. Each entry pairs the
  // customer-facing colour name (written to the gift add-on cart property)
  // with the swatch chip colour and the per-colour ribbon overlay image
  // hosted on agent.skylitstudio.co.uk. `slug` is the colour name lowercased
  // with spaces hyphenated and drives the gift-ribbon-<slug>.png filename.
  var RIBBON_BASE = 'https://agent.skylitstudio.co.uk/';
  var RIBBON_COLORS = [
    { name: 'Red',         slug: 'red',         chip: '#b3261e' },
    { name: 'Gold',        slug: 'gold',        chip: '#c39c71' },
    { name: 'Pink',        slug: 'pink',        chip: '#d23c78' },
    { name: 'Evergreen',   slug: 'evergreen',   chip: '#1f5d44' },
    { name: 'Navy Blue',   slug: 'navy-blue',   chip: '#1d2b53' },
    { name: 'Baby Blue',   slug: 'baby-blue',   chip: '#a4c2dc' },
    { name: 'Pearl White', slug: 'pearl-white', chip: '#f0e9da' },
    { name: 'Black',       slug: 'black',       chip: '#1a1a1a' },
    // Printed satins (gift-ribbon-<slug>.png, same 612x765 geometry).
    { name: 'Gold Stars',      slug: 'gold-stars',      chip: '#8a4a22' },
    { name: 'Merry Christmas', slug: 'merry-christmas', chip: '#b3261e' }
  ];
  function ribbonImageFor(colorName) {
    var match = null;
    for (var i = 0; i < RIBBON_COLORS.length; i++) {
      if (RIBBON_COLORS[i].name === colorName) { match = RIBBON_COLORS[i]; break; }
    }
    var slug = match ? match.slug : 'red';
    return RIBBON_BASE + 'gift-ribbon-' + slug + '.png';
  }

  // Frame textures (verbatim from locationmap.js:28). Border-image CDN assets
  // that give the preview a physical wooden/metal frame look. Used when
  // selectedType === 'FRAMED'; Print/Canvas clear all border styling.
  var FRAME_TEXTURES = {
    Black:        { color: 'rgba(33,33,33,0.75)', image: 'url(https://cdn.shopify.com/s/files/1/0710/5278/7987/t/104/assets/frame-black-v3.webp) 100 / 1 / 0 stretch' },
    White:        { color: 'rgb(255,255,255)',     image: 'url(https://cdn.shopify.com/s/files/1/0710/5278/7987/t/104/assets/frame-white-v3.webp) 115 / 1 / 0 stretch' },
    Natural:      { color: 'rgba(0,0,0,0)',        image: 'url(https://cdn.shopify.com/s/files/1/0710/5278/7987/t/104/assets/frame-natural-v3.webp) 110 / 1 / 0 stretch' },
    'Dark Brown': { color: 'rgba(0,0,0,0)',        image: 'url(https://cdn.shopify.com/s/files/1/0710/5278/7987/t/104/assets/frame-dark-brown-v3.webp) 110 / 1 / 0 stretch' }
  };

  // ── State ────────────────────────────────────────────────────────────────

  var state = {
    // Wizard (Phase B)
    step: 1,

    // Temporal
    date: null,               // ISO YYYY-MM-DD
    time: '21:00',
    timezone: null,
    utcDateTime: null,

    // Spatial
    location: 'London, UK',
    coords: { lat: 51.5074, lng: -0.1278 },
    coordsDisplay: '',
    // Display-only overrides. Empty string = use auto (state.location /
    // state.coordsDisplay). Non-empty = printed caption + cart property +
    // admin `address`/`coordinates` columns show this instead. The geocode
    // search + lat/lng used for the star chart are never affected.
    locationOverride: '',
    coordsOverride: '',
    // Set true when the user DELETES a pre-filled override (vs never touching
    // it). A deliberately-cleared line prints blank with its spacing preserved
    // — effectiveLocation/effectiveCoords return a non-breaking space, which the
    // renderer's `div:empty{display:none}` rule does NOT collapse — whereas an
    // untouched override still falls back to the auto value.
    locationOverrideCleared: false,
    coordsOverrideCleared: false,
    // Display-only override for the printed date/time line. Empty = use the
    // auto value (computedDateTime, honouring showTime). Print-text only —
    // never feeds the star chart calc or timezone logic.
    dateTimeOverride: '',

    // Render
    printStyle: 'classic',    // classic | photographer | explorer
    composition: 'classic',   // classic | boxed | panel | header | gradient (caption treatment)
    shape: 'circle',          // circle | azimuth | zodiac | heart (any composition but header)
    themeKey: 'skylit-night',
    showMilkyway: true,
    showTime: false,          // caption toggle: show time next to date

    // Star map elements (ported from v1 starmap.liquid Step 4)
    showConstellations: true,
    showGrid: false,
    showLabels: false,
    showPlanets: false,
    showBorder: false,

    // Classic-only background (matte area around the star disc).
    // type: 'color' | 'image'; value: hex string | image URL.
    // v1 split these across star_map_color_change (colors) and background-img
    // radios (images). v2 combines them into one picker (see starmap-v2.liquid).
    background: { type: 'color', value: '#000000' },

    // Text (Phase B wires inputs) — defaults match v1's preset in starmap.liquid
    headline: '',
    customMessage: 'Written In The Stars\nTold By You',
    font: 'Josefin Sans',
    detailsFont: 'Josefin Sans',
    fontScope: 'message', // which text the font picker updates: both | message | details

    // ── Handwritten message ── (parallels locationmap.js handwriting)
    handwritingEnabled: false,
    handwritingStrokes: [],
    handwritingSize: 0.75,
    handwritingHeight: 1.0,

    // Extras (Phase C) — mutually exclusive; syncGlobals derives the three
    // v1-era booleans (ribbonSelected / giftWrapSelected / bundleSelected).
    gift: 'none',              // 'none' | 'ribbon' | 'giftwrap' | 'bundle'
    ribbonColor: 'Red',        // ribbon-colour swatch (ribbon / bundle only)
    giftPaper: 'dots',         // gift-wrap paper design (giftwrap / bundle only)

    // Product / variant (Phase C) — pulled from sm2ProductVariants in liquid.
    // PRINT = option1 'Print', FRAMED = 'Framed', CANVAS = 'Canvas'; size is
    // option2 (e.g. '30x40cm'); frame colour is option3 ('Black' | 'White' |
    // 'Natural') and only applies to FRAMED.
    selectedType:  'PRINT',    // 'PRINT' | 'FRAMED' | 'CANVAS'
    selectedSize:  '30x40',    // bare numeric size (sans 'cm')
    selectedFrame: '',         // '' for PRINT/CANVAS, colour name for FRAMED
    variantId:     null,       // resolved Shopify variant ID

    // Save-for-later form fields
    email:      '',
    newsletter: false,

    // Edit/save (Phase C)
    orderId:     null,         // generated on boot; overwritten by ?order_id= / ?saved=
    editMode:    false,        // set when ?order_id= present AND found in live cart
    savedMode:   false,        // set when ?saved= present
    cartOrderIds:  [],         // _OrderID values from current cart
    cartOrderKeys: [],         // matching cart item keys (for cart/change.js)
    cartLineRef:   null        // per-copy ref stamped on the print + its add-on
  };

  // Non-serialized render handles
  var _glContext = null;
  var _glCanvas = null;
  var _imageCache = new Map();
  var _planetsFullyLoaded = false;
  var _isLoading = false;
  var _isZooming = false;
  var _zoomTimeout = null;
  var _animFrameId = null;
  var _currentAnimation = null;
  var _lastRenderTime = 0;
  var _renderDebounce = null;
  var _canvasImageDebounce = null;
  var _milkywayToggleTimer = null;
  var RENDER_THROTTLE_MS = 500;

  // ── Phase 3: Alpine.js bridge ────────────────────────────────────────
  // Mirror customizer state into Alpine.store('sm2') so picker markup can
  // use declarative `:class` / `@click` against `$store.sm2.*` instead of
  // imperative classList toggles. Alpine.store wraps the value in a Proxy:
  // writes through the proxy fire reactivity, direct `state.x = y` writes
  // bypass it. Use sm2StoreSet for any value the picker bindings depend on.
  document.addEventListener('alpine:init', function () {
    if (window.Alpine) window.Alpine.store('sm2', state);
  });
  function sm2StoreSet(key, value) {
    if (window.Alpine && window.Alpine.store) {
      var s = window.Alpine.store('sm2');
      if (s) { s[key] = value; return; }
    }
    state[key] = value; // fallback before Alpine has booted
  }

  var milkywayConfig = {
    center: [0, 0, 0],
    orientationFixed: false,
    projectionScale: 1024,
    milky_name: 'SKYLIT-night_milkyway-render.jpg',
    disableAnimations: false,
    imageUrl: CDN_BASE + 'SKYLIT-night_milkyway-web.webp?v=1737639576'
  };

  // ── Utilities ────────────────────────────────────────────────────────────

  function isMobileDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  function findTheme(key) {
    for (var i = 0; i < THEMES.length; i++) if (THEMES[i].key === key) return THEMES[i];
    return THEMES[0];
  }

  // Pure luminance check — hex or rgb string. Returns 'light' or 'dark'.
  // Ported from v1 myjs.js:3205 but stripped of its side effects (which
  // re-entered Celestial's reload path).
  function lightOrDark(color) {
    if (!color) return 'dark';
    var r, g, b;
    if (color.indexOf('rgb') === 0) {
      var m = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
      if (!m) return 'dark';
      r = +m[1]; g = +m[2]; b = +m[3];
    } else {
      var hex = color.replace('#', '');
      if (hex.length === 3) hex = hex.split('').map(function(c) { return c + c; }).join('');
      var n = parseInt(hex, 16);
      r = (n >> 16) & 255;
      g = (n >> 8) & 255;
      b = n & 255;
    }
    var hsp = Math.sqrt(0.299 * r * r + 0.587 * g * g + 0.114 * b * b);
    return hsp > 127.5 ? 'light' : 'dark';
  }

  // True once the user has typed into the Printed date & time override. While
  // false, the override field auto-syncs to computedDateTime() as the date /
  // time / show-time inputs change. Cleared back to false when emptied.
  var dateTimeOverrideDirty = false;
  // True once the user DELETES the pre-filled printed date/time. The line then
  // prints blank with its spacing preserved (effectiveDateTime returns a
  // non-breaking space). Reset to false when the date/time/show-time inputs
  // change, so a fresh date re-derives the printed value.
  var dateTimeOverrideCleared = false;

  function formatCoords(lat, lng) {
    if (lat == null || lng == null) return '';
    var latStr = Math.abs(lat).toFixed(2) + '° ' + (lat >= 0 ? 'N' : 'S');
    var lngStr = Math.abs(lng).toFixed(2) + '° ' + (lng >= 0 ? 'E' : 'W');
    return latStr + ', ' + lngStr;
  }

  // Flash the "Calculating star positions" pill over the print frame.
  // Debounced so rapid consecutive changes (date scroll wheel, search
  // autocomplete typing) extend a single indicator instead of spamming.
  // Also fades the star layers out while calculating so only the spinner
  // is visible over an empty disc.
  var _calcFlashTimer = null;
  function flashCalcIndicator() {
    var el = document.getElementById('sm2-calc-indicator');
    var map = document.getElementById('sm2-celestial-map');
    if (!el) return;
    el.classList.add('is-visible');
    if (map) map.classList.add('is-calculating');
    if (_calcFlashTimer) clearTimeout(_calcFlashTimer);
    _calcFlashTimer = setTimeout(function() {
      el.classList.remove('is-visible');
      if (map) map.classList.remove('is-calculating');
    }, 1800);
  }

  // ── State mutation ───────────────────────────────────────────────────────

  function setState(patch) {
    var prev = {};
    Object.keys(patch).forEach(function(k) { prev[k] = state[k]; });
    // Write through Alpine.store proxy when available so picker `:class`
    // bindings re-evaluate. The proxy wraps `state` so each write also
    // updates the underlying property — no double-write needed.
    var alpineStore = (window.Alpine && window.Alpine.store) ? window.Alpine.store('sm2') : null;
    if (alpineStore) {
      Object.keys(patch).forEach(function(k) { alpineStore[k] = patch[k]; });
    } else {
      Object.assign(state, patch);
    }

    // Theatre: when the inputs that drive star positions change, flash the
    // "calculating" pill over the print frame so the user sees an
    // acknowledgement of their change.
    if (patch.coords || patch.location || patch.date || patch.time) {
      flashCalcIndicator();
    }

    // Derived values
    if (patch.coords) {
      state.coordsDisplay = formatCoords(state.coords.lat, state.coords.lng);
      applyGeoposToCelestial();
    }
    if (patch.date || patch.time) {
      applyDateToCelestial();
    }

    syncGlobals();
    syncLegacyInputs();
    scheduleRender(prev);
    if (Cart && Cart.scheduleAutosaveDraft) Cart.scheduleAutosaveDraft();

    // Notify the framed 3D mockup (customizer-3d-core.js listens for this
    // on document) that the design changed. setState is the single choke
    // point for every option — composition, shape, theme, background,
    // fonts, text, handwriting — so one debounced dispatch here covers
    // them all; async celestial re-renders are additionally caught by the
    // core's frame MutationObserver (preview img src swaps).
    clearTimeout(setState._sm3dNotify);
    setState._sm3dNotify = setTimeout(function() {
      try { document.dispatchEvent(new CustomEvent('starmap-updated')); } catch (e) { /* noop */ }
    }, 150);
  }

  // Push state.coords → Celestial. Celestial.location() triggers its own
  // internal go() + mwrotate() chain; we sync milkywayConfig.center from the
  // actual projection rotation afterwards (same pattern as v1's
  // _celestialAfterTimezoneCallback in myjs.js).
  // Minutes east of UTC when the boot state supplied one (headless renders),
  // else null. 0 (UTC) is a real value and must not be dropped as falsy.
  /* @lm-test-export:start headlessTimezone */
  function headlessTimezone() {
    var tz = state.timezone;
    return (typeof tz === 'number' && isFinite(tz)) ? tz : null;
  }
  /* @lm-test-export:end headlessTimezone */

  /* @lm-test-export:start applyGeoposToCelestial */
  function applyGeoposToCelestial() {
    if (typeof Celestial === 'undefined') return;
    var lat = state.coords.lat, lng = state.coords.lng;
    if (lat == null || lng == null) return;
    try {
      if (typeof Celestial.location === 'function') {
        // Celestial API takes [lat, lng] order (geographic), not GeoJSON [lng, lat].
        // Matches v1 starmap/myjs.js:changeLocation (line 3326) verbatim.
        // When the state carries a timezone (the cart-thumbnail host forwards
        // the row's timezone_offset), hand it over: with a valid tz the engine
        // skips setPosition() and never asks timezonedb. Without it, headless
        // boot fired TWO async lookups (this one for "now", Celestial.date()'s
        // for the order date) and kept whichever answered last — a random 1h
        // shift wherever the two offsets differ (DST, Iran post-2022).
        // Storefront state.timezone stays null, so that path is unchanged.
        var tz = headlessTimezone();
        if (tz != null) Celestial.location([lat, lng], tz);
        else Celestial.location([lat, lng]);
      }
    } catch (e) { /* noop */ }
    syncMilkywayFromProjection();
  }
  /* @lm-test-export:end applyGeoposToCelestial */

  /* @lm-test-export:start applyDateToCelestial */
  function applyDateToCelestial() {
    if (typeof Celestial === 'undefined' || !state.date) return;
    try {
      var d = new Date(state.date + 'T' + (state.time || '21:00'));
      if (isNaN(d.getTime())) return;
      if (typeof Celestial.date === 'function') {
        var tz = headlessTimezone();
        if (tz != null) Celestial.date(d, tz);
        else {
          Celestial.date(d);
          refreshTimezoneForDate();
        }
      }
    } catch (e) { /* noop */ }
    syncMilkywayFromProjection();
  }
  /* @lm-test-export:end applyDateToCelestial */

  // The engine asks timezonedb for the location's offset only when the
  // LOCATION changes, at whatever date the map showed then (usually today).
  // A winter date picked in October kept October's offset, so the preview,
  // the saved timezone_offset and the Milky Way rotation were an hour out
  // (308 designs Sept-Oct 2026, #222009). Ask again for the chosen date once
  // the pickers settle. Celestial.location() without a zone re-runs that
  // lookup against the date just set.
  /* @lm-test-export:start refreshTimezoneForDate */
  var tzLookupKey = null, tzLookupTimer = null;
  function refreshTimezoneForDate() {
    var lat = state.coords.lat, lng = state.coords.lng;
    if (lat == null || lng == null || !state.date || typeof Celestial.location !== 'function') return;
    var key = lat + '|' + lng + '|' + state.date;
    if (key === tzLookupKey) return;
    clearTimeout(tzLookupTimer);
    tzLookupTimer = setTimeout(function () {
      tzLookupKey = key;
      try { Celestial.location([lat, lng]); } catch (e) { /* noop */ }
    }, 500);
  }
  /* @lm-test-export:end refreshTimezoneForDate */

  // celestial-new.js calls mwrotate() on every internal redraw. In v1 this
  // was a global in starmap-render-engine.js. Must be on window for Celestial
  // to find it. Animates milkywayConfig.center to a new projection rotation.
  window.mwrotate = function(config) {
    if (!config || !config.center) return;
    if (_currentAnimation) {
      cancelAnimationFrame(_currentAnimation);
      _currentAnimation = null;
    }
    var cFrom = milkywayConfig.center.slice();
    var distance = Math.hypot(cFrom[0] - config.center[0], cFrom[1] - config.center[1]);
    var oDistance = Math.abs(cFrom[2] - config.center[2]);

    if ((distance < 0.1 && oDistance < 0.1) || config.disableAnimations) {
      milkywayConfig.center = config.center.slice();
      renderMilkyWay(milkywayConfig.center);
      return;
    }

    var interval = 800;
    var cTween = (typeof d3 !== 'undefined' && d3.interpolate)
      ? d3.interpolate(cFrom, config.center)
      : function(t) {
          return [
            cFrom[0] + (config.center[0] - cFrom[0]) * t,
            cFrom[1] + (config.center[1] - cFrom[1]) * t,
            cFrom[2] + (config.center[2] - cFrom[2]) * t
          ];
        };
    milkywayConfig.center = config.center.slice();
    var startTime = null;
    function animate(ts) {
      if (!startTime) startTime = ts;
      var elapsed = ts - startTime;
      var t = Math.min(elapsed / interval, 1);
      renderMilkyWay(cTween(t));
      if (t < 1) _currentAnimation = requestAnimationFrame(animate);
      else _currentAnimation = null;
    }
    _currentAnimation = requestAnimationFrame(animate);
  };

  function syncMilkywayFromProjection() {
    // Wait 2 rAFs for Celestial's internal render to settle, then pull the
    // actual D3 projection rotation and feed it to the milkyway shader.
    requestAnimationFrame(function() {
      requestAnimationFrame(function() {
        try {
          if (typeof Celestial !== 'undefined' && typeof Celestial.projectionRotate === 'function') {
            var projRot = Celestial.projectionRotate();
            if (projRot && projRot.length >= 3) {
              milkywayConfig.center = [projRot[0], projRot[1], projRot[2]];
              renderMilkyWay(milkywayConfig.center, true);
            }
          }
        } catch (e) { /* noop */ }
      });
    });
  }

  /**
   * Re-emit state flags on window.* so v1-era backend/cart observers keep working.
   * These globals are read by other scripts that we are NOT rewriting yet —
   * save-for-later, cart observers, admin webhook harvest paths.
   */
  function syncGlobals() {
    window.selectedPrintStyle = state.printStyle;
    // Derive the three v1 booleans from the unified `gift` selector so any
    // downstream script that still reads them sees the right flag.
    window.ribbonSelected   = state.gift === 'ribbon';
    window.giftWrapSelected = state.gift === 'giftwrap';
    window.bundleSelected   = state.gift === 'bundle';
    window.sm2State = state;
  }

  /**
   * Write state into the legacy hidden inputs the admin webhook reads from
   * cart property metadata. Humans never write here — only syncLegacyInputs.
   */
  function syncLegacyInputs() {
    var set = function(id, val) {
      var el = document.getElementById(id);
      if (el) el.value = val != null ? String(val) : '';
    };
    set('custom_date', state.date && state.time ? (state.date + ' ' + state.time) : '');
    set('custom_add', state.location);
    set('custom_lat_lng', state.coords.lat != null && state.coords.lng != null
      ? state.coords.lat + ',' + state.coords.lng
      : '');
  }

  // ── Render pipeline ──────────────────────────────────────────────────────

  // Last-rendered cache for properties whose change should trigger heavy
  // side effects (applyTheme + the theme-switch zoom pulse). Compared from
  // inside render() so it works whether called from setState (where prev
  // is the patched-keys snapshot) or from boot (where prev is {}).
  var _lastRenderedThemeKey = null;
  var _lastRenderedShowMilkyway = null;

  function scheduleRender(prev) {
    clearTimeout(_renderDebounce);
    _renderDebounce = setTimeout(function() { render(prev); }, 120);
  }

  function render(prev) {
    prev = prev || {};

    // Frame container data attribute drives CSS branch (classic vs photographer/explorer)
    var frame = document.getElementById('sm2-frame');
    if (frame) {
      frame.setAttribute('data-print-style', state.printStyle);
      frame.setAttribute('data-shape', state.shape || 'circle');
      frame.setAttribute('data-composition', state.composition || 'classic');
      // Zodiac ring: rotate so the date's sun sign sits at the top. The SVG
      // is authored with Aries at top and signs running counterclockwise, so
      // sign index n comes to the top with a clockwise rotation of n*30deg.
      frame.style.setProperty('--sm2-zodiac-rot', (sunSignIndex(state.date) * 30) + 'deg');
      // Preview aspect ratio tracks the chosen print size (A4 → 210/297 etc.)
      // for classic + photographer. Explorer has its own fixed tall-format
      // ratio set in CSS, so skip the inline override there.
      if (state.printStyle === 'explorer') {
        frame.style.removeProperty('--sm2-frame-ratio');
      } else {
        var ratio = sizeToRatio(state.selectedSize);
        if (ratio) frame.style.setProperty('--sm2-frame-ratio', ratio);
      }
    }
    // Mirror composition onto the outer wrapper so sidebar-side CSS can
    // react to it (e.g. hide shape/background/border when header is active).
    var wrapper = document.querySelector('.sm2-wrapper');
    if (wrapper) wrapper.setAttribute('data-composition', state.composition || 'classic');

    // Composition or size change → clear the inline width/height that squareUp
    // set last time around (inline !important beats stylesheet !important, so
    // without clearing, the header-composition CSS can't grow the canvas past
    // the classic 90%-square sizing, and a new print size's reshaped frame
    // leaves the disc pinned at the old width). squareUp re-runs and picks up
    // the new CSS geometry. Milkyway is re-rendered so its overlay img's fixed
    // px dimensions (applyCanvasToBackground) match the new disc size.
    //
    // selectedType / selectedFrame are included because applyFinish() swaps a
    // border-image on the wrap (Framed only, slice 30-40px depending on
    // colour) which shrinks/grows the inner area the disc lays out against —
    // without re-squaring, the stale inline px width drifts out of the new
    // frame and the disc layers misalign with each other.
    if (
      (prev.composition !== undefined && prev.composition !== state.composition) ||
      (prev.selectedSize !== undefined && prev.selectedSize !== state.selectedSize) ||
      (prev.printStyle !== undefined && prev.printStyle !== state.printStyle) ||
      (prev.selectedType !== undefined && prev.selectedType !== state.selectedType) ||
      (prev.selectedFrame !== undefined && prev.selectedFrame !== state.selectedFrame)
    ) {
      // Also clear any stale inline width/height left by older builds of
      // squareUpCelestialMap (which used to pixel-pin). Cheap safety net so
      // a customer with a cached/stale JS can still recover by moving to a
      // build where squareUp no longer writes pixels.
      var cm = document.getElementById('sm2-celestial-map');
      if (cm) {
        cm.style.removeProperty('width');
        cm.style.removeProperty('height');
      }
      var ring = document.querySelector('.sm2-disc-border');
      if (ring) {
        ring.style.removeProperty('width');
        ring.style.removeProperty('height');
      }

      // Re-render the milkyway after the DOM settles. Two passes: immediate
      // (covers the common in-sync layout) and post-transition (catches
      // Print↔Framed, where .sm2-frame's `transition: border-width 0.3s`
      // keeps the outer geometry animating for 300ms after the state flip).
      var doRender = function() {
        if (typeof renderMilkyWay === 'function' && typeof milkywayConfig !== 'undefined') {
          renderMilkyWay(milkywayConfig.center, true);
        }
      };
      setTimeout(doRender, 0);
      setTimeout(doRender, 350);
    }

    applyPrintStyle(state.printStyle);

    // Theme change → swap milkyway image URL + re-render WebGL.
    // setState's `prev` only contains keys from its patch, so prev.themeKey
    // is undefined for non-theme patches and `prev.themeKey !== state.themeKey`
    // would fire on every setState (the old buggy behaviour). Compare against
    // a module-level cache of the last-rendered theme/milkyway instead — and
    // do this from inside render() rather than referencing `patch` (which
    // isn't in render's scope; doing so threw a ReferenceError when render({})
    // was called from boot, killing updateCaption() and leaving the caption
    // text blank).
    var firstRender = (_lastRenderedThemeKey === null);
    var themeChanged = !firstRender && state.themeKey !== _lastRenderedThemeKey;
    var milkywayChanged = !firstRender && state.showMilkyway !== _lastRenderedShowMilkyway;
    _lastRenderedThemeKey = state.themeKey;
    _lastRenderedShowMilkyway = state.showMilkyway;
    if (themeChanged || milkywayChanged) {
      applyTheme();
      // Visual zoom pulse on theme switch. Deferred via double rAF + small
      // timeout so renderMilkyWay's rAF callback (which reads
      // baseElement.getBoundingClientRect()) measures the unscaled bounds
      // BEFORE the transform applies — otherwise the milkyway texture sizes
      // to the scaled rect and the disc warps.
      if (themeChanged) {
        var _zoomFrame = document.getElementById('sm2-frame');
        if (_zoomFrame) {
          if (_zoomFrame._sm2ZoomTimer) clearTimeout(_zoomFrame._sm2ZoomTimer);
          if (_zoomFrame._sm2ZoomRaf1) cancelAnimationFrame(_zoomFrame._sm2ZoomRaf1);
          if (_zoomFrame._sm2ZoomRaf2) cancelAnimationFrame(_zoomFrame._sm2ZoomRaf2);
          _zoomFrame._sm2ZoomRaf1 = requestAnimationFrame(function() {
            _zoomFrame._sm2ZoomRaf2 = requestAnimationFrame(function() {
              // Re-add to restart the keyframe animation if it's already on.
              _zoomFrame.classList.remove('sm2-zooming');
              void _zoomFrame.offsetWidth;
              _zoomFrame.classList.add('sm2-zooming');
              _zoomFrame._sm2ZoomTimer = setTimeout(function() {
                _zoomFrame.classList.remove('sm2-zooming');
                _zoomFrame._sm2ZoomTimer = null;
              }, 1250);
            });
          });
        }
      }
    }

    // Element toggles (constellations/grid/labels/planets) → Celestial.applyQuiet
    var planetsChanged = prev.showPlanets !== state.showPlanets;
    if (
      prev.showConstellations !== state.showConstellations ||
      prev.showGrid !== state.showGrid ||
      prev.showLabels !== state.showLabels ||
      planetsChanged
    ) {
      applyElements({ planetsChanged: planetsChanged });
    }

    // Border toggle — purely decorative CSS on the frame
    if (prev.showBorder !== state.showBorder) {
      applyBorder();
    }

    // Classic background (matte around star disc) — only applies when
    // printStyle === 'classic'; photographer/explorer have their own matte.
    if (
      prev.background !== state.background ||
      prev.printStyle !== state.printStyle
    ) {
      applyBackground();
    } else if (prev.composition !== state.composition) {
      fitPanelBackground();
    }

    // Finish treatment (Print | Framed | Canvas) + frame colour. Framed
    // stamps a border-image onto the preview frame; Print/Canvas strip it.
    if (
      prev.selectedType  !== state.selectedType ||
      prev.selectedFrame !== state.selectedFrame
    ) {
      applyFinish();
    }

    // Gift overlay + type-driven visibility of wrap/bundle tiles.
    if (prev.gift !== state.gift ||
        prev.selectedType !== state.selectedType ||
        prev.ribbonColor !== state.ribbonColor ||
        prev.giftPaper !== state.giftPaper) {
      applyGiftUI();
    }

    // Caption text
    updateCaption();

    // Sidebar tile active-state sync. Click handlers toggle .active inline
    // when the user picks a tile, but hydrateFromRow (edit / resume flows)
    // mutates state without going through the DOM — so without this call
    // the sidebar shows the default selections even though state + preview
    // already reflect the loaded row.
    syncSidebarTiles();

    // Debug panel
    updateDebug();
  }

  function syncSidebarTiles() {
    // Phase 3: composition / shape / background / element toggles' .active is
    // now Alpine-driven via :class against $store.sm2.*. We still re-render
    // theme tiles (their markup is built dynamically), and keep the milkyway
    // <input type=checkbox> in sync since it's a native form control.
    if (typeof renderThemeTiles === 'function') renderThemeTiles();
    var mwCheck = document.getElementById('sm2-milkyway-toggle');
    if (mwCheck) mwCheck.checked = !!state.showMilkyway;
    // Font tile .active is Alpine-driven via :class="sm2IsFontActive(...)".
    // Text inputs (headline / message / location). Init seeds these once
    // when empty, but hydration later can mutate state without the DOM
    // reflecting it. Skip inputs the user is currently editing so an async
    // hydration doesn't stomp their keystrokes.
    function setIfUnfocused(id, val) {
      var el = document.getElementById(id);
      if (!el || document.activeElement === el) return;
      if (el.value !== (val || '')) el.value = val || '';
    }
    setIfUnfocused('sm2-headline-input', state.headline);
    setIfUnfocused('sm2-message-input',  state.customMessage);
    setIfUnfocused('sm2-location-input', state.location);
    setIfUnfocused('sm2-location-override', state.locationOverride);
    setIfUnfocused('sm2-coords-override',   state.coordsOverride);
    // Date/time override: when not dirty, mirror the auto value so the field
    // always shows what would print; when dirty, show the override verbatim.
    setIfUnfocused('sm2-datetime-override', dateTimeOverrideDirty ? state.dateTimeOverride : (dateTimeOverrideCleared ? '' : computedDateTime()));
    // Placeholders reflect the auto values so users see what they'd revert to
    // when they clear the override input.
    var locOv = document.getElementById('sm2-location-override');
    if (locOv) locOv.placeholder = state.location || '';
    var coOv = document.getElementById('sm2-coords-override');
    if (coOv) coOv.placeholder = state.coordsDisplay || '';
    // Coords readout under the location search box. Previously only written by
    // the place-select handler and seedDefaults(), so a resume/edit that
    // mutated state.coords via hydrateFromRow never refreshed it — leaving the
    // seedDefaults London default ("51.51° N, 0.13° W") showing under a
    // correctly-restored location. Mirror the derived value here so it tracks
    // state on every render, same as the override placeholder above.
    var coDisp = document.getElementById('sm2-coords-display');
    if (coDisp) coDisp.textContent = state.coordsDisplay || '';
    // Expand the relevant disclosure if an override is active, so it's visible on resume.
    var disc = document.getElementById('sm2-display-override');
    if (disc && (state.locationOverride || state.coordsOverride)) disc.open = true;
    var dtDisc = document.getElementById('sm2-datetime-disclosure');
    if (dtDisc && state.dateTimeOverride) dtDisc.open = true;

    // Size / Type / Frame / Gift tiles. applyFinish() updates the preview
    // wrap but not the sidebar; refreshFinishTiles+refreshGiftTiles are the
    // only paths that toggle .active on these tiles. Click handlers call
    // them inline, but hydration mutates state directly — re-run on every
    // render so loaded orders show the right selection.
    if (typeof refreshFinishTiles === 'function') refreshFinishTiles();
    if (typeof refreshGiftTiles === 'function')   refreshGiftTiles();
  }

  // Push element toggles into Celestial without re-initialising everything.
  // applyQuiet is the no-recompute variant — live config swap, no redraw
  // cascade that would flash the star field blank. Exception: the *first*
  // time planets are enabled, the DOM paths don't exist yet and applyQuiet
  // can't create them — we need a quiet reload() (+ image preload) once, then
  // applyQuiet is enough for every subsequent toggle. Matches v1 togglePlanets
  // in myjs.js:23.
  function applyElements(opts) {
    if (typeof Celestial === 'undefined') return;
    opts = opts || {};
    var cfg = {
      constellations: {
        show: state.showConstellations,
        names: state.showLabels,
        lines: state.showConstellations,
        lineStyle: { stroke: '#cccccc', width: 2, opacity: 0.75 },
        nameStyle: {
          fill: '#ffffff', align: 'center', baseline: 'middle', opacity: 0.8,
          font: [
            '12px Josefin Sans, Open Sans, Arial, sans-serif',
            '9px Josefin Sans, Open Sans, Arial, sans-serif',
            '9px Josefin Sans, Open Sans, Arial, sans-serif'
          ]
        }
      },
      lines: {
        graticule: {
          show: state.showGrid,
          stroke: '#cccccc', width: 1.2, opacity: 0.4
        },
        equatorial: { show: false },
        ecliptic: { show: false },
        galactic: { show: false },
        supergalactic: { show: false }
      },
      planets: {
        show: state.showPlanets,
        which: state.showPlanets ? ['ter', 'mer', 'ven', 'mar', 'jup', 'sat', 'ura', 'nep'] : ['ter'],
        symbolType: state.showPlanets ? 'image' : 'symbol',
        names: state.showPlanets && state.showLabels,
        namesType: 'en',
        symbolStyle: {
          fill: '#cccccc', opacity: 1,
          font: "bold 17px 'DejaVu Sans Mono', 'Arial Unicode MS', sans-serif",
          align: 'center', baseline: 'middle'
        }
      }
    };

    // First planet-on: need reload() to build planet DOM paths. Subsequent
    // toggles (on/off) use applyQuiet.
    if (opts.planetsChanged && state.showPlanets && !_planetsFullyLoaded) {
      var doReload = function() {
        try {
          if (typeof Celestial.setQuietMode === 'function') Celestial.setQuietMode(true);
          if (typeof Celestial.reload === 'function') Celestial.reload(cfg);
        } catch (e) { /* noop */ }
        setTimeout(function() {
          _planetsFullyLoaded = true;
          try {
            if (typeof Celestial.setQuietMode === 'function') Celestial.setQuietMode(false);
            if (typeof Celestial.redraw === 'function') Celestial.redraw();
          } catch (e) { /* noop */ }
        }, 500);
      };
      if (typeof Celestial.preloadPlanetImages === 'function') {
        try { Celestial.preloadPlanetImages(doReload); } catch (e) { doReload(); }
      } else {
        doReload();
      }
      return;
    }

    try {
      if (typeof Celestial.applyQuiet === 'function') Celestial.applyQuiet(cfg);
      else if (typeof Celestial.apply === 'function') Celestial.apply(cfg);
      if (typeof Celestial.redraw === 'function') Celestial.redraw();
    } catch (e) { /* noop */ }
  }

  function applyBorder() {
    var frame = document.getElementById('sm2-frame');
    if (frame) frame.classList.toggle('sm2-frame--border', !!state.showBorder);
  }

  // Apply the Print/Framed/Canvas finish to the preview frame. Framed stamps
  // a border-image with the selected colour; Print/Canvas strip it entirely
  // so the print fills edge-to-edge. Matches locationmap's updateFrame()
  // pattern (assets/locationmap.js:1374).
  function applyFinish() {
    var wrap = document.getElementById('sm2-frame-wrap');
    if (!wrap) return;
    var t = (state.selectedType || 'PRINT').toUpperCase();
    wrap.setAttribute('data-finish', t.toLowerCase());
    if (t === 'FRAMED') {
      var tex = FRAME_TEXTURES[state.selectedFrame] || FRAME_TEXTURES.Black;
      wrap.setAttribute('data-frame-color', state.selectedFrame || 'Black');
      wrap.style.setProperty('--sm2-frame-border-color', tex.color);
      wrap.style.setProperty('--sm2-frame-border-image', tex.image);
    } else {
      wrap.removeAttribute('data-frame-color');
      wrap.style.removeProperty('--sm2-frame-border-color');
      wrap.style.removeProperty('--sm2-frame-border-image');
    }
  }

  // Gift preview overlays + Print-incompatible option filtering. Mirrors
  // locationmap (assets/locationmap.js:1195-1260). When type === PRINT we
  // hide wrap + bundle tiles and auto-revert gift to ribbon/none so the
  // cart never tries to pair a flat Print with giftwrap.
  function applyGiftUI() {
    var form = document.getElementById('sm2-product-form');
    if (form) form.setAttribute('data-type', (state.selectedType || 'PRINT').toUpperCase());

    // If the current gift is incompatible with the chosen type, demote it.
    // MUST write through the Alpine store (sm2StoreSet), not `state.gift =`
    // directly: a direct write bypasses the store proxy, so the wrapping
    // tile kept showing Bundle as selected while pricing/cart used the
    // demoted value. Bundle keeps its ribbon half on Print; plain gift wrap
    // has no print-compatible part. The pre-demotion choice is stashed and
    // restored when the customer returns to a wrap-compatible finish
    // (a manual wrapping click clears the stash - see bindFinishUI).
    var isPrint = (state.selectedType || 'PRINT').toUpperCase() === 'PRINT';
    if (isPrint && (state.gift === 'giftwrap' || state.gift === 'bundle')) {
      _giftBeforePrint = state.gift;
      sm2StoreSet('gift', state.gift === 'bundle' ? 'ribbon' : 'none');
    } else if (!isPrint && _giftBeforePrint) {
      sm2StoreSet('gift', _giftBeforePrint);
      _giftBeforePrint = null;
    }

    // Gift tile .active is Alpine-driven via :class against $store.sm2.gift.

    // Toggle preview overlays. Bundle = giftwrap + ribbon simultaneously.
    // ANDed with !sm2GiftPeekHidden so peek mode suppresses both overlays.
    var wrap   = document.getElementById('sm2-giftwrap-overlay');
    var ribbon = document.getElementById('sm2-ribbon-overlay');
    if (wrap)   wrap.classList.toggle('visible',   !sm2GiftPeekHidden && (state.gift === 'giftwrap' || state.gift === 'bundle'));
    if (ribbon) ribbon.classList.toggle('visible', !sm2GiftPeekHidden && (state.gift === 'ribbon'   || state.gift === 'bundle'));

    // Recolour the ribbon overlay to the customer's chosen swatch.
    applyRibbonColor();

    // Swap the 2D paper overlay and the 3D parcel to the chosen design.
    if (wrap && window.SkylitGifts) window.SkylitGifts.applyPaperTexture(wrap, state.giftPaper);
    if (sm2PaperPicker) sm2PaperPicker.set(state.giftPaper || 'dots');

    // Gift card overlay rides with the ribbon — only ribbon/bundle pin a
    // card to the wrap.
    var giftcard = document.getElementById('sm2-giftcard-overlay');
    if (giftcard) giftcard.classList.toggle('visible', !sm2GiftPeekHidden && (state.gift === 'ribbon' || state.gift === 'bundle'));

    // Refresh the peek button visibility + pressed state.
    sm2RefreshPeekBtn();

    // Mirror gift + peek state into the 3D framed mockup (if loaded). The
    // mockup's init also reads window.sm2GiftPeekHidden, so keep it fresh.
    // applyGiftUI runs on both gift-selection changes and peek toggles, so
    // this one hook covers every transition. Safe pre-init: the show/hide
    // fns just set flags that init picks up. (3D button is currently
    // hidden on star map — this is ready for when it returns.)
    window.sm2GiftPeekHidden = sm2GiftPeekHidden;
    var api3d = window.Star3DFramedMockup;
    if (api3d) {
      var show3dRibbon = !sm2GiftPeekHidden && (state.gift === 'ribbon' || state.gift === 'bundle');
      var show3dWrap = !sm2GiftPeekHidden && (state.gift === 'giftwrap' || state.gift === 'bundle');
      if (typeof api3d.showRibbon === 'function') {
        if (show3dRibbon) { api3d.showRibbon(); } else { api3d.hideRibbon(); }
      }
      if (typeof api3d.showGiftWrap === 'function') {
        if (show3dWrap) { api3d.showGiftWrap(); } else { api3d.hideGiftWrap(); }
      }
      if (typeof api3d.setWrapDesign === 'function') api3d.setWrapDesign(state.giftPaper || 'dots');
    }
  }

  // ── Gift peek (eye icon) — transient view-only toggle ─────────────────
  // Mirrors ACP preview route.ts wrapHidden / refreshPeekBtn() pattern.
  // Does NOT alter state.gift; only suppresses the overlay rendering.
  var sm2GiftPeekHidden = false;
  var sm2PaperPicker = null;
  // Wrapping choice stashed by applyGiftUI while an incompatible finish
  // (Print) is selected; restored when a wrap-compatible finish returns.
  var _giftBeforePrint = null;
  function sm2RefreshPeekBtn() {
    var btn = document.getElementById('sm2-gift-peek-btn');
    var active = state.gift && state.gift !== 'none';
    if (btn) {
      btn.hidden = !active;
      var label = sm2GiftPeekHidden ? 'Show gift wrap' : 'Hide gift wrap';
      btn.setAttribute('aria-label', label);
      btn.setAttribute('title', label);
      btn.setAttribute('aria-pressed', sm2GiftPeekHidden ? 'true' : 'false');
    }

    // The eye is up on the preview, away from the wrapping picker. Echo its
    // hidden state beside the choices so a customer changing their selection
    // understands why the newly selected wrap still is not visible.
    var notice = document.getElementById('sm2-gift-peek-notice');
    if (notice) notice.hidden = !(active && sm2GiftPeekHidden);
  }
  document.addEventListener('DOMContentLoaded', function () {
    var btn = document.getElementById('sm2-gift-peek-btn');
    if (btn) {
      btn.addEventListener('click', function () {
        sm2GiftPeekHidden = !sm2GiftPeekHidden;
        applyGiftUI();
      });
    }

    var showBtn = document.getElementById('sm2-gift-peek-show');
    if (showBtn) {
      showBtn.addEventListener('click', function () {
        sm2GiftPeekHidden = false;
        applyGiftUI();
      });
    }
  });

  // Swap #sm2-ribbon-overlay's image to the per-colour ribbon for the
  // selected swatch. Client-side and instant — the eight ribbon PNGs are
  // pre-hosted on agent.skylitstudio.co.uk. No-op when the overlay isn't on
  // screen, but the src is still updated so it's correct when it next shows.
  function applyRibbonColor() {
    var ribbon = document.getElementById('sm2-ribbon-overlay');
    if (!ribbon) return;
    var nextSrc = ribbonImageFor(state.ribbonColor || 'Red');
    if (ribbon.getAttribute('src') !== nextSrc) ribbon.setAttribute('src', nextSrc);
  }

  // Paint the matte area around the star disc for Classic. Hidden picker
  // section otherwise — photographer/explorer have their own fixed mattes
  // (#f4ede2 / #0a0a0a) set via CSS.
  var lastPaintedBg = null;
  function applyBackground() {
    var frame = document.getElementById('sm2-frame');
    var section = document.getElementById('sm2-bg-section');
    var isClassic = state.printStyle === 'classic';
    if (section) section.style.display = isClassic ? '' : 'none';
    if (!frame) return;

    if (!isClassic) {
      // Clear classic-only inline styles so CSS rules for photographer/explorer win
      frame.style.backgroundColor = '';
      frame.style.backgroundImage = '';
      frame.style.backgroundOrigin = '';
      frame.style.backgroundClip = '';
      frame.style.backgroundRepeat = '';
      frame.classList.remove('sm2-frame--light-bg');
      frame.classList.remove('sm2-bg-fade');
      lastPaintedBg = null;
      return;
    }

    var bg = state.background || { type: 'color', value: '#000000' };
    var bgKey = bg.type + ':' + bg.value;
    var commit = function() {
      if (bg.type === 'image') {
        // Two layers: the picture covers the PRINT (padding box) so what the
        // customer sees is what gets printed; a black backing still fills
        // the border box under the Framed border-image (starmap-v2.css sets
        // background-origin/clip: border-box for that). Painting the picture
        // on the border box scaled it up ~7% behind a 25px frame and hid its
        // bottom edge under the chrome, which is why it "jumped" after the
        // ::before cross-fade (that layer already sat on the padding box).
        frame.style.backgroundImage = 'url("' + bg.value + '"), linear-gradient(#000, #000)';
        frame.style.backgroundSize = 'cover, auto';
        frame.style.backgroundPosition = 'center, center';
        frame.style.backgroundRepeat = 'no-repeat, no-repeat';
        frame.style.backgroundOrigin = 'padding-box, border-box';
        frame.style.backgroundClip = 'padding-box, border-box';
        frame.style.backgroundColor = '#000';
        // Images: default to light-text (most backgrounds are dark nebulae).
        frame.classList.remove('sm2-frame--light-bg');
      } else {
        frame.style.backgroundImage = 'none';
        frame.style.backgroundOrigin = '';
        frame.style.backgroundClip = '';
        frame.style.backgroundRepeat = '';
        frame.style.backgroundColor = bg.value;
        frame.classList.toggle('sm2-frame--light-bg', lightOrDark(bg.value) === 'light');
      }
      fitPanelBackground();
    };

    // Cross-fade between backgrounds: background-image isn't animatable,
    // so paint the incoming background on the ::before overlay, fade it
    // in, then commit. First paint (boot/hydration/style switches) and
    // reduced-motion apply instantly.
    var reduceMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (lastPaintedBg && lastPaintedBg !== bgKey && !reduceMotion && frame.animate) {
      frame.style.setProperty('--sm2-bg-next',
        bg.type === 'image' ? 'url("' + bg.value + '")' : bg.value);
      // Flip the text colour alongside the fade so the caption stays legible.
      frame.classList.toggle('sm2-frame--light-bg',
        bg.type !== 'image' && lightOrDark(bg.value) === 'light');
      frame.classList.add('sm2-bg-fade');
      clearTimeout(applyBackground._commit);
      applyBackground._commit = setTimeout(function() {
        commit();
        frame.classList.remove('sm2-bg-fade');
        frame.style.removeProperty('--sm2-bg-next');
      }, 380);
    } else {
      commit();
    }
    lastPaintedBg = bgKey;
  }

  // Panel + Cartoon: the white panel covered the bottom of the picture, which
  // is where the animals stand. Fit the picture to the area ABOVE the panel
  // (cover-scaled, bottom edge on the panel's top edge) so they stand on it.
  // The panel's height follows the text, so this is measured, and re-run on
  // resize / caption changes. Mirrored in the print template
  // (skylit-admin renderer/templates/site/star_v2_img.html); the Cloudflare
  // cart render runs this file headless, so it inherits it.
  var CARTOON_BG_ASPECT = 7090 / 8856;   // print master and web asset share it
  var CARTOON_PAPER = '#c4a27a';          // the artwork's flat paper colour
  // Each animal group's box in the artwork, as fractions (measured on web-3;
  // y1 is the feet line, so the groups stand exactly on the panel).
  var CARTOON_GROUPS = [
    { side: 'left',  x0: 0.048, x1: 0.352, y0: 0.722, y1: 0.987 },
    { side: 'right', x0: 0.652, x1: 0.938, y0: 0.733, y1: 0.987 }
  ];
  var CARTOON_HIDE_ROW = 0.72;            // base layer: animal band starts here
  function isCartoonBg(bg) {
    return !!(bg && bg.type === 'image' && /night-you-were-born/.test(bg.value || ''));
  }
  // The disc (or ring / heart) as a circle in frame padding-box px.
  function discCircle(frame) {
    var map = document.getElementById('sm2-celestial-map');
    if (!map) return null;
    var fr = frame.getBoundingClientRect(), r = map.getBoundingClientRect();
    var grow = (state.shape === 'azimuth' || state.shape === 'zodiac') ? 1.12 : 1.02;
    return {
      cx: r.left + r.width / 2 - fr.left - frame.clientLeft,
      cy: r.top + r.height / 2 - fr.top - frame.clientTop,
      r: (r.width / 2) * grow
    };
  }
  function boxHitsCircle(x0, y0, x1, y1, c, pad) {
    var nx = Math.max(x0, Math.min(c.cx, x1)), ny = Math.max(y0, Math.min(c.cy, y1));
    var dx = c.cx - nx, dy = c.cy - ny;
    return dx * dx + dy * dy < (c.r + pad) * (c.r + pad);
  }
  function fitPanelBackground() {
    var frame = document.getElementById('sm2-frame');
    if (!frame) return;
    var crops = frame.querySelectorAll('.sm2-cartoon-crop');
    var active = state.printStyle === 'classic' && state.composition === 'panel' && isCartoonBg(state.background);
    if (!active) {
      if (frame._sm2PanelFit) {
        // Back to the standard paint (applyBackground's commit), which only
        // image backgrounds on Classic use; colours set their own styles.
        var bgNow = state.background;
        if (state.printStyle === 'classic' && bgNow && bgNow.type === 'image') {
          frame.style.backgroundImage = 'url("' + bgNow.value + '"), linear-gradient(#000, #000)';
          frame.style.backgroundSize = 'cover, auto';
          frame.style.backgroundPosition = 'center, center';
          frame.style.backgroundRepeat = 'no-repeat, no-repeat';
          frame.style.backgroundOrigin = 'padding-box, border-box';
          frame.style.backgroundClip = 'padding-box, border-box';
        }
        frame._sm2PanelFit = null;
      }
      Array.prototype.forEach.call(crops, function (c) { c.classList.remove('is-on'); });
      return;
    }
    var cap = document.getElementById('sm2-caption');
    var W = frame.clientWidth, H = frame.clientHeight;
    if (!cap || !W || !H) return;
    var url = state.background.value;
    var panelTop = H - cap.offsetHeight;

    // Base: the artwork full width, dropped so every animal is under the
    // (opaque) panel; the paper colour fills whatever it leaves at the top.
    var bw = W, bh = W / CARTOON_BG_ASPECT;
    var by = panelTop - CARTOON_HIDE_ROW * bh;
    frame.style.backgroundImage = 'url("' + url + '"), linear-gradient(' + CARTOON_PAPER + ', ' + CARTOON_PAPER + '), linear-gradient(#000, #000)';
    frame.style.backgroundSize = bw.toFixed(1) + 'px ' + bh.toFixed(1) + 'px, auto, auto';
    frame.style.backgroundPosition = '0px ' + by.toFixed(1) + 'px, center, center';
    frame.style.backgroundRepeat = 'no-repeat, no-repeat, no-repeat';
    frame.style.backgroundOrigin = 'padding-box, padding-box, border-box';
    frame.style.backgroundClip = 'padding-box, padding-box, border-box';

    // Groups: natural size first, shrunk until they clear the disc.
    var disc = discCircle(frame);
    var geom = { base: { x: 0, y: by, w: bw, h: bh }, groups: [] };
    CARTOON_GROUPS.forEach(function (g) {
      var el = frame.querySelector('.sm2-cartoon-crop[data-side="' + g.side + '"]');
      if (!el) return;
      var inset = (g.side === 'left' ? g.x0 : 1 - g.x1) * W;
      var place = function (k) {
        var gw = (g.x1 - g.x0) * bw * k, gh = (g.y1 - g.y0) * bh * k;
        var x0 = g.side === 'left' ? inset : W - inset - gw;
        return { x: x0, y: panelTop - gh, w: gw, h: gh, k: k };
      };
      var k = 1;
      if (disc) {
        var lo = 0.4, hi = 1;
        var hits = function (kk) { var b = place(kk); return boxHitsCircle(b.x, b.y, b.x + b.w, b.y + b.h, disc, 4); };
        if (hits(1)) {
          for (var i = 0; i < 14; i++) { var mid = (lo + hi) / 2; if (hits(mid)) hi = mid; else lo = mid; }
          k = lo;
        }
      }
      var b = place(k);
      el.style.left = b.x.toFixed(1) + 'px';
      el.style.top = b.y.toFixed(1) + 'px';
      el.style.width = b.w.toFixed(1) + 'px';
      el.style.height = b.h.toFixed(1) + 'px';
      el.style.backgroundImage = 'url("' + url + '")';
      el.style.backgroundSize = (bw * k).toFixed(1) + 'px ' + (bh * k).toFixed(1) + 'px';
      el.style.backgroundPosition = (-g.x0 * bw * k).toFixed(1) + 'px ' + (-g.y0 * bh * k).toFixed(1) + 'px';
      el.classList.add('is-on');
      geom.groups.push({ g: g, x: b.x, y: b.y, w: b.w, h: b.h });
    });
    frame._sm2PanelFit = geom;
  }
  (function watchPanelFit() {
    if (!window.ResizeObserver) return;
    var start = function () {
      var frame = document.getElementById('sm2-frame');
      var cap = document.getElementById('sm2-caption');
      if (!frame || !cap) return;
      var ro = new ResizeObserver(function () { fitPanelBackground(); });
      ro.observe(frame); ro.observe(cap);
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
    else start();
  })();

  // Preload the full-size image backgrounds once the page is idle so the
  // first selection cross-fades into a loaded image, not a blank one.
  window.addEventListener('load', function() {
    var kick = window.requestIdleCallback || function(cb) { setTimeout(cb, 1500); };
    kick(function() {
      document.querySelectorAll('.sm2-tile--bg[data-bg-type="image"]').forEach(function(t) {
        var img = new Image();
        img.src = t.getAttribute('data-bg-value');
      });
    });
  });

  // "30x40" → "30 / 40", "a4" → "210 / 297" (ISO 216 portrait).
  // Returned as a CSS aspect-ratio fraction string (width / height).
  // Tropical sun sign for an ISO date, as an index into the zodiac ring's
  // authored order (Aries=0 … Pisces=11). Cusp boundaries use the common
  // civil convention; no date → 0 keeps Aries (the authored top) in place.
  function sunSignIndex(iso) {
    if (!iso) return 0;
    var m = parseInt(iso.slice(5, 7), 10);
    var d = parseInt(iso.slice(8, 10), 10);
    if (!m || !d) return 0;
    // First day of each sign, Aries..Pisces as [month, day].
    var starts = [[3, 21], [4, 20], [5, 21], [6, 21], [7, 23], [8, 23], [9, 23], [10, 23], [11, 22], [12, 22], [1, 20], [2, 19]];
    for (var i = 0; i < 12; i++) {
      var s = starts[i], e = starts[(i + 1) % 12];
      var afterStart = (m > s[0]) || (m === s[0] && d >= s[1]);
      var beforeEnd = (m < e[0]) || (m === e[0] && d < e[1]);
      // Capricorn wraps the year end: inside when after start OR before end.
      var inside = (s[0] < e[0]) ? (afterStart && beforeEnd) : (afterStart || beforeEnd);
      if (inside) return i;
    }
    return 0;
  }

  function sizeToRatio(sizeStr) {
    if (!sizeStr) return null;
    if (String(sizeStr).toLowerCase() === 'a4') return '210 / 297';
    var m = String(sizeStr).match(/(\d+)\s*x\s*(\d+)/i);
    return m ? (m[1] + ' / ' + m[2]) : null;
  }

  function applyPrintStyle(styleKey) {
    var cfg = PRINT_STYLES[styleKey] || PRINT_STYLES.classic;
    var svg = document.getElementById('sm2-preview-svg');
    if (svg) {
      svg.setAttribute('viewBox', cfg.viewBox);
      var clipPath = document.getElementById('sm2-preview-clip-path');
      if (clipPath) clipPath.setAttribute('d', cfg.clip);
      var stage = document.getElementById('sm2-preview-stage');
      if (stage) stage.setAttribute('transform', cfg.stageTransform);
    }
    syncPreviewImage();
  }

  function applyTheme() {
    var theme = findTheme(state.themeKey);
    var useNoMilky = !state.showMilkyway && theme.hasMilkyWayOption;
    var smallImage = useNoMilky ? theme.no_milky_small : theme.milky_small;
    var largeImage = useNoMilky ? theme.no_milky_large : theme.milky_large;

    milkywayConfig.milky_name = largeImage;
    milkywayConfig.imageUrl = CDN_BASE + smallImage;

    renderMilkyWay(milkywayConfig.center, true);
  }

  // Visual-line count for the rendered caption — accounts for explicit \n
  // AND soft-wrapping at ~MSG_CHARS_PER_LINE per line. Used for dynamic
  // font-scaling so longer messages auto-shrink to fit the caption band
  // without encroaching on the date/location/coords stack below.
  var MSG_CHARS_PER_LINE = 26;
  var MSG_MAX_LINES = 4;
  // Paste-bomb guard only. The soft-wrap estimate must NOT be the hard
  // Enter gate: the print renderer honours explicit \n only (no word-wrap),
  // so a user breaking a long soft-wrapped line into explicit lines is
  // IMPROVING the print — but the estimate goes up and the old gate
  // reverted the edit, which read as "Enter randomly doesn't work"
  // (two ~40-char lines already estimate as 4; any break then estimated 5).
  var MSG_MAX_EST_LINES = 8;
  function countExplicitLines(s) {
    return s ? s.split('\n').length : 0;
  }
  function countMessageLines(s) {
    if (!s) return 0;
    var parts = s.split('\n');
    var n = 0;
    for (var i = 0; i < parts.length; i++) {
      n += Math.max(1, Math.ceil(parts[i].length / MSG_CHARS_PER_LINE));
    }
    return n;
  }

  // Overflow-warn threshold — distinct from MSG_CHARS_PER_LINE, which is
  // the soft-wrap column used for visual line counting / font scaling.
  // The actual character budget that fits across the print at the base
  // font size is larger; setting the warn threshold to match the real
  // overflow point (rather than the soft-wrap column) keeps the nudge
  // from harassing users who've made a reasonable line break but still
  // have lines longer than one soft-wrap column.
  var MSG_WARN_CHARS = 55;

  // True when any explicit line (text between \n's) is long enough that
  // the server-rendered preview (save-for-later email, cart thumbnail)
  // will overflow the print bounds. The live editor soft-wraps visually
  // so it looks fine here, but the server renderer doesn't word-wrap —
  // see DUP-827e7b49. Drives a non-blocking nudge that asks the user to
  // press Enter where they want the break to land.
  function messageHasLongLine(s) {
    if (!s) return false;
    var parts = s.split('\n');
    for (var i = 0; i < parts.length; i++) {
      if (parts[i].length > MSG_WARN_CHARS) return true;
    }
    return false;
  }

  function updateMessageNudge() {
    var input = document.getElementById('sm2-message-input');
    var hint = document.getElementById('sm2-message-hint');
    if (!input || !hint) return;
    var warn = messageHasLongLine(input.value);
    input.classList.toggle('sm2-input--warn', warn);
    hint.classList.toggle('sm2-hint--warn', warn);
    hint.textContent = warn
      ? 'Long line — your printed version may wrap differently from the preview. Press Enter to break where you want.'
      : 'Up to 4 lines — text shrinks to fit.';
  }

  function updateCaption() {
    var msg = document.getElementById('sm2-caption-message');
    var headline = document.getElementById('sm2-caption-headline');
    var block = msg && msg.parentElement;
    var date = document.getElementById('sm2-caption-date');
    var loc = document.getElementById('sm2-caption-location');
    var coords = document.getElementById('sm2-caption-coords');
    if (headline) headline.textContent = state.headline || '';
    if (msg) {
      if (smHwHasContent()) {
        // Swap text content for handwriting SVG, sized via print_size width
        // and message_height vertical margin. Strokes use the caption text
        // colour resolved by the parent block.
        msg.textContent = '';
        msg.innerHTML = smHwHTML();
      } else {
        msg.innerHTML = '';
        msg.textContent = state.customMessage || '';
        var lines = countMessageLines(state.customMessage);
        var sizeCqh = lines <= 2 ? 2.143 : lines === 3 ? 1.857 : 1.571;
        // Script faces (Sacramento, Great Vibes, Allura, …) have x-heights
        // ~30–50% smaller than the sans/serif details font. At the same px
        // they read visibly smaller — and on 4+ line messages the base size
        // already drops to match details (1.571cqh), making the message look
        // *smaller* than details. Bump per-family so the message holds visual
        // weight. Pacifico is excluded — it already has a generous x-height.
        // Conservative bumps — paired with a small details size reduction in
        // CSS (1.571cqh → 1.4cqh) so the message doesn't have to grow huge to
        // look bigger than the details. Targets a visual hierarchy where the
        // message reads roughly the same size as details on script faces, and
        // a touch larger on sans/serif. Pacifico's x-height is already big
        // enough that no bump is needed.
        var SCRIPT_BUMP = {
          'Sacramento': 1.25, 'Great Vibes': 1.25, 'Allura': 1.25,
          'Aguafina Script': 1.25, 'Alex Brush': 1.2, 'Parisienne': 1.2,
          'Dancing Script': 1.15, 'Caveat': 1.15, 'Yellowtail': 1.15,
          'Kaushan Script': 1.1, 'Satisfy': 1.1
        };
        var bump = SCRIPT_BUMP[state.font] || 1;
        msg.style.fontSize = (sizeCqh * bump) + 'cqh';
      }
    }
    if (block && state.font) block.style.fontFamily = "'" + state.font + "', sans-serif";
    var details = document.querySelector('.sm2-caption-details');
    if (details && state.detailsFont) details.style.fontFamily = "'" + state.detailsFont + "', sans-serif";
    if (date) date.textContent = effectiveDateTime();
    if (loc) loc.textContent = effectiveLocation();
    if (coords) coords.textContent = effectiveCoords();
  }

  // Display-only resolution: override wins when non-empty, else fall back to
  // the auto value driven by the geocode / formatter.
  function effectiveLocation() {
    var ov = (state.locationOverride || '').trim();
    if (ov) return ov;
    // Deliberately cleared → blank line (non-breaking space keeps its height
    // on both the preview and the print; not collapsed by div:empty).
    if (state.locationOverrideCleared) return '\u00A0';
    return state.location || '';
  }
  function effectiveCoords() {
    var ov = (state.coordsOverride || '').trim();
    if (ov) return ov;
    if (state.coordsOverrideCleared) return '\u00A0';
    return state.coordsDisplay || '';
  }
  // Auto dateline the print would show: date-only when showTime is off,
  // date + time when on. formatDateTime() already drops the time when passed a
  // falsy `time`, so honour showTime by passing null in that case.
  function computedDateTime() {
    if (!state.date) return '';
    return formatDateTime(state.date, state.showTime ? state.time : null);
  }
  function effectiveDateTime() {
    var ov = (state.dateTimeOverride || '').trim();
    if (ov) return ov;
    if (dateTimeOverrideCleared) return '\u00A0';
    return computedDateTime();
  }
  // Keep the Printed date & time override input mirroring the auto value while
  // the user hasn't overridden it, so it re-derives when the date, time or
  // show-time toggle change. No-op once the field is dirty.
  function syncDateTimeOverrideField() {
    if (dateTimeOverrideDirty) return;
    // A date/time/show-time change re-derives the printed date, cancelling any
    // prior "deliberately cleared" state.
    dateTimeOverrideCleared = false;
    var el = document.getElementById('sm2-datetime-override');
    if (!el || document.activeElement === el) return;
    var v = computedDateTime();
    if (el.value !== v) el.value = v;
  }

  // Normalise a date input to a bare YYYY-MM-DD. Tolerates a full ISO timestamp
  // (e.g. "2026-06-08T06:00:34.004Z") that some legacy/agent-created sessions
  // stored in state.date — without this, appending 'T'+time produced an Invalid
  // Date and the print line rendered "NANth Invalid Date NAN".
  function dateOnly(isoDate) {
    return String(isoDate).slice(0, 10);
  }

  function formatDateTime(isoDate, time) {
    if (!isoDate) return '';
    var datePart = formatDate(isoDate);
    if (!time) return datePart;
    try {
      var d = new Date(dateOnly(isoDate) + 'T' + time);
      // No leading zero ("7:00 PM"), as the print writes it
      // (render_star_map.py strftime('%I:%M %p').lstrip('0')).
      var timePart = d.toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true }).toUpperCase();
      return datePart + ' - ' + timePart;
    } catch (e) { return datePart; }
  }

  function formatDate(isoDate, time) {
    if (!isoDate) return '';
    try {
      var d = new Date(dateOnly(isoDate) + 'T' + (time || '00:00'));
      var day = d.getDate();
      var month = d.toLocaleDateString('en-GB', { month: 'long' });
      var year = d.getFullYear();
      return day + ordinalSuffix(day) + ' ' + month + ' ' + year;
    } catch (e) { return isoDate; }
  }

  function ordinalSuffix(n) {
    var s = ['th', 'st', 'nd', 'rd'];
    var v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  }

  function updateDebug() {
    var el = document.getElementById('sm2-state-debug');
    if (!el) return;
    var snapshot = {
      printStyle: state.printStyle,
      themeKey: state.themeKey,
      showMilkyway: state.showMilkyway,
      coords: state.coords,
      globals: {
        selectedPrintStyle: window.selectedPrintStyle,
        ribbonSelected: window.ribbonSelected,
        giftWrapSelected: window.giftWrapSelected,
        bundleSelected: window.bundleSelected
      }
    };
    el.textContent = JSON.stringify(snapshot, null, 2);
  }

  // ── Offscreen compositor ─────────────────────────────────────────────────
  // Stamps stars + milkyway into a single JPEG data URL for photographer/explorer.
  // Classic doesn't need this — the canvas + .sm2-milkyway-overlay layer via CSS blend.
  function syncPreviewImage() {
    var img = document.getElementById('sm2-preview-image');
    if (!img) return;
    var starCanvas = document.querySelector('#sm2-celestial-map canvas');
    if (!starCanvas) return;
    var milkyImg = document.querySelector('.sm2-milkyway-overlay');

    var w = starCanvas.width || 1408;
    var h = starCanvas.height || 1408;
    var off = document.createElement('canvas');
    off.width = w; off.height = h;
    var ctx = off.getContext('2d');

    ctx.drawImage(starCanvas, 0, 0, w, h);

    if (milkyImg && milkyImg.complete && milkyImg.naturalWidth > 0) {
      try {
        ctx.globalCompositeOperation = 'lighter';
        ctx.drawImage(milkyImg, 0, 0, w, h);
        ctx.globalCompositeOperation = 'source-over';
      } catch (e) { /* cross-origin — stars only */ }
    }

    try {
      var url = off.toDataURL('image/jpeg', 0.92);
      img.setAttribute('href', url);
      img.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', url);
    } catch (e) { /* canvas tainted */ }
  }

  // ── WebGL milky way renderer (lifted) ────────────────────────────────────

  function cleanupWebGL() {
    // Don't call loseContext() every render — that's what was causing the
    // context-lost cascade. We reuse the same canvas across renders (see
    // _glCanvas) and only force-lose if the caller explicitly needs it.
    _glContext = null;
  }

  function forceLoseWebGL() {
    if (_glContext) {
      try {
        var ext = _glContext.getExtension('WEBGL_lose_context');
        if (ext) ext.loseContext();
      } catch (e) { /* noop */ }
    }
    _glContext = null;
    _glCanvas = null;
  }

  function ensureMilkywayContainer() {
    var el = document.getElementById('sm2-milkyway-container');
    if (!el) {
      el = document.createElement('div');
      el.id = 'sm2-milkyway-container';
      el.style.cssText = 'position:absolute;left:-99999px;top:-99999px;pointer-events:none;';
      document.body.appendChild(el);
    }
    return el;
  }

  var _baseElementCache = null;
  function getBaseElement() {
    // Cache the celestial canvas while it stays in the DOM - the rect reads
    // below run once per milkyway render and were forced whole-page reflows
    // whenever anything had dirtied layout (10s+ per session in traces).
    if (_baseElementCache && _baseElementCache.isConnected) return _baseElementCache;
    _baseElementCache = null;
    var canvas = document.querySelector('#sm2-celestial-map canvas');
    if (canvas && canvas.getBoundingClientRect().width > 0) {
      _baseElementCache = canvas;
      return canvas;
    }
    var map = document.getElementById('sm2-celestial-map');
    if (map && map.getBoundingClientRect().width > 0) return map;
    return null;
  }

  function limitCacheSize(maxSize) {
    if (_imageCache.size <= maxSize) return;
    var currentUrl = milkywayConfig.imageUrl;
    var keys = Array.from(_imageCache.keys())
      .filter(function(k) { return k !== currentUrl; })
      .slice(0, _imageCache.size - maxSize);
    keys.forEach(function(k) {
      var img = _imageCache.get(k);
      if (img) img.src = '';
      _imageCache.delete(k);
    });
  }

  function renderMilkyWay(center, forceRender) {
    if (_isLoading || _isZooming) return;
    var now = Date.now();
    if (!forceRender && now - _lastRenderTime < RENDER_THROTTLE_MS) return;
    _lastRenderTime = now;
    _isLoading = true;

    if (_animFrameId) { cancelAnimationFrame(_animFrameId); _animFrameId = null; }

    _animFrameId = requestAnimationFrame(function() {
      try {
        var isMobile = isMobileDevice();
        if (isMobile) limitCacheSize(2);

        var container = ensureMilkywayContainer();

        var baseElement = getBaseElement();
        if (!baseElement) { _isLoading = false; return; }
        var rect = baseElement.getBoundingClientRect();

        var maxSize = isMobile ? 350 : 1024;
        // Force a SQUARE texture — celestial-map is meant to be square but
        // browser pixel rounding can leave rect.width and rect.height off
        // by 1-2px, which warps the milkyway.
        var dim = Math.min(maxSize, Math.max(200, Math.min(rect.width, rect.height)));

        // Reuse the canvas + GL context across renders. Creating a new canvas
        // each call piles up WebGL contexts until the browser (Chrome caps
        // around 16 per page) drops oldest → the "context lost" cascade.
        var canvas = _glCanvas;
        var gl = _glContext;
        var isFresh = false;
        if (!canvas || !gl || gl.isContextLost && gl.isContextLost()) {
          canvas = document.createElement('canvas');
          canvas.style.background = 'none';
          container.innerHTML = '';
          container.appendChild(canvas);
          gl = canvas.getContext('webgl', {
            premultipliedAlpha: true,
            preserveDrawingBuffer: true,
            antialias: false,
            alpha: false,
            powerPreference: 'low-power',
            failIfMajorPerformanceCaveat: false
          });
          if (!gl) { _isLoading = false; return; }
          _glCanvas = canvas;
          _glContext = gl;
          attachContextLossHandler(canvas);
          isFresh = true;
        }
        if (canvas.width !== dim || canvas.height !== dim) {
          canvas.width = dim;
          canvas.height = dim;
          isFresh = true; // viewport changed, need to rebuild program
        }
        gl.viewport(0, 0, dim, dim);

        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
        gl.clearColor(0, 0, 0, 0);

        setupShaders(gl, center, canvas);
      } catch (e) {
        console.warn('[SM2] renderMilkyWay error:', e);
        _isLoading = false;
      }
    });
  }

  function setupShaders(gl, center, canvas) {
    var vertexShaderSource =
      'attribute vec4 a_position;' +
      'attribute vec2 a_texCoord;' +
      'varying vec2 vUv;' +
      'void main() { vUv = a_texCoord; gl_Position = vec4(a_position.xy, 0.0, 1.0); }';

    var fragmentShaderSource =
      '#ifdef GL_ES\nprecision mediump float;\n#endif\n' +
      '#define PI 3.1415926535897931\n' +
      '#define TAU 6.2831853071795862\n' +
      'varying vec2 vUv;' +
      'uniform sampler2D u_texture1;' +
      'uniform float u_phi1;' +
      'uniform float u_lambda0;' +
      'uniform float u_zoom;' +
      'void main(void) {' +
      '  vec2 uv = (vUv - 0.5) * u_zoom;' +
      '  float x = TAU * uv.x;' +
      '  float y = TAU * uv.y;' +
      '  float c = sqrt(x * x + y * y);' +
      '  if (c < 0.001) { gl_FragColor = texture2D(u_texture1, vec2(0.5, 0.5)); return; }' +
      '  float phi = asin(cos(c) * sin(u_phi1) + y * sin(c) * cos(u_phi1) / c);' +
      '  float lambda = u_lambda0 + atan(x * sin(c), c * cos(u_phi1) * cos(c) - y * sin(u_phi1) * sin(c));' +
      '  vec2 texCoord = vec2(mod(lambda / TAU + 0.5, 1.0), clamp((phi / PI + 0.5), 0.0, 1.0));' +
      '  vec4 color = texture2D(u_texture1, texCoord);' +
      '  vec3 adjustedColor = color.rgb * 0.95;' +
      '  gl_FragColor = vec4(adjustedColor, color.a * 0.9);' +
      '}';

    var program = buildProgram(gl, vertexShaderSource, fragmentShaderSource);
    if (!program) { _isLoading = false; return; }
    gl.useProgram(program);
    setupBuffers(gl, program, center, canvas);
  }

  function buildProgram(gl, vertexSrc, fragmentSrc) {
    var vs = compileShader(gl, gl.VERTEX_SHADER, vertexSrc);
    var fs = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSrc);
    if (!vs || !fs) return null;
    var program = gl.createProgram();
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('[SM2] Program link error:', gl.getProgramInfoLog(program));
      gl.deleteProgram(program);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
      return null;
    }
    gl.detachShader(program, vs);
    gl.detachShader(program, fs);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    return program;
  }

  function compileShader(gl, type, source) {
    var shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.error('[SM2] Shader compile error:', gl.getShaderInfoLog(shader));
      gl.deleteShader(shader);
      return null;
    }
    return shader;
  }

  function setupBuffers(gl, program, center, canvas) {
    var positions = new Float32Array([-1, -1, -1, 1, 1, -1, 1, 1]);
    var texCoords = new Float32Array([0, 1, 0, 0, 1, 1, 1, 0]);
    var positionBuffer = gl.createBuffer();
    var texCoordBuffer = gl.createBuffer();

    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);
    var positionLocation = gl.getAttribLocation(program, 'a_position');
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, texCoords, gl.STATIC_DRAW);
    var texCoordLocation = gl.getAttribLocation(program, 'a_texCoord');
    gl.enableVertexAttribArray(texCoordLocation);
    gl.vertexAttribPointer(texCoordLocation, 2, gl.FLOAT, false, 0, 0);

    loadTexture(gl, program, center, canvas, [positionBuffer, texCoordBuffer]);
  }

  function loadTexture(gl, program, center, canvas, buffers) {
    var texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    var placeholder = new Uint8Array(2 * 2 * 4);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 2, 2, 0, gl.RGBA, gl.UNSIGNED_BYTE, placeholder);

    if (!milkywayConfig.imageUrl) {
      cleanupBuffers(gl, texture, buffers);
      _isLoading = false;
      return;
    }

    var timeout = new Promise(function(_, reject) {
      setTimeout(function() { reject(new Error('Image loading timeout')); }, 5000);
    });

    var imageP = new Promise(function(resolve) {
      if (_imageCache.has(milkywayConfig.imageUrl)) {
        resolve(_imageCache.get(milkywayConfig.imageUrl));
        return;
      }
      var image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = function() {
        _imageCache.set(milkywayConfig.imageUrl, image);
        limitCacheSize(10);
        resolve(image);
      };
      image.onerror = function() { resolve(null); };
      image.src = milkywayConfig.imageUrl;
    });

    Promise.race([imageP, timeout])
      .then(function(img) {
        if (!img) throw new Error('Failed to load image');
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);

        var phi1 = (center[1] * Math.PI) / 180;
        var lambda0 = (center[0] * Math.PI) / 180;
        gl.uniform1f(gl.getUniformLocation(program, 'u_phi1'), phi1);
        gl.uniform1f(gl.getUniformLocation(program, 'u_lambda0'), lambda0);
        gl.uniform1f(gl.getUniformLocation(program, 'u_zoom'), 0.5);

        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

        applyCanvasToBackground(canvas);
        cleanupBuffers(gl, texture, buffers, program);
        _isLoading = false;
      })
      .catch(function(err) {
        console.warn('[SM2] Milky Way render error:', err);
        cleanupBuffers(gl, texture, buffers, program);
        _isLoading = false;
      });
  }

  function cleanupBuffers(gl, texture, buffers, program) {
    if (!gl) return;
    if (texture) gl.deleteTexture(texture);
    if (Array.isArray(buffers)) {
      buffers.forEach(function(b) { if (b) gl.deleteBuffer(b); });
    } else if (buffers) {
      gl.deleteBuffer(buffers);
    }
    // Program must be deleted too — otherwise we leak one per render call
    // even though the underlying GL context is reused.
    if (program) {
      try { gl.deleteProgram(program); } catch (e) { /* noop */ }
    }
  }

  /**
   * Convert the rendered milkyway canvas into an <img class="sm2-milkyway-overlay">
   * layered over #sm2-celestial-map. The CSS rule applies plus-lighter blend
   * and a circular clip-path so the milkyway sits additively on the stars.
   */
  function applyCanvasToBackground(canvas) {
    var map = document.getElementById('sm2-celestial-map');
    if (!map) return;
    var quality = isMobileDevice() ? 0.8 : 0.95;
    var url = canvas.toDataURL('image/jpeg', quality);

    // Reuse the overlay node - remove()+append per frame dirtied layout and
    // made every later geometry read a forced whole-page reflow during
    // milkyway animations. Sizing is owned by CSS (width/height:100%
    // !important on .sm2-milkyway-overlay), so no rect read is needed here.
    var img = map.querySelector('.sm2-milkyway-overlay');
    if (!img) {
      img = document.createElement('img');
      img.className = 'sm2-milkyway-overlay';
      img.alt = '';
      img.setAttribute('aria-hidden', 'true');
      map.appendChild(img);
    }
    img.src = url;

    // If photographer/explorer is active, restamp the composited image into
    // the SVG preview so the visible output matches.
    try { syncPreviewImage(); } catch (e) { /* non-fatal */ }
  }

  function attachContextLossHandler(canvas) {
    // Was registered with { passive: true } and called preventDefault(), which
    // a passive listener silently ignores. That meant the browser was never
    // asked to restore, so the Milky Way never came back after a loss.
    if (!canvas || !window.SkylitWebGL) return;
    window.SkylitWebGL.attach(canvas, {
      label: 'star map milky way',
      critical: true,
      onRestore: function () {
        try {
          renderMilkyWay(milkywayConfig.center, true);
          return true;
        } catch (e) {
          return false;
        }
      },
      onFail: function () {
        var host = document.getElementById('sm2-celestial-map');
        if (host) window.SkylitWebGL.showFallback(host.parentNode || host, { label: 'star map' });
      }
    });
  }

  // ── Celestial canvas → <img> preview hook ────────────────────────────────
  // Celestial renders into a <canvas>; we additionally stamp the canvas into an
  // <img class="sm2-celestial-preview"> so the visible star field is a stable
  // image even while Celestial re-renders. Mirrors v1's convertCanvasToImage.
  //
  // The <img> on the page is sized for the screen (its displayed size x the
  // pixel ratio), not the canvas's 1400px: the sticky phone preview scales
  // it, and every re-raster of a 1400px image into ~500 device px was the
  // costly part of that on iPhone. The full-size stamp is kept off the page
  // for anything that needs the pixels: the snapshot compositor and the
  // full-screen viewer (sm2StarPreviewSource).
  var _starPreviewFullUrl = null;
  var _starPreviewFull = null;
  function sm2StarPreviewSource(fallbackImg) {
    if (_starPreviewFull && _starPreviewFull.complete && _starPreviewFull.naturalWidth) return _starPreviewFull;
    if (fallbackImg && fallbackImg.complete && fallbackImg.naturalWidth) return fallbackImg;
    return null;
  }
  function displaySizedUrl(canvas, map, fullUrl) {
    var dpr = window.devicePixelRatio || 1;
    // A quarter over the displayed size, so the collapse's slight re-grow
    // and fractional scales stay sharp.
    var target = Math.ceil((map.clientWidth || 0) * dpr * 1.25);
    if (!target || target >= canvas.width * 0.9) return fullUrl;
    try {
      var off = document.createElement('canvas');
      off.width = target;
      off.height = Math.round(target * canvas.height / canvas.width);
      var octx = off.getContext('2d');
      octx.imageSmoothingEnabled = true;
      octx.imageSmoothingQuality = 'high';
      octx.drawImage(canvas, 0, 0, off.width, off.height);
      return off.toDataURL('image/png');
    } catch (e) { return fullUrl; }
  }
  function convertCanvasToImage() {
    clearTimeout(_canvasImageDebounce);
    _canvasImageDebounce = setTimeout(function() {
      var map = document.getElementById('sm2-celestial-map');
      if (!map) return;
      var canvas = map.querySelector('canvas');
      if (!canvas || canvas.width === 0) return;
      try {
        var url = canvas.toDataURL('image/png');
        _starPreviewFullUrl = url;
        _starPreviewFull = new Image();
        _starPreviewFull.src = url;
        var img = map.querySelector('.sm2-celestial-preview');
        if (!img) {
          img = document.createElement('img');
          img.className = 'sm2-celestial-preview';
          img.alt = '';
          img.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;z-index:1;display:block;';
          map.appendChild(img);
        }
        img.src = displaySizedUrl(canvas, map, url);
        // Hidden, not just transparent, so it is never painted.
        canvas.style.opacity = '0';
        canvas.style.visibility = 'hidden';
      } catch (e) { console.warn('[SM2] canvas→image failed:', e); }
    }, 50);
  }

  // Re-stamp only when the preview is now shown wider than its stamp covers
  // (rotation, a wider desktop window). iOS fires resize as the toolbars
  // collapse during a scroll; the width doesn't change, so nothing happens.
  var _previewResizeTimer = null;
  window.addEventListener('resize', function () {
    clearTimeout(_previewResizeTimer);
    _previewResizeTimer = setTimeout(function () {
      var map = document.getElementById('sm2-celestial-map');
      var img = map && map.querySelector('.sm2-celestial-preview');
      var canvas = map && map.querySelector('canvas');
      if (!img || !canvas || !canvas.width || !img.naturalWidth || img.naturalWidth >= canvas.width) return;
      var want = (map.clientWidth || 0) * (window.devicePixelRatio || 1);
      if (want > img.naturalWidth) convertCanvasToImage();
    }, 400);
  });

  function setupCelestialImageHook() {
    if (typeof Celestial === 'undefined') return;
    function wrap(name, delay) {
      if (Celestial[name] && !Celestial[name]._sm2Hooked) {
        var original = Celestial[name];
        Celestial[name] = function() {
          original.apply(this, arguments);
          setTimeout(convertCanvasToImage, delay);
        };
        Celestial[name]._sm2Hooked = true;
      }
    }
    wrap('redraw', 100);
    wrap('apply', 100);
    wrap('applyQuiet', 100);
    wrap('date', 150);
    wrap('location', 150);
    wrap('reload', 150);
    // Safety-net late captures. d3-celestial fetches stars.6.json async; if
    // the initial paint happens before the fetch resolves, the stamped
    // preview image can freeze with only constellations visible. A single
    // 500ms capture isn't always enough on slow networks, so we fire a
    // few more staggered captures to guarantee the final frame (with
    // stars) lands in the preview.
    [500, 1500, 3000, 6000].forEach(function(d) {
      setTimeout(convertCanvasToImage, d);
    });
  }

  // ── Celestial.js initialisation ──────────────────────────────────────────
  function initCelestial() {
    if (typeof Celestial === 'undefined') {
      console.warn('[SM2] Celestial not loaded yet');
      return;
    }
    Celestial.display({
      width: 700,
      renderer: 'canvas2d',
      projection: 'airy',
      transform: 'equatorial',
      center: null,
      geopos: null,
      follow: 'zenith',
      orientationfixed: true,
      zoomlevel: null,
      zoomextend: 1,
      adaptable: true,
      interactive: false,
      disableAnimations: true,
      location: false,
      advanced: true,
      daterange: [],
      settimezone: true,
      controls: false,
      container: 'sm2-celestial-map',
      stars: {
        show: true, limit: 6, colors: true,
        designation: false, propername: false,
        size: 12, exponent: -0.29,
        data: 'stars.6.json'
      },
      dsos: { show: false, data: 'dsos.bright.json' },
      constellations: {
        show: true,
        names: false,
        // 'iau' = International Astronomical Union full Latin names (Andromeda,
        // Cassiopeia, …) — v1's choice. 'en' gives short English forms which
        // render in a different style and miss constellations without a name.
        namesType: 'iau',
        lines: true,
        bounds: false,
        lineStyle: { stroke: '#cccccc', width: 2, opacity: 0.75 },
        nameStyle: {
          fill: '#ffffff', align: 'center', baseline: 'middle', opacity: 0.8,
          // Three-entry font array: Celestial picks size by constellation rank.
          font: [
            '12px Josefin Sans, Open Sans, Arial, sans-serif',
            '9px Josefin Sans, Open Sans, Arial, sans-serif',
            '9px Josefin Sans, Open Sans, Arial, sans-serif'
          ]
        }
      },
      mw: { show: false, style: { fill: '#ffffff', opacity: '0' } },
      lines: {
        graticule: { show: false },
        equatorial: { show: false },
        ecliptic: { show: false },
        galactic: { show: false },
        supergalactic: { show: false }
      },
      horizon: { show: false },
      daylight: { show: false },
      planets: {
        show: true,
        which: ['ter'],
        symbolType: 'image',
        symbols: {
          sol: { symbol: '\u2609', letter: 'Su', fill: '#ffff00', size: 12 },
          mer: { symbol: '\u263F', letter: 'Me', fill: '#cccccc' },
          ven: { symbol: '\u2640', letter: 'V',  fill: '#eeeecc' },
          ter: { symbol: '\u2295', letter: 'T',  fill: '#00ccff' },
          lun: { symbol: '\u25CF', letter: 'L',  fill: '#ffffff', size: 40 },
          mar: { symbol: '\u2642', letter: 'Ma', fill: '#ff6600' },
          jup: { symbol: '\u2643', letter: 'J',  fill: '#ffaa33' },
          sat: { symbol: '\u2644', letter: 'Sa', fill: '#ffdd66' },
          ura: { symbol: '\u2645', letter: 'U',  fill: '#66ccff' },
          nep: { symbol: '\u2646', letter: 'N',  fill: '#6666ff' }
        },
        symbolStyle: {
          fill: '#cccccc', opacity: 1,
          font: "bold 17px 'DejaVu Sans Mono', 'Arial Unicode MS', sans-serif",
          align: 'center', baseline: 'middle'
        },
        names: false,
        nameStyle: {
          fill: '#cccccc',
          font: "10px 'Lucida Sans Unicode', 'DejaVu Sans', sans-serif",
          align: 'right', baseline: 'top'
        },
        namesType: 'en'
      },
      form: false,
      datapath: 'https://admin.skylitstudio.co.uk/api/preview-image/static/data/',
      timezoneid: 'GF56K3KYT58X',
      background: { fill: '#000000', opacity: 1, stroke: 'transparent', width: 0 }
    });
    setupCelestialImageHook();
  }

  // ── Theme picker tile rendering ──────────────────────────────────────────
  function renderThemeTiles() {
    var grid = document.getElementById('sm2-theme-grid');
    if (!grid) return;
    // Idempotent: build markup once. Alpine's :class binding then drives
    // .active reactively against $store.sm2.themeKey, so subsequent state
    // changes don't need to rebuild the grid. (Pre-Phase 3 this rebuilt
    // every applyState to re-flag the active tile imperatively.)
    if (grid.dataset.rendered === '1') return;
    grid.innerHTML = THEMES.map(function(t) {
      // Tile thumbnails only need ~400px — ask Shopify's CDN to resize the
      // full milky-way render (255-580 KB) down to a ~9 KB thumbnail.
      var src = CDN_BASE + t.milky_small;
      var bg = src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=400';
      var pos = t.tile_pos || 'center';
      var size = t.tile_size || 'cover';
      var preview = 'background-image:url(\'' + bg + '\');' +
                    'background-position:' + pos + ';' +
                    'background-size:' + size + ';';
      return '<button type="button" ' +
             ':class="$store.sm2.themeKey === \'' + t.key + '\' && \'active\'" ' +
             'class="sm2-tile sm2-tile--theme" ' +
             'data-theme-key="' + t.key + '" ' +
             'aria-label="' + t.name + '">' +
               '<span class="sm2-theme-preview" style="' + preview + '"></span>' +
               '<span class="sm2-theme-label">' + t.name + '</span>' +
             '</button>';
    }).join('');
    grid.dataset.rendered = '1';
  }

  // ── Wizard step navigation ───────────────────────────────────────────────
  var TOTAL_STEPS = 4;
  var _stepScrollRequest = 0;

  // Step 1 is considerably taller than the later steps on mobile. Replacing
  // it from the Back / Next controls while the customer is near its footer
  // makes Safari clamp the old scroll position to the bottom of the
  // newly-shorter document, which strands the customer beside the site footer.
  // In that case, align the step navigation immediately below the follow-along
  // preview. Direct tab taps do not call this helper: the tab bar is already in
  // view and moving the page from there makes the sticky preview cover the new
  // controls.
  // Two animation frames let display:none/block and sticky-preview sizing settle
  // before we measure. Initial hydration deliberately does not call this.
  function scrollActiveStepIntoView() {
    if (!window.matchMedia || !window.matchMedia('(max-width: 900px)').matches) return;
    var nav = document.getElementById('sm2-steps-nav');
    if (!nav) return;

    var request = ++_stepScrollRequest;
    window.requestAnimationFrame(function() {
      window.requestAnimationFrame(function() {
        if (request !== _stepScrollRequest) return;

        var safeTop = 8;
        var main = document.querySelector('.sm2-main');
        if (main) {
          var mainStyle = window.getComputedStyle(main);
          var mainRect = main.getBoundingClientRect();
          var stickyTop = parseFloat(mainStyle.top) || 0;
          // Where the preview's bottom edge rests once pinned. Its `top` is
          // negative (it collapses under the viewport edge), so this is the
          // compact height, even if the page is still above the pin point and
          // the scroll below is what pins it.
          var pinnedBottom = stickyTop + mainRect.height;
          if (mainStyle.position === 'sticky' && pinnedBottom < window.innerHeight * 0.8) {
            safeTop = Math.ceil(pinnedBottom) + 8;
          }
        }

        var navRect = nav.getBoundingClientRect();
        var targetY = Math.max(0, (window.scrollY || window.pageYOffset || 0) + navRect.top - safeTop);
        // This is layout correction, not navigation animation. An instant
        // scroll prevents Safari from painting the clamped footer position
        // while a smooth scroll is still in flight.
        window.scrollTo({ top: targetY, behavior: 'auto' });
      });
    });
  }

  function goToStep(n, options) {
    n = Math.max(1, Math.min(TOTAL_STEPS, n));
    var changed = n !== state.step;
    state.step = n;
    document.querySelectorAll('.sm2-step-tab').forEach(function(t) {
      t.classList.toggle('active', parseInt(t.getAttribute('data-step'), 10) === n);
    });
    document.querySelectorAll('.sm2-step-content').forEach(function(c) {
      c.classList.toggle('active', parseInt(c.getAttribute('data-step-content'), 10) === n);
    });
    var prev = document.getElementById('sm2-step-prev');
    var next = document.getElementById('sm2-step-next');
    if (prev) prev.disabled = n === 1;
    if (next) next.textContent = n === TOTAL_STEPS ? 'Finish' : 'Next →';
    // Step 4 has its own ATC + save-link CTAs, so the step-footer nav is redundant there.
    var footer = document.querySelector('.sm2-step-footer');
    if (footer) footer.style.display = n === TOTAL_STEPS ? 'none' : '';
    // The handwriting canvas is in step 3. When that content was display:none
    // at hydrate time (preset / saved-design load), getBoundingClientRect()
    // returned 0×0 and the strokes never painted. Re-sync once the step
    // becomes visible so the inline canvas paints on first arrival.
    if (n === 3 && typeof window.sm2HandwritingSyncUI === 'function') {
      window.sm2HandwritingSyncUI();
    }
    if (changed && options && options.scroll) scrollActiveStepIntoView();
  }

  // ── Legacy date/time input sync ──────────────────────────────────────────
  // The scroll-wheel pickers (starmap-date-picker.js + starmap-time-picker.js)
  // drive v1's hidden .bear-dates/.bear-months/.bear-years selects and the
  // #timePicker jQuery widget's .hours-dropdown/.minutes-dropdown + AM/PM
  // radios. We observe those controls and push their combined values into
  // state.date / state.time via setState.
  function bindLegacyDateTimeObservers() {
    // IMPORTANT: starmap-date-picker.js / starmap-time-picker.js sync their
    // scroll-wheel selection back to the hidden controls using jQuery's
    // `$(el).trigger('change')`, which fires ONLY jQuery-registered handlers
    // — native addEventListener('change') handlers never see it. So we bind
    // through jQuery when it's present, and fall back to native otherwise.
    function on(el, evt, fn) {
      if (!el) return;
      if (window.jQuery) window.jQuery(el).on(evt, fn);
      else el.addEventListener(evt, fn);
    }

    var dayEl = document.querySelector('.bear-dates');
    var monthEl = document.querySelector('.bear-months');
    var yearEl = document.querySelector('.bear-years');

    function pushDate() {
      if (!dayEl || !monthEl || !yearEl) return;
      var d = parseInt(dayEl.value, 10);
      var m = parseInt(monthEl.value, 10); // 0-indexed month (bear-months uses 0..11)
      var y = parseInt(yearEl.value, 10);
      if (isNaN(d) || isNaN(m) || isNaN(y)) return;
      var iso = y + '-' + String(m + 1).padStart(2, '0') + '-' + String(d).padStart(2, '0');
      // Only re-derive the printed-date override on a REAL date change.
      // syncPickersToState() fires synthetic change events on these selects
      // during resume hydration; an unconditional sync here would reset
      // dateTimeOverrideCleared and resurrect a deliberately-blanked date.
      if (iso !== state.date) {
        setState({ date: iso });
        syncDateTimeOverrideField();
      }
    }

    [dayEl, monthEl, yearEl].forEach(function(el) {
      on(el, 'change', pushDate);
      try {
        new MutationObserver(pushDate).observe(el, { childList: true, attributes: true, subtree: true });
      } catch (e) { /* noop */ }
    });

    function pushTime() {
      var hSel = document.querySelector('#timePicker .hours-dropdown');
      var mSel = document.querySelector('#timePicker .minutes-dropdown');
      var pm = document.getElementById('timePickerMeridianPM');
      if (!hSel || !mSel) return;
      var h = parseInt(hSel.value, 10);
      var mm = parseInt(mSel.value, 10);
      if (isNaN(h) || isNaN(mm)) return;
      var meridiem = pm && pm.checked ? 'PM' : 'AM';
      var h24 = h % 12 + (meridiem === 'PM' ? 12 : 0);
      var time24 = String(h24).padStart(2, '0') + ':' + String(mm).padStart(2, '0');
      // Same guard as pushDate: only a real time change re-derives the
      // printed-date override (see comment there).
      if (time24 !== state.time) {
        setState({ time: time24 });
        syncDateTimeOverrideField();
      }
    }

    // #timePicker is populated asynchronously by timepicker.js — poll until ready.
    function tryWireTime() {
      var hSel = document.querySelector('#timePicker .hours-dropdown');
      if (!hSel) return setTimeout(tryWireTime, 50);
      var mSel = document.querySelector('#timePicker .minutes-dropdown');
      var am = document.getElementById('timePickerMeridianAM');
      var pm = document.getElementById('timePickerMeridianPM');
      on(hSel, 'change', pushTime);
      on(mSel, 'change', pushTime);
      on(am, 'change', pushTime);
      on(pm, 'change', pushTime);
      // Bootstrap radio labels trigger the radio via click — listen to both
      // to catch either path (native click OR jQuery-triggered change).
      document.querySelectorAll('label[for="timePickerMeridianAM"], label[for="timePickerMeridianPM"]').forEach(function(lbl) {
        lbl.addEventListener('click', function() { setTimeout(pushTime, 20); });
      });
      pushTime();
    }
    tryWireTime();

    // Initial flush in case pickers have pre-populated values
    setTimeout(pushDate, 200);
  }

  // Switching to a non-classic composition force-clears the heart/azimuth
  // shape and the border, and hides both pickers — they're meaningless once
  // the starfield fills the rectangle. Stash what we clobbered so returning to
  // Classic puts the customer's design back exactly as they left it. Without
  // this the round trip is lossy and silent: they peek at Header, come back,
  // and their heart is gone with nothing to tell them it happened.
  var compShapeStash = null;

  // ── Event delegation (single root) ───────────────────────────────────────
  function bindEvents() {
    document.addEventListener('click', function(e) {
      // .active is Alpine-driven via :class bindings (Phase 3 Wave 2).
      // Click handlers just call setState(); applyState pipeline still runs.
      var tile = e.target.closest('.sm2-tile[data-print-style]');
      if (tile) {
        var key = tile.getAttribute('data-print-style');
        if (key) setState({ printStyle: key });
        return;
      }
      var themeTile = e.target.closest('.sm2-tile[data-theme-key]');
      if (themeTile) {
        setState({ themeKey: themeTile.getAttribute('data-theme-key') });
        return;
      }
      var shapeTile = e.target.closest('.sm2-tile--shape[data-shape]');
      if (shapeTile) {
        setState({ shape: shapeTile.getAttribute('data-shape') });
        return;
      }
      var compTile = e.target.closest('.sm2-tile--comp[data-composition]');
      if (compTile) {
        var ck = compTile.getAttribute('data-composition');
        // .active is now Alpine-driven via :class against $store.sm2.composition
        // (Phase 3 Wave 1). Imperative classList.toggle removed.
        var patch = { composition: ck };
        var from = state.composition || 'classic';
        // Shape: only Header needs the circle (it replaces the disc with a
        // full-bleed sky). Boxed and Panel just change the caption plaque, and
        // the renderer masks every shape the same way under them, so the
        // customer's shape carries across Classic / Boxed / Panel. Entering
        // Header stashes it; leaving Header restores it.
        if (ck === 'header' && from !== 'header') {
          compShapeStash = compShapeStash || {};
          compShapeStash.shape = state.shape;
          if (state.shape !== 'circle') patch.shape = 'circle';
        } else if (ck !== 'header' && from === 'header' && compShapeStash && compShapeStash.shape) {
          patch.shape = compShapeStash.shape;
          compShapeStash.shape = undefined;
        }
        // Border: Classic only (bordered boxed/panel/header prints have never
        // shipped). Stash it on the way out of Classic, restore on return.
        if (ck !== 'classic' && from === 'classic') {
          compShapeStash = compShapeStash || {};
          compShapeStash.showBorder = state.showBorder;
          if (state.showBorder) patch.showBorder = false;
        } else if (ck === 'classic' && compShapeStash && compShapeStash.showBorder !== undefined) {
          patch.showBorder = compShapeStash.showBorder;
          compShapeStash.showBorder = undefined;
        }
        setState(patch);
        return;
      }
      var stepTab = e.target.closest('.sm2-step-tab[data-step]');
      if (stepTab) {
        // The customer can only tap these while the step nav is already in the
        // viewport. Preserve that viewport; forcing the nav to a new scroll
        // position activates the sticky preview and can expose the site footer.
        goToStep(parseInt(stepTab.getAttribute('data-step'), 10));
        return;
      }
      // Background picker (Classic only) — combines solid colours + image bgs
      var bgTile = e.target.closest('.sm2-tile--bg[data-bg-type]');
      if (bgTile) {
        setState({ background: {
          type: bgTile.getAttribute('data-bg-type'),
          value: bgTile.getAttribute('data-bg-value')
        }});
        return;
      }

      // Star map element toggles (Constellations / Grid / Labels / Planets / Milky Way / Border)
      var elTile = e.target.closest('.sm2-tile[data-element-key]');
      if (elTile) {
        var elKey = elTile.getAttribute('data-element-key');
        var stateKeyMap = {
          constellations: 'showConstellations',
          grid: 'showGrid',
          labels: 'showLabels',
          planets: 'showPlanets',
          milkyway: 'showMilkyway',
          border: 'showBorder'
        };
        var stateKey = stateKeyMap[elKey];
        if (!stateKey) return;
        var next = !state[stateKey];
        var patch = {}; patch[stateKey] = next;
        // Keep the Milky Way checkbox in Step 2 in sync (the tile's .active
        // is now Alpine-driven from $store.sm2.showMilkyway).
        if (elKey === 'milkyway') {
          var mwCheck = document.getElementById('sm2-milkyway-toggle');
          if (mwCheck) mwCheck.checked = next;
        }
        setState(patch);
        return;
      }
    });

    var mwToggle = document.getElementById('sm2-milkyway-toggle');
    if (mwToggle) {
      mwToggle.addEventListener('change', function() {
        clearTimeout(_milkywayToggleTimer);
        var on = mwToggle.checked;
        // .active on the milkyway tile is now Alpine-driven via :class.
        _milkywayToggleTimer = setTimeout(function() { setState({ showMilkyway: on }); }, 150);
      });
    }

    // Prev / Next buttons
    var prevBtn = document.getElementById('sm2-step-prev');
    var nextBtn = document.getElementById('sm2-step-next');
    if (prevBtn) prevBtn.addEventListener('click', function() { goToStep(state.step - 1, { scroll: true }); });
    if (nextBtn) nextBtn.addEventListener('click', function() { goToStep(state.step + 1, { scroll: true }); });

    // Date — listen for changes on v1's hidden selects, which the scroll-wheel
    // picker (starmap-date-picker.js) writes to via $(...).trigger('change').
    bindLegacyDateTimeObservers();

    // Headline (debounced via scheduleRender)
    var headlineInput = document.getElementById('sm2-headline-input');
    if (headlineInput) {
      var _hlTimer = null;
      headlineInput.addEventListener('input', function() {
        clearTimeout(_hlTimer);
        var v = headlineInput.value;
        _hlTimer = setTimeout(function() { setState({ headline: v }); }, 180);
      });
    }

    // Display-only overrides for the printed location + coordinates line.
    // Empty input = use the auto value (geocoded address / formatted coords);
    // non-empty input = that literal string is what prints. The override
    // does NOT touch state.location / state.coords, so the star chart
    // calculation and cart line-item geocoding are unaffected.
    var locOverrideInput = document.getElementById('sm2-location-override');
    if (locOverrideInput) {
      var _locOvTimer = null;
      locOverrideInput.addEventListener('input', function() {
        clearTimeout(_locOvTimer);
        var v = locOverrideInput.value;
        _locOvTimer = setTimeout(function() { setState({ locationOverride: v, locationOverrideCleared: v.trim() === '' }); }, 180);
      });
    }
    var coordsOverrideInput = document.getElementById('sm2-coords-override');
    if (coordsOverrideInput) {
      var _coOvTimer = null;
      coordsOverrideInput.addEventListener('input', function() {
        clearTimeout(_coOvTimer);
        var v = coordsOverrideInput.value;
        _coOvTimer = setTimeout(function() { setState({ coordsOverride: v, coordsOverrideCleared: v.trim() === '' }); }, 180);
      });
    }
    // Printed date & time override. Non-empty marks the field dirty (auto-sync
    // pauses); clearing it resumes auto-sync to computedDateTime(). Print-text
    // only — never touches state.date / state.time / the star chart calc.
    var dtOverrideInput = document.getElementById('sm2-datetime-override');
    if (dtOverrideInput) {
      var _dtOvTimer = null;
      dtOverrideInput.addEventListener('input', function() {
        clearTimeout(_dtOvTimer);
        var v = dtOverrideInput.value;
        dateTimeOverrideDirty = (v.trim() !== '');
        dateTimeOverrideCleared = (v.trim() === '');
        _dtOvTimer = setTimeout(function() { setState({ dateTimeOverride: v }); }, 180);
      });
    }

    // Custom message — dynamic font scaling (see updateCaption); hard cap
    // at MSG_MAX_LINES visual lines (soft-wrap aware). If user exceeds the
    // cap we revert the textarea to the last-valid value rather than
    // silently truncating, so the cursor doesn't jump unexpectedly.
    var msgInput = document.getElementById('sm2-message-input');
    if (msgInput) {
      var _msgTimer = null;
      var _lastValidMsg = msgInput.value || state.customMessage || '';
      msgInput.addEventListener('input', function() {
        var v = msgInput.value;
        // Hard cap = EXPLICIT lines (what the print honours); the soft-wrap
        // estimate only guards against pasted walls of text.
        if (countExplicitLines(v) > MSG_MAX_LINES || countMessageLines(v) > MSG_MAX_EST_LINES) {
          msgInput.value = _lastValidMsg;
          updateMessageNudge();
          return;
        }
        _lastValidMsg = v;
        updateMessageNudge();
        clearTimeout(_msgTimer);
        _msgTimer = setTimeout(function() { setState({ customMessage: v }); }, 180);
      });
      // Initial pass so prefilled text (saved-design hydrate, preset seed)
      // also gets the warn styling if it contains a long unbroken line.
      updateMessageNudge();
    }

    // Font — tile grid + scope tabs (Both / Message / Details). The scope
    // controls which state field(s) the tile click writes to: message
    // updates state.font, details updates state.detailsFont, both updates
    // both. The active-tile highlight reflects whichever scope is selected
    // (both is considered "matching" only when font === detailsFont).
    var fontGrid = document.getElementById('sm2-font-grid');
    var fontScopeBar = document.getElementById('sm2-font-scope');
    // Font picker .active is Alpine-driven via :class with a window helper
    // that mirrors the original target-based logic (details/message/both).
    function refreshFontTileActive() { /* Alpine handles .active now */ }
    window.sm2IsFontActive = function (fontName) {
      // Must read through the Alpine store proxy, not the raw `state` object:
      // Alpine tracks a :class binding's deps from the reactive reads made
      // while it evaluates. Reading raw `state` registers no deps, so the
      // tiles would evaluate once at boot and never update on font changes.
      var s = (window.Alpine && window.Alpine.store && window.Alpine.store('sm2')) || state;
      var target;
      if (s.fontScope === 'details') target = s.detailsFont;
      else if (s.fontScope === 'message') target = s.font;
      else target = s.font === s.detailsFont ? s.font : null;
      return target !== null && fontName === target;
    };
    if (fontGrid) {
      fontGrid.addEventListener('click', function(e) {
        var btn = e.target.closest('.sm2-font-btn[data-font]');
        if (!btn) return;
        var fn = btn.getAttribute('data-font');
        var patch = {};
        if (state.fontScope === 'message' || state.fontScope === 'both') patch.font = fn;
        if (state.fontScope === 'details' || state.fontScope === 'both') patch.detailsFont = fn;
        setState(patch);
      });
    }
    if (fontScopeBar) {
      fontScopeBar.addEventListener('click', function(e) {
        var btn = e.target.closest('.sm2-font-scope-btn[data-scope]');
        if (!btn) return;
        // .active on the scope buttons is Alpine-driven; we just push fontScope
        // through the proxy so :class re-evaluates.
        sm2StoreSet('fontScope', btn.getAttribute('data-scope'));
      });
    }

    // Show-time toggle — hides/shows the time part of the date caption line
    var showTimeCbx = document.getElementById('sm2-show-time');
    if (showTimeCbx) {
      showTimeCbx.addEventListener('change', function() {
        setState({ showTime: showTimeCbx.checked });
        syncDateTimeOverrideField();
      });
    }

    // ── Helper toolbar (top-right of preview) — cycle messages, cycle
    // fonts, save-for-later. Lifted from v1's .floating-toolbar behaviour
    // so users can quick-browse options without touching the sidebar.
    var PRESET_MESSAGES = [
      'Written In The Stars\nTold By You',
      'The Moment\nEverything Changed',
      'Under The Same Stars\nForever And Always',
      'The night we fell in love',
      'Under the same stars',
      'Our love story began here',
      'Forever and always',
      'Our Perfect Moment\nFrozen In Time',
      'Where it all started...',
      'The Night It All Began',
      'The day everything changed',
      'Our perfect moment',
      'Together under the stars',
      'This is where our story begins'
    ];
    var _msgCycleIdx = -1;
    var msgBtn = document.getElementById('sm2-toolbar-msg');
    if (msgBtn && msgInput) {
      msgBtn.addEventListener('click', function() {
        _msgCycleIdx = (_msgCycleIdx + 1) % PRESET_MESSAGES.length;
        var next = PRESET_MESSAGES[_msgCycleIdx];
        msgInput.value = next;
        _lastValidMsg = next;
        setState({ customMessage: next });
      });
    }

    var fontBtn = document.getElementById('sm2-toolbar-font');
    if (fontBtn && fontGrid) {
      fontBtn.addEventListener('click', function() {
        var tiles = Array.prototype.slice.call(fontGrid.querySelectorAll('.sm2-font-btn[data-font]'));
        if (!tiles.length) return;
        var fontList = tiles.map(function(t) { return t.getAttribute('data-font'); });
        // Advance from whichever font the current scope is tracking.
        var current = state.fontScope === 'details' ? state.detailsFont : state.font;
        var curIdx = fontList.indexOf(current);
        var nextIdx = (curIdx + 1) % fontList.length;
        var fn = fontList[nextIdx];
        var patch = {};
        if (state.fontScope === 'message' || state.fontScope === 'both') patch.font = fn;
        if (state.fontScope === 'details' || state.fontScope === 'both') patch.detailsFont = fn;
        setState(patch);
        refreshFontTileActive();
      });
    }

    var saveBtn = document.getElementById('sm2-toolbar-save');
    if (saveBtn) {
      saveBtn.addEventListener('click', function() { openSaveModal(); });
    }

    bindSaveModal();
    bindFinishUI();
  }

  // ── Save-for-later modal ────────────────────────────────────────────────

  function openSaveModal() {
    var overlay = document.getElementById('sm2-save-modal');
    if (!overlay) return;
    var err = document.getElementById('sm2-save-error');
    if (err) { err.style.display = 'none'; err.textContent = ''; }
    var emailInput = document.getElementById('sm2-save-email');
    if (emailInput && state.email) emailInput.value = state.email;
    overlay.classList.add('active');
  }
  function closeSaveModal() {
    var overlay = document.getElementById('sm2-save-modal');
    if (overlay) overlay.classList.remove('active');
  }
  function bindSaveModal() {
    var overlay = document.getElementById('sm2-save-modal');
    if (!overlay) return;
    var backdrop = overlay.querySelector('.sm2-save-modal__backdrop');
    var closeBtn = overlay.querySelector('.sm2-save-modal__close');
    var submit   = document.getElementById('sm2-save-submit');
    if (backdrop) backdrop.addEventListener('click', closeSaveModal);
    if (closeBtn) closeBtn.addEventListener('click', closeSaveModal);
    if (submit) submit.addEventListener('click', function() {
      var email = (document.getElementById('sm2-save-email').value || '').trim();
      var newsletter = !!(document.getElementById('sm2-save-newsletter') && document.getElementById('sm2-save-newsletter').checked);
      var errEl = document.getElementById('sm2-save-error');
      if (!/^\S+@\S+\.\S+$/.test(email)) {
        if (errEl) { errEl.textContent = 'Please enter a valid email.'; errEl.style.display = 'block'; }
        return;
      }
      submit.disabled = true;
      submit.textContent = 'Checking…';
      (window.SkylitEmailCheck ? window.SkylitEmailCheck(email) : Promise.resolve({ ok: true })).then(function(chk) {
        if (!chk.ok) {
          submit.disabled = false;
          submit.textContent = 'Send save link';
          if (errEl) { errEl.textContent = chk.message; errEl.style.display = 'block'; }
          return;
        }
      if (errEl) { errEl.style.display = 'none'; }
      submit.textContent = 'Sending…';
      Cart.saveForLater(email, newsletter).then(function(res) {
        submit.disabled = false;
        submit.textContent = 'Send save link';
        if (res && res.status === 429) {
          if (errEl) { errEl.textContent = "You've sent too many save requests. Please try again later."; errEl.style.display = 'block'; }
          return;
        }
        if (res && res.status === 400 && res.body && res.body.message) {
          if (errEl) { errEl.textContent = res.body.message; errEl.style.display = 'block'; }
          return;
        }
        closeSaveModal();
        var toast = document.getElementById('sm2-save-success');
        if (toast) {
          toast.classList.add('active');
          setTimeout(function() { toast.classList.remove('active'); }, 4000);
        }
      }).catch(function(e) {
        submit.disabled = false;
        submit.textContent = 'Send save link';
        if (errEl) { errEl.textContent = 'Something went wrong. Please try again.'; errEl.style.display = 'block'; }
        console.warn('[sm2.cart] save failed', e);
      });
      });
    });
  }

  // ── Finish step (gift picker + add-to-cart) ─────────────────────────────

  function bindFinishUI() {
    // Gift picker — mutually exclusive radios; state.gift = 'none' means no companion line.
    var giftEls = document.querySelectorAll('[data-sm2-gift]');
    Array.prototype.forEach.call(giftEls, function(el) {
      el.addEventListener('click', function() {
        var g = el.getAttribute('data-sm2-gift') || 'none';
        // An explicit wrapping choice supersedes any stashed pre-Print
        // selection - don't silently bring the old one back later.
        _giftBeforePrint = null;
        setState({ gift: g });
        refreshGiftTiles();
      });
    });
    refreshGiftTiles();

    // Ribbon-colour swatches — recolour the live preview ribbon and record
    // the colour for the gift add-on cart property. Only shown when the
    // wrapping is Ribbon or Bundle (x-show in the section markup).
    var ribbonSwatches = document.querySelectorAll('[data-sm2-ribbon-color]');
    Array.prototype.forEach.call(ribbonSwatches, function(el) {
      el.addEventListener('click', function() {
        setState({ ribbonColor: el.getAttribute('data-sm2-ribbon-color') || 'Red' });
      });
    });

    // Paper design swatches (gift-paper-picker snippet).
    sm2PaperPicker = window.SkylitGifts
      ? window.SkylitGifts.bindPaperPicker(document.getElementById('sm2-gift-paper'), function(id) { setState({ giftPaper: id }); })
      : null;

    // Type picker — switching to FRAMED auto-selects Black if no colour set,
    // so the frame preview has something to render with.
    var typeEls = document.querySelectorAll('[data-sm2-type]');
    Array.prototype.forEach.call(typeEls, function(el) {
      el.addEventListener('click', function() {
        var t = el.getAttribute('data-sm2-type');
        var patch = { selectedType: t };
        if (t === 'FRAMED' && !state.selectedFrame) patch.selectedFrame = 'Dark Brown';
        if (t !== 'FRAMED') patch.selectedFrame = '';
        // Canvas doesn't come in 60x80 — fall back to 50x70 if needed.
        if (t === 'CANVAS' && state.selectedSize === '60x80') patch.selectedSize = '50x70';
        setState(patch);
        refreshFinishTiles();
      });
    });

    // Size picker (buttons inside the type picker's size row)
    var sizeEls = document.querySelectorAll('[data-sm2-size]');
    Array.prototype.forEach.call(sizeEls, function(el) {
      el.addEventListener('click', function() {
        setState({ selectedSize: el.getAttribute('data-sm2-size') });
        refreshFinishTiles();
      });
    });

    // Frame colour picker (FRAMED only)
    var frameEls = document.querySelectorAll('[data-sm2-frame]');
    Array.prototype.forEach.call(frameEls, function(el) {
      el.addEventListener('click', function() {
        setState({ selectedFrame: el.getAttribute('data-sm2-frame') });
        refreshFinishTiles();
      });
    });

    var atc = document.getElementById('sm2-add-to-cart');
    if (atc) atc.addEventListener('click', function() {
      if (atc.hasAttribute('data-sm2-soldout')) return;
      atc.disabled = true;
      var original = atc.textContent;
      var busy = state.editMode ? 'Updating…' : 'Adding…';
      if (!atcMorph.start(atc, busy)) atc.textContent = busy;
      launchCartFlight(atc);
      Cart.addToCart().catch(function(e) {
        if (_cartFlight) _cartFlight.cancel();
        console.warn('[sm2.cart] atc failed', e);
        // Shopify rate-limits /cart/* endpoints (often via the bot-challenge
        // page which returns 429 with HTML). _post tags the thrown Error
        // with .httpStatus so we can give a non-generic message. .claimBlocked
        // marks the defensive claim-mode rejection from addToCart() above —
        // its message is already the specific driver/claim message.
        if (e && (e.claimBlocked || e.soldOut)) {
          alert(e.message);
        } else if (e && e.httpStatus === 429) {
          alert('Too many requests right now — please wait a moment and try again.');
        } else {
          alert('Unable to add to cart. Please try again.');
        }
        atc.disabled = false;
        atc.textContent = original;
        atcMorph.reset(true);
      });
    });

    var saveLink = document.getElementById('sm2-save-link');
    if (saveLink) saveLink.addEventListener('click', function(e) {
      e.preventDefault();
      openSaveModal();
    });

    // Post-purchase design-claim handshake (design-claim.js). Fired once
    // (from inside a fetch().then(), so always async and strictly after this
    // synchronous bindFinishUI() call registers the listener — the type/
    // size/frame tiles are static Liquid markup, not innerHTML-rebuilt at
    // boot the way name-landsat's are, so there is no async-boot race to
    // guard against here and no pendingClaimVariant-style queue is needed)
    // only when the claim resolved to a confirmed pending order. Pre-selects
    // the paid type/size/frame via the same setState() the tile click
    // handlers use, then re-locks via refreshFinishTiles() (see the
    // claim-lock choke point above). design-claim.js disabled the CTA the
    // instant ?claim= was seen ("Loading your order…") and never re-enables
    // it itself — only this handshake hands control back, now that the
    // claim is confirmed usable.
    document.addEventListener('skylit:claim-ready', function (e) {
      var claim = (e && e.detail) || {};
      // Variant title format is Shopify's own "Type / Size / Frame", so
      // claim.type/size/frame are literally the paid variant's
      // option1/option2/option3 strings — same case-insensitive matching
      // starmap already applies when hydrating from an order (see
      // resolveVariant/hasExactVariant above), not a bespoke translation.
      var typeWanted = (claim.type || '').trim().toLowerCase();
      var patch = {
        selectedType: typeWanted === 'framed' ? 'FRAMED' : typeWanted === 'canvas' ? 'CANVAS' : 'PRINT'
      };
      if (claim.size) patch.selectedSize = String(claim.size).toLowerCase().replace(/\s|cm/g, '');
      patch.selectedFrame = patch.selectedType === 'FRAMED' ? (claim.frame || '') : '';
      setState(patch);
      refreshFinishTiles();

      var atc = document.getElementById('sm2-add-to-cart');
      if (atc) {
        // Mirror aria-disabled with .disabled here — design-claim.js's
        // setCtaLabel() only ever sets [data-claim-cta] to a disabled
        // state (loading/blocked); this handshake is the only place that
        // re-enables #sm2-add-to-cart, so it must clear aria-disabled too
        // or the button stays announced as unavailable to assistive tech
        // even once .disabled is false. Keep this pair moving together.
        atc.disabled = false;
        atc.removeAttribute('aria-disabled');
        atc.textContent = 'Confirm my design';
      }
    });

    refreshFinishTiles();
  }

  function refreshGiftTiles() {
    // .active is Alpine-driven via :class against $store.sm2.gift. No-op kept
    // for backward compatibility with call sites; safe to remove later.
  }

  function refreshFinishTiles() {
    // Type / size / frame .active classes are now Alpine-driven via :class
    // bindings against $store.sm2.selectedType / .selectedSize / .selectedFrame.
    // Remaining work for this function: visibility (60x80 hidden on Canvas),
    // disabled state on unavailable frame variants, and price/variant resolution.

    // Claim mode (post-purchase design link): size/type/frame are the paid
    // variant and must stay locked to whatever skylit:claim-ready
    // pre-selected, regardless of stock availability. This function is the
    // SINGLE choke point where .disabled gets assigned for these three
    // pickers — every caller (click handlers in bindFinishUI, this
    // function's own early-return branches below, any future
    // hydrate/availability path) funnels through here, so the
    // `applyClaimLock` calls are load-bearing. Do not "simplify" them back
    // out or move the lock to a one-time call-site instead — a caller that
    // re-renders after boot (e.g. a future hydrate-from-order path) would
    // silently re-enable a locked button exactly the way task-9a's
    // name-landsat regression did (see task-9a-fix-report.md round 2).
    var claimActive = !!(window.SkylitClaim && window.SkylitClaim.active);
    function applyClaimLock(el, baseDisabled) {
      var claimLocked = claimActive && !!el.closest('[data-claim-locked]');
      el.disabled = baseDisabled || claimLocked;
      if (claimLocked) {
        el.setAttribute('aria-disabled', 'true');
        el.tabIndex = -1;
      } else if (!baseDisabled) {
        el.removeAttribute('aria-disabled');
        el.removeAttribute('tabindex');
      }
    }
    // Stock for a type + size (+ frame, or any frame when omitted):
    // 'ok', 'soldout' (made, none in stock) or 'none' (not made). Claim mode
    // skips stock entirely: that variant is already paid for.
    function stockOf(type, size, frame) {
      if (claimActive) return 'ok';
      var vs = window.sm2ProductVariants || [];
      var tw = String(type || '').toLowerCase(), sw = String(size || '').toLowerCase();
      var fw = frame ? String(frame).toLowerCase() : null;
      var made = false;
      for (var i = 0; i < vs.length; i++) {
        var v = vs[i];
        var t = (v.option1 || '').toLowerCase();
        if (tw === 'print' ? !(t === 'print' || t === 'poster') : t !== tw) continue;
        if ((v.option2 || '').toLowerCase().replace(/\s|cm/g, '').indexOf(sw) !== 0) continue;
        if (fw && (v.option3 || '').toLowerCase() !== fw) continue;
        made = true;
        if (v.available !== false) return 'ok';
      }
      return made ? 'soldout' : 'none';
    }
    function markSoldOut(el, soldOut) {
      var lab = el.querySelector('.sm2-tile-label');
      var name = (lab ? lab.textContent : el.textContent).trim();
      el.classList.toggle('sm2-tile--soldout', soldOut);
      if (soldOut) el.setAttribute('aria-label', name + ', sold out');
      else el.removeAttribute('aria-label');
    }

    // A finish with nothing in stock at the chosen size (e.g. Framed at
    // 60x80 with every frame sold out) can't be picked.
    Array.prototype.forEach.call(document.querySelectorAll('[data-sm2-type]'), function (t) {
      var code = t.getAttribute('data-sm2-type');
      var soldOut = stockOf(code, state.selectedSize) === 'soldout';
      t.classList.toggle('sm2-tile--disabled', soldOut);
      markSoldOut(t, soldOut);
      applyClaimLock(t, soldOut);
    });

    var hide60x80 = state.selectedType === 'CANVAS';
    // Large framed AND large canvas parcels ship UK-only (delivery profile
    // "Large framed — UK only"). Same blocked-size list applies to both finishes;
    // canvas only ever offers 50x70 as a large size (60x80 is hidden on canvas).
    var blockedFramedSizes = window.sm2BlockedFramedSizes || [];
    var marketGatedType = state.selectedType === 'FRAMED' || state.selectedType === 'CANVAS';
    Array.prototype.forEach.call(document.querySelectorAll('[data-sm2-size]'), function(t) {
      var code = t.getAttribute('data-sm2-size');
      t.style.display = (hide60x80 && code === '60x80') ? 'none' : '';
      var marketBlocked = marketGatedType && blockedFramedSizes.indexOf(code) !== -1;
      // Sold out in this finish (for Framed: in every frame colour).
      var sizeSoldOut = !marketBlocked && stockOf(state.selectedType, code) === 'soldout';
      t.classList.toggle('sm2-tile--disabled', marketBlocked || sizeSoldOut);
      markSoldOut(t, sizeSoldOut);
      applyClaimLock(t, marketBlocked || sizeSoldOut);
    });
    // Size guide mirrors the size tiles: drop sizes this finish can't be
    // bought in, and mark the selected one so the panel doubles as feedback.
    // Shared component — assets/size-guide.js, snippets/size-guide.liquid.
    if (window.SkylitSizeGuide) {
      var guideHidden = marketGatedType ? blockedFramedSizes.slice() : [];
      if (hide60x80 && guideHidden.indexOf('60x80') === -1) guideHidden.push('60x80');
      window.SkylitSizeGuide.sync('sm2', { selected: state.selectedSize, hidden: guideHidden });
    }
    if (marketGatedType && blockedFramedSizes.indexOf(state.selectedSize) !== -1) {
      setState({ selectedSize: '40x50' });
      return;
    }
    var frameTiles = document.querySelectorAll('[data-sm2-frame]');
    var currentFrameStillOk = false;
    var firstAvailable = null;
    var darkBrownAvail = false;
    Array.prototype.forEach.call(frameTiles, function(t) {
      var fname = t.getAttribute('data-sm2-frame');
      var avail = Cart.hasExactVariant({ type: 'FRAMED', size: state.selectedSize, frame: fname });
      // Offered in this size but out of stock: badge it "Sold out" rather
      // than leave a struck-through tile that reads as broken.
      var soldOut = !avail && Cart.hasExactVariant({ type: 'FRAMED', size: state.selectedSize, frame: fname }, true);
      t.classList.toggle('sm2-tile--disabled', !avail);
      markSoldOut(t, soldOut);
      applyClaimLock(t, !avail);
      if (avail) {
        if (!firstAvailable) firstAvailable = fname;
        if (fname === 'Dark Brown') darkBrownAvail = true;
        if (fname === state.selectedFrame) currentFrameStillOk = true;
      }
    });
    if (state.selectedType === 'FRAMED' && !currentFrameStillOk) {
      var fallback = darkBrownAvail ? 'Dark Brown' : firstAvailable;
      if (fallback) { setState({ selectedFrame: fallback }); return; }
    }
    // Frame-picker container visibility
    var fp = document.getElementById('sm2-frame-picker');
    if (fp) fp.style.display = (state.selectedType === 'FRAMED') ? '' : 'none';

    // The chosen combination itself sold out (e.g. a saved or in-cart design
    // reopened after stock ran out): no add to cart / confirm edits.
    var selSoldOut = stockOf(state.selectedType, state.selectedSize,
      state.selectedType === 'FRAMED' ? state.selectedFrame : null) === 'soldout';
    var atcBtn = document.getElementById('sm2-add-to-cart');
    if (atcBtn) {
      if (selSoldOut) {
        atcBtn.disabled = true;
        atcBtn.setAttribute('data-sm2-soldout', '');
      } else if (atcBtn.hasAttribute('data-sm2-soldout')) {
        atcBtn.removeAttribute('data-sm2-soldout');
        atcBtn.disabled = false;
      }
    }
    var soldNote = document.getElementById('sm2-soldout-note');
    if (soldNote) soldNote.hidden = !selSoldOut;

    // Price hint — look up the resolved variant's price and stamp into the ATC text.
    // Displayed total = variant price + selected wrapping add-on (see GIFT_PRICES).
    // The wrapping is still posted to the cart as its own line item (addGiftToCart),
    // so this is purely a display-side sum to keep "Total" honest to what the
    // customer will pay at checkout.
    // The Wrapping pills are rendered from the gift product's FIRST variant
    // (the smallest tier), so re-stamp them for the size selected now —
    // otherwise a 60 x 80 cm still advertises the A4 wrap price.
    try { if (window.SkylitGifts) window.SkylitGifts.refreshLabels(state.selectedSize); } catch (e) {}

    var v = Cart.resolveVariant({});
    if (v) {
      state.variantId = v.id;
      var vidInput = document.getElementById('sm2-variant-id');
      if (vidInput) vidInput.value = v.id;
      var priceEl = document.getElementById('sm2-price');
      if (priceEl && v.price != null) {
        var giftPence = GIFT_PRICES[state.gift] || 0;
        // Size-tiered wrap/bundle price when the map is loaded.
        try {
          if (state.gift !== 'none' && window.SkylitGifts) {
            giftPence = window.SkylitGifts.priceFor(state.gift, state.selectedSize) || giftPence;
          }
        } catch (e) {}
        var totalPence = v.price + giftPence;
        var price = (totalPence / 100).toFixed(2);
        var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
        priceEl.textContent = (cur === 'GBP' ? '£' : cur + ' ') + price;
      }
    }
  }

  // ── Google Places autocomplete (PlaceAutocompleteElement, web component) ──
  // Uses google.maps.places.PlaceAutocompleteElement — the element renders
  // its own shadow-DOM input + dropdown (and a fullscreen dialog on mobile).
  // We mount it into #sm2-location-wrapper and listen for gmp-select.
  var _placesBound = false;
  function bindPlacesAutocomplete() {
    if (_placesBound) return;
    if (typeof google === 'undefined' || !google.maps || !google.maps.places) return;
    if (!google.maps.places.PlaceAutocompleteElement) return;
    var wrapper = document.getElementById('sm2-location-wrapper');
    if (!wrapper) return;
    _placesBound = true;
    // Bias toward UK results — Skylit is a UK store and the audience is
    // ~95% UK customers. Without bias, "Manchester" returns Manchester NH
    // (US) and "Birmingham" returns Birmingham AL (US) above the UK city.
    // requestedRegion shapes ranking; locationBias gives an explicit UK
    // bounding box. Neither RESTRICTS — international customers can still
    // pick non-UK results, they just appear lower in the dropdown.
    // SkylitPlaces swaps in our look-alike (assets/skylit-place-autocomplete.js),
    // which debounces typing and bills picks at the Essentials Places tier.
    var pacOptions = {
      requestedRegion: 'gb',
      locationBias: { north: 60.9, south: 49.5, east: 1.8, west: -8.7 }
    };
    var pac = window.SkylitPlaces
      ? window.SkylitPlaces.createAutocomplete(pacOptions)
      : new google.maps.places.PlaceAutocompleteElement(pacOptions);
    pac.id = 'sm2-location-input';
    pac.className = 'sm2-pac-element';
    // Leave the visible value blank so users don't have to delete the
    // default before typing. state.location stays at the seeded default
    // (London, UK) so the disc still renders for the default location;
    // we only show "London, UK" as a placeholder hint.
    try { pac.placeholder = state.location || 'Enter a location'; } catch (e) { /* noop */ }
    try { pac.setAttribute && pac.setAttribute('placeholder', state.location || 'Enter a location'); } catch (e) { /* noop */ }
    wrapper.insertBefore(pac, wrapper.firstChild);

    pac.addEventListener('gmp-select', async function(event) {
      try {
        var place = event.placePrediction.toPlace();
        await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location', 'addressComponents'] });
        var lat = place.location.lat();
        var lng = place.location.lng();
        // Label = the NAME the user tapped + its country, e.g. "London Bridge,
        // UK" — NOT Google's formattedAddress, which for a POI is a postcoded
        // street address ("London EC4R 3TN"). displayName alone drops the
        // country ("London"), so we append the country pulled from
        // addressComponents (formattedAddress omits it for GB results when the
        // request is region-biased to GB, so we can't read it from there).
        var name = place.displayName || '';
        var country = '';
        var comps = place.addressComponents || [];
        for (var ci = 0; ci < comps.length; ci++) {
          var ctypes = comps[ci].types || [];
          if (ctypes.indexOf('country') !== -1) {
            var sc = comps[ci].shortText;
            if (sc === 'GB') country = 'UK';
            else if (sc === 'US') country = 'USA';
            else country = comps[ci].longText || sc || '';
            break;
          }
        }
        var picked;
        if (name) {
          picked = (country && name.indexOf(country) === -1) ? (name + ', ' + country) : name;
        } else {
          picked = place.formattedAddress || '';
        }
        // Prefill the Printed location / coordinates override fields so
        // the user can tweak the wording (e.g. "Manchester, UK" → "Where
        // we met") without having to retype it from scratch. Stored as
        // overrides — effectiveLocation/effectiveCoords still fall back
        // to the auto value if the user clears them.
        setState({
          location: picked,
          coords: { lat: lat, lng: lng },
          locationOverride: picked,
          coordsOverride: formatCoords(lat, lng),
          // Selecting a new place re-prefills the overrides, so any prior
          // "deliberately cleared" state no longer applies.
          locationOverrideCleared: false,
          coordsOverrideCleared: false
        });
        var disp = document.getElementById('sm2-coords-display');
        if (disp) disp.textContent = formatCoords(lat, lng);
      } catch (e) { console.warn('[sm2] place select failed', e); }
      // The dialog auto-closes on select; release the body lock immediately.
      _pacUnlock();
    });

    // iOS keyboard / autocomplete dialog visibility — restored body-fixed
    // lock (the only reliable way to keep the PlaceAutocomplete dialog
    // visible on iOS, because the dialog is position:fixed against the
    // layout viewport and iOS path-b shifts the visual viewport down,
    // pushing the input/dialog off-screen otherwise). Improvement over
    // the earlier version that felt "hijacked": (a) we now scroll the
    // wrapper to top BEFORE locking (so it actually ends up under the
    // user's finger, not 600px below), and (b) any tap outside the
    // wrapper dismisses the lock so the user can escape without having
    // to tap the input again.
    var _pacLocked = false;
    var _pacSavedScrollY = 0;
    var _pacUnlockTimer = null;
    var _pacViewportResetQueued = false;
    function _pacIsMobile() {
      return window.matchMedia && window.matchMedia('(max-width: 900px)').matches;
    }
    // If the field starts near the bottom of the initial viewport, iOS decides
    // it will need a focus scroll before focusin fires. _pacLock() then moves the
    // field safely below the collapsed preview, but Safari can still apply that
    // now-stale scroll when the keyboard finishes opening. With the body fixed,
    // the intended document offset already lives in body.style.top, so the
    // layout/visual viewport must remain at scroll zero for the duration of the
    // lock. In the normal follow-along state Safari leaves it at zero and this
    // is a no-op.
    function _pacCancelPendingFocusScroll() {
      if (!_pacLocked || _pacViewportResetQueued) return;
      var vv = window.visualViewport;
      var viewportShifted = (window.scrollY || window.pageYOffset || 0) !== 0 ||
        !!(vv && vv.offsetTop);
      if (!viewportShifted) return;
      _pacViewportResetQueued = true;
      window.requestAnimationFrame(function() {
        _pacViewportResetQueued = false;
        if (!_pacLocked) return;
        var currentVv = window.visualViewport;
        if ((window.scrollY || window.pageYOffset || 0) !== 0 ||
            (currentVv && currentVv.offsetTop)) {
          window.scrollTo(0, 0);
        }
      });
    }
    window.addEventListener('scroll', _pacCancelPendingFocusScroll, { passive: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', _pacCancelPendingFocusScroll, { passive: true });
      window.visualViewport.addEventListener('scroll', _pacCancelPendingFocusScroll, { passive: true });
    }
    function _pacLock() {
      if (_pacLocked) return;
      _pacLocked = true;
      _pacSavedScrollY = window.scrollY || window.pageYOffset || 0;
      // Collapse the follow-along preview first. It is position:sticky, so once
      // the body is frozen it pins to the top of the viewport — at full height
      // it lands squarely on top of the input we are about to scroll to.
      if (window.sm2SetPreviewCollapsed) window.sm2SetPreviewCollapsed(true);
      var w = document.getElementById('sm2-location-wrapper');
      var rect = w ? w.getBoundingClientRect() : null;
      // Re-read after the collapse: the document just got ~50vh shorter, which
      // can clamp scrollY.
      var curY = window.scrollY || window.pageYOffset || 0;
      var targetY = curY;
      if (rect) {
        // Land the input clear of the collapsed panel, and never ask for more
        // scroll than the document has — an unclamped offset on a fixed body
        // isn't corrected by the browser, it just shows past the page end.
        var maxY = Math.max(0, (document.documentElement.scrollHeight || 0) - window.innerHeight);
        targetY = Math.min(maxY, Math.max(0, curY + rect.top - _pacStickyBottom() - 12));
      }
      document.body.style.top = (-targetY) + 'px';
      document.documentElement.classList.add('sm2-pac-locked');
    }
    // Height of the sticky preview panel as it will paint once the body is
    // frozen: pinned at its `top` offset, so it occupies [0, top + height).
    function _pacStickyBottom() {
      var main = document.querySelector('.sm2-main');
      if (!main) return 0;
      var cs = window.getComputedStyle(main);
      if (cs.position !== 'sticky') return 0;
      return main.getBoundingClientRect().height + (parseFloat(cs.top) || 0);
    }
    function _pacUnlock() {
      if (!_pacLocked) return;
      _pacLocked = false;
      document.documentElement.classList.remove('sm2-pac-locked');
      document.body.style.top = '';
      if (window.sm2SetPreviewCollapsed) window.sm2SetPreviewCollapsed(false);
      window.scrollTo(0, _pacSavedScrollY);
      try { pac.blur && pac.blur(); } catch (e) {}
    }
    function _pacOutsideTap(e) {
      if (!_pacLocked) return;
      // Tap inside the wrapper or inside the autocomplete dialog (which
      // appears in the document layer above body) → keep lock.
      var w = document.getElementById('sm2-location-wrapper');
      if (w && w.contains(e.target)) return;
      // Closed-shadow predictions list retargets events to <gmp-place-autocomplete>;
      // composedPath is empty for those, so checking the host suffices.
      if (e.target === pac || (pac.contains && pac.contains(e.target))) return;
      _pacUnlock();
    }
    pac.addEventListener('pointerdown', function() {
      if (_pacUnlockTimer) { clearTimeout(_pacUnlockTimer); _pacUnlockTimer = null; }
    });
    // Defer the lock to focusin so we don't interrupt iOS's
    // tap→focus→keyboard chain by mutating body position mid-tap (doing
    // it on pointerdown swallowed the focus and required a second tap to
    // bring the keyboard up).
    pac.addEventListener('focusin', function() {
      if (!_pacIsMobile()) return;
      _pacLock();
      document.addEventListener('pointerdown', _pacOutsideTap, true);
    });
    pac.addEventListener('focusout', function() {
      if (_pacUnlockTimer) clearTimeout(_pacUnlockTimer);
      _pacUnlockTimer = setTimeout(function() {
        _pacUnlockTimer = null;
        if (document.activeElement !== pac) {
          document.removeEventListener('pointerdown', _pacOutsideTap, true);
          _pacUnlock();
        }
      }, 200);
    });
  }
  window.sm2InitPlaces = bindPlacesAutocomplete;

  // ── Responsive re-layout ─────────────────────────────────────────────────
  // Force the celestial-map to an exact square — CSS percentage-based height
  // leaves fractional-pixel drift that warps the milkyway overlay and shifts
  // it off the star disc. We set width = height = min(rect.w, rect.h) in px.
  function squareUpCelestialMap() {
    // Historically this function pinned #sm2-celestial-map (and, as a follow-up
    // fix, .sm2-disc-border) to fixed pixel dimensions captured from
    // getBoundingClientRect. The goal was to defeat ~1px browser pixel rounding
    // that was said to warp the milkyway overlay. In practice the pinning was
    // the single largest source of misalignment bugs across finish/size
    // transitions: any time the captured rect didn't perfectly match the CSS-
    // computed 90%-of-parent width (mid-transition layout, aspect-ratio not yet
    // reconciled with height:auto, content-box vs border-box quirks during a
    // border-width transition), we pinned the element to a slightly-too-small
    // or slightly-too-large px value. Because the element is anchored at
    // left:5% (not centred), a stale pin shifted the visible disc away from
    // centre — and subsequent renders didn't always re-measure into a better
    // state, so the misalignment persisted through Print↔Framed↔Print cycles.
    //
    // CSS already guarantees a square disc (`aspect-ratio: 1/1 !important` on
    // the celestial-map rule) and horizontal centring (`left:5% + width:90%`).
    // Trusting those rules removes an entire class of bug; the ≤1px subpixel
    // rounding is visually imperceptible. The function is kept as a callable
    // so handleResize / render()'s doSquareAndRender can still chain into
    // renderMilkyWay(), but it no longer mutates any inline styles.
    var map = document.getElementById('sm2-celestial-map');
    if (!map) return null;
    var rect = map.getBoundingClientRect();
    return Math.floor(Math.min(rect.width, rect.height));
  }

  // Only a change in the disc's width means a new texture (rotation, a wider
  // desktop window). iOS fires resize as the toolbars show and hide during a
  // scroll; the width doesn't change, and re-rendering the Milky Way then
  // (WebGL + toDataURL + a data-URL decode) stalled the page ~340ms each time.
  var _lastMilkyWaySide = null;
  function handleResize() {
    if (_isZooming) return;
    var side = squareUpCelestialMap();
    if (side == null) return;
    if (side === _lastMilkyWaySide) return;
    _lastMilkyWaySide = side;
    renderMilkyWay(milkywayConfig.center, true);
  }

  var _resizeTimeout = null;
  window.addEventListener('resize', function() {
    clearTimeout(_resizeTimeout);
    _resizeTimeout = setTimeout(handleResize, 250);
  }, { passive: true });

  // ── Cart / admin integration (Phase C) ───────────────────────────────────
  //
  // Contracts preserved from v1 (myjs.js, starmap-gift-options.js):
  //   - POST  https://admin.skylitstudio.co.uk/api/star-map-data/<?send_email=1>
  //       · full star_maps row (v2 adds design_version=2, composition, theme_key,
  //         font_size, join_location_coords, orientation)
  //   - GET   https://admin.skylitstudio.co.uk/api/star-map-data/?order_no=<id>
  //   - POST  https://admin.skylitstudio.co.uk/api/send-saved/
  //       · { order, email, product_type: "star", thumbnail }
  //   - POST  /cart/add.js { items: [{id: variantId, quantity: 1, properties: {...v1-shape}}, ...gift] }
  //   - POST  /cart/change.js { id: <cart key>, quantity: 0 }  (edit-mode replace)
  //   - POST  /cart/update.js { attributes: { _custom_bundle_active, _custom_bundle_quantity } }
  //
  // Order ID shape: Math.random().toString(36).slice(2) → ~11 lowercase alnum.
  // Admin webhook harvests properties._OrderID from the Shopify orders/paid
  // payload and calls our renderer.

  // Named preset slugs → admin order IDs used as visual templates. Homepage
  // bestselling cards link to /products/starmap?preset=<slug>; the wizard
  // hydrates the template's theme/composition/shape/style/background and then
  // generates a fresh order ID for the customer's new purchase.
  var PRESETS = {
    classic:  'io51p0wjeh9',
    treeline: 'bp9d27bt9dw',
    night:    'bpn6iw7l6gc',
    aurora:   'dkngkkmg25',
    galaxy:   'ffgnhp08j66',
    heart:    'spqfi8bimlc',
    handwritten: 'vtijr8g3hdf',
    written:  'mhc4ne76vkj'
  };

  var Cart = (function() {

    // ── helpers ─────────────────────────────────────────────────────────

    function newOrderId() {
      return Math.random().toString(36).slice(2);
    }

    function theme() { return findTheme(state.themeKey); }

    // "Included on print" cart line: everything the customer switched on,
    // spelled out so the cart row and the order confirmation leave no doubt.
    // The Milky Way leads the list because it is the one people write in
    // about (order #232009: "please confirm the Milky Way is included"); on a
    // theme that offers it, its absence is stated too, so silence never
    // reads as "forgotten".
    function elementsLabel() {
      var parts = [];
      if (theme().hasMilkyWayOption) parts.push(state.showMilkyway ? 'Milky Way' : 'No Milky Way');
      if (state.showConstellations) parts.push('Constellations');
      if (state.showGrid)           parts.push('Grid');
      if (state.showLabels)         parts.push('Star & constellation names');
      if (state.showPlanets)        parts.push('Planets');
      if (state.showBorder)         parts.push('Border');
      if (!state.showConstellations && !state.showGrid && !state.showLabels && !state.showPlanets && !state.showBorder) parts.push('Stars only');
      return parts.join(', ');
    }

    // "Style" cart line: colourway + map shape (+ caption layout when it is
    // not the classic one). Customers pick these on purpose and the
    // confirmation used to say nothing about them.
    function styleLabel() {
      // Customer-facing names for the Shape tiles (the internal value stays
      // "circle" etc. for the renderer). "circle map" read oddly next to the
      // tile the customer actually picked ("Classic"), and Zodiac was
      // missing, so zodiac orders were labelled "circle map".
      var shape = {
        circle: 'classic round',
        azimuth: 'azimuth ring',
        zodiac: 'zodiac ring',
        heart: 'heart-shaped'
      }[state.shape] || 'classic round';
      var s = theme().name + ', ' + shape + ' map';
      var comp = state.composition || 'classic';
      if (comp !== 'classic') s += ', ' + comp + ' caption';
      // Name the background the customer picked (Classic only), using the
      // picker tiles' own names so the confirmation reads back what they chose.
      var bgName = backgroundLabel();
      if (bgName) s += ', ' + bgName + ' background';
      return s;
    }

    function backgroundLabel() {
      var bg = state.background;
      if (!bg || !bg.value) return '';
      if (bg.type === 'image') {
        var names = [['skylit_galaxy', 'Skylit Galaxy'], ['starry_night_sky', 'Starry Night'], ['Noctis-Realm', 'Noctis Realm'], ['NGC604', 'NGC 604'], ['night-you-were-born', 'Cartoon Animals']];
        for (var i = 0; i < names.length; i++) if (bg.value.indexOf(names[i][0]) !== -1) return names[i][1];
        return 'image';
      }
      var colours = { '#000000': 'black', '#ffffff': 'white', '#0a1628': 'navy blue', '#f5e6d1': 'cream', '#a01830': 'deep red' };
      var th = theme();
      // The theme's own ground is implied by the theme name; only a swapped colour is worth saying.
      if (th && th.bg && String(th.bg).toLowerCase() === String(bg.value).toLowerCase()) return '';
      return colours[String(bg.value).toLowerCase()] || '';
    }

    // "Time on print" cart line: the dateline prints the date only when the
    // time toggle is off, which otherwise surfaces as "did you get my time?".
    function timeOnPrintLabel() {
      if (state.showTime && state.time) return 'Shown (' + state.time + ')';
      return 'Not shown (date only)';
    }

    function dateTimeLabel() {
      if (!state.date) return '';
      if (state.showTime && state.time) return state.date + ' ' + state.time;
      return state.date;
    }

    function aspectRatioPercent(sizeStr) {
      var m = (sizeStr || '').match(/(\d+)x(\d+)/);
      if (!m) return '142.86';
      return ((parseFloat(m[2]) / parseFloat(m[1])) * 100).toFixed(2);
    }

    // Resolve a Shopify variant ID from (type, size, frame) against the
    // sm2ProductVariants array (written into the liquid inline script).
    // option1 = type, option2 = size (format '30x40cm'), option3 = frame colour.
    function resolveVariant(sel) {
      var variants = window.sm2ProductVariants || [];
      if (!variants.length) return null;
      var typeWanted = (sel.type || state.selectedType || 'PRINT').toLowerCase();
      var sizeWanted = (sel.size || state.selectedSize || '30x40').toLowerCase();
      var frameWanted = (sel.frame || state.selectedFrame || '').toLowerCase();

      // v1 stores variant option1 as 'Print' / 'Framed' / 'Canvas'. Some older
      // exports use 'Poster' as a synonym for 'Print' — map both.
      function matchType(o1) {
        var t = (o1 || '').toLowerCase();
        if (typeWanted === 'print')  return t === 'print' || t === 'poster';
        if (typeWanted === 'framed') return t === 'framed';
        if (typeWanted === 'canvas') return t === 'canvas';
        return false;
      }
      function matchSize(o2) {
        var t = (o2 || '').toLowerCase().replace(/\s|cm/g, '');
        return t.indexOf(sizeWanted) === 0;
      }
      function matchFrame(o3) {
        if (typeWanted !== 'framed') return true;
        if (!frameWanted) return true;
        return (o3 || '').toLowerCase() === frameWanted;
      }

      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        if (matchType(v.option1) && matchSize(v.option2) && matchFrame(v.option3)) return v;
      }
      // Fallback: first available matching type
      for (var j = 0; j < variants.length; j++) {
        if (matchType(variants[j].option1)) return variants[j];
      }
      return variants[0];
    }

    // Exact (type, size, frame) lookup with no fallback — used by the frame
    // tile availability check so tiles grey out when the combo is not stocked
    // (e.g. Dark Brown only ships as A4).
    // `includeSoldOut` also matches variants that exist but have no stock.
    function hasExactVariant(sel, includeSoldOut) {
      var variants = window.sm2ProductVariants || [];
      var typeWanted = (sel.type  || state.selectedType  || 'PRINT').toLowerCase();
      var sizeWanted = (sel.size  || state.selectedSize  || '30x40').toLowerCase();
      var frameWanted = (sel.frame || state.selectedFrame || '').toLowerCase();
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var t = (v.option1 || '').toLowerCase();
        var s = (v.option2 || '').toLowerCase().replace(/\s|cm/g, '');
        var f = (v.option3 || '').toLowerCase();
        var typeOk  = typeWanted === 'print'  ? (t === 'print' || t === 'poster')
                     : typeWanted === 'framed' ? t === 'framed'
                     : typeWanted === 'canvas' ? t === 'canvas' : false;
        if (typeOk && s.indexOf(sizeWanted) === 0 && f === frameWanted && (includeSoldOut || v.available !== false)) {
          return true;
        }
      }
      return false;
    }

    // ── cart state ──────────────────────────────────────────────────────

    function loadCartState() {
      return fetch(window.Shopify.routes.root + 'cart.js')
        .then(function(r) { return r.json(); })
        .then(function(cart) {
          var ids = [], keys = [];
          var pairedByOrder = {};
          (cart.items || []).forEach(function(x) {
            var oid = x.properties && (x.properties._OrderID || x.properties.OrderID);
            if (oid) { ids.push(oid); keys.push(x.key); }
            var paired = x.properties && x.properties._paired_with_order_id;
            if (paired) pairedByOrder[paired] = x;
          });
          state.cartOrderIds   = ids;
          state.cartOrderKeys  = keys;
          state.cartPairedById = pairedByOrder;
          return cart;
        })
        .catch(function(e) { console.warn('[sm2.cart] load failed', e); return null; });
    }

    // Read the gift-note editor state (text/design/font/enabled) from the
    // gift-note-card snippet DOM. Used both to persist the note on the order
    // record (buildAdminData) and as the canonical read elsewhere.
    function readStarGiftDom() {
      var out = { note: '', design: 'editorial', font: 'handwritten', enabled: false };
      try {
        var ta = document.getElementById('Gift-note-starmap') ||
                 document.getElementById('Gift-note'); // legacy id
        if (ta) out.note = ta.value || '';
        var root = document.querySelector('.gnc[data-gnc="starmap"]');
        if (root) {
          var d = root.getAttribute('data-design');
          if (d === 'editorial' || d === 'minimal') out.design = d;
          var f = root.getAttribute('data-font');
          if (f === 'handwritten' || f === 'classic') out.font = f;
          out.enabled = root.getAttribute('data-enabled') === 'true';
        }
      } catch (_) {}
      return out;
    }

    // Pre-fill the gift-note editor (textarea + design + enabled state) from
    // the paired add-on that's already in cart for this orderId. Called in
    // edit-mode after hydrateFromRow so the customer sees their previous
    // message when they revisit. No-op if no paired add-on or no message.
    function hydrateGiftNoteFromPairedAddon() {
      var paired = state.cartPairedById && state.cartPairedById[state.orderId];
      if (!paired || !paired.properties) return;
      var p = paired.properties;
      var pd = window.SkylitGifts && (window.SkylitGifts.designFor(paired.variant_id) || (p._gift_wrap_theme && window.SkylitGifts.designKey(p._gift_wrap_theme)));
      if (pd) setState({ giftPaper: pd });
      var note = paired.properties._gift_note || '';
      var design = paired.properties._gift_card_design || 'editorial';
      if (design !== 'minimal' && design !== 'editorial') design = 'editorial';
      var font = paired.properties._gift_card_font || 'handwritten';
      if (font !== 'handwritten' && font !== 'classic') font = 'handwritten';
      try {
        var root = document.querySelector('.gnc[data-gnc="starmap"]');
        if (!root) return;
        // Mirror the state across both rendered instances (overlay + editor).
        document.querySelectorAll('.gnc[data-gnc="starmap"]').forEach(function(el) {
          el.setAttribute('data-design', design);
          el.setAttribute('data-font', font);
          el.setAttribute('data-enabled', note ? 'true' : 'false');
        });
        var ta = document.getElementById('Gift-note-starmap') ||
                 document.getElementById('Gift-note');
        if (ta) ta.value = note;
        var cb = document.querySelector('.gnc[data-gnc="starmap"] [data-gnc-enabled]');
        if (cb) cb.checked = !!note;
        // Trigger an input event so the snippet's render() repaints the
        // overlay card with the loaded message at the right font size.
        if (ta) ta.dispatchEvent(new Event('input', { bubbles: true }));
      } catch (e) {
        console.warn('[sm2.cart] paired-addon hydrate failed', e);
      }
    }

    // ── edit / resume hydration ─────────────────────────────────────────

    // Map a star_maps row back into state. Fields are looked up loosely —
    // v1 rows may be missing newer columns; missing fields keep defaults.
    function normalizeLegacyFont(value) {
      var font = String(value || '').trim();
      if ((font.charAt(0) === '"' && font.charAt(font.length - 1) === '"') ||
          (font.charAt(0) === "'" && font.charAt(font.length - 1) === "'")) {
        font = font.slice(1, -1).trim();
      }
      // A tiny number of v1 rows stored the style label rather than the
      // actual Google Fonts family name.
      if (font === 'Cormorant Italic') font = 'Cormorant Infant';
      return font;
    }

    function hydrateFromRow(row) {
      if (!row) return;
      var patch = {};
      // Normalise to the customizer's canonical size keys ('a4', '30x40',
      // '50x70') — the Liquid size tiles emit data-sm2-size lowercased and
      // space-stripped, so a stored 'A4' or '30 x 40 cm' would never match
      // `$store.sm2.selectedSize === '<code>'` and no tile would highlight.
      // (Pricing still resolved correctly because resolveVariant() lowercases,
      // which is why this only ever showed up as a "no size selected" visual.)
      if (row.size)      patch.selectedSize  = String(row.size).toLowerCase().replace(/\s|cm/g, '');
      // Use `!= null` so explicitly-cleared empty strings round-trip as empty.
      // A truthy check would re-apply the default state value on reload, silently
      // restoring text the customer deliberately deleted.
      if (row.title != null)   patch.headline      = row.title;
      if (row.message != null) patch.customMessage = row.message;
      if (row.handwriting_enabled != null) patch.handwritingEnabled = row.handwriting_enabled === 1 || row.handwriting_enabled === '1' || row.handwriting_enabled === true || row.handwriting_enabled === 'true';
      if (row.handwriting_strokes) {
        try {
          var _hw = typeof row.handwriting_strokes === 'string' ? JSON.parse(row.handwriting_strokes) : row.handwriting_strokes;
          patch.handwritingStrokes = (_hw && _hw.strokes) || [];
        } catch (e) { patch.handwritingStrokes = []; }
      }
      if (row.handwriting_size != null) {
        var _hs = parseFloat(row.handwriting_size);
        if (!isNaN(_hs)) patch.handwritingSize = Math.max(0.2, Math.min(1.1, _hs));
      }
      if (row.handwriting_height != null) {
        var _hh = parseFloat(row.handwriting_height);
        if (!isNaN(_hh)) patch.handwritingHeight = Math.max(0.4, Math.min(1.4, _hh));
      }
      // Hydration split: `row.location` is the canonical geocoded string that
      // seeds the search box. `row.address` is the printed display text — if
      // it differs, treat it as the user's override so the printed caption
      // round-trips on resume. Legacy rows only have `row.address` (no
      // separate `row.location`), so fall back to address for the search box
      // too, leaving the override empty.
      if (row.location)  patch.location        = row.location;
      else if (row.address) patch.location     = row.address;
      // A saved override that is present but whitespace-only (the non-breaking
      // space we store for a deliberately-cleared line) round-trips as cleared,
      // not as the auto value.
      if (row.address != null && String(row.address).length > 0 && String(row.address).trim() === '') {
        patch.locationOverride = '';
        patch.locationOverrideCleared = true;
      } else if (row.address && row.location && row.address !== row.location) {
        patch.locationOverride = row.address;
        patch.locationOverrideCleared = false;
      } else {
        patch.locationOverride = '';
        patch.locationOverrideCleared = false;
      }
      if (row.lat != null && row.long != null) patch.coords = { lat: +row.lat, lng: +row.long };
      if (row.coordinates != null && String(row.coordinates).length > 0 && String(row.coordinates).trim() === '') {
        patch.coordsOverride = '';
        patch.coordsOverrideCleared = true;
      } else if (row.coordinates) {
        var autoCoords = (row.lat != null && row.long != null) ? formatCoords(+row.lat, +row.long) : '';
        patch.coordsOverride = (autoCoords && row.coordinates !== autoCoords) ? row.coordinates : '';
        patch.coordsOverrideCleared = false;
      } else {
        patch.coordsOverride = '';
        patch.coordsOverrideCleared = false;
      }
      // Printed date & time override round-trips via its own column so it
      // never collides with the canonical date/time used by the renderer.
      if (row.date_time_printed != null && String(row.date_time_printed).length > 0 && String(row.date_time_printed).trim() === '') {
        // Saved as a deliberately-blank printed date → restore as cleared.
        patch.dateTimeOverride = '';
        dateTimeOverrideDirty = false;
        dateTimeOverrideCleared = true;
      } else if (row.date_time_printed != null && String(row.date_time_printed).trim() !== '') {
        patch.dateTimeOverride = String(row.date_time_printed);
        dateTimeOverrideDirty = true;
        dateTimeOverrideCleared = false;
      } else {
        patch.dateTimeOverride = '';
        dateTimeOverrideDirty = false;
        dateTimeOverrideCleared = false;
      }
      if (row.date)      patch.date          = row.date;
      if (row.time)      patch.time          = row.time;
      if (row.time_visible != null) patch.showTime = !!row.time_visible;
      if (row.shape)     patch.shape         = row.shape;
      if (row.message_font)  patch.font        = normalizeLegacyFont(row.message_font);
      if (row.details_font)  patch.detailsFont = normalizeLegacyFont(row.details_font);
      if (row.border != null) patch.showBorder = row.border === 1 || row.border === '1' || row.border === true || row.border === 'true';
      if (row.email)     patch.email         = row.email;
      if (row.newsletter_opt_in != null) patch.newsletter = !!row.newsletter_opt_in;
      if (row._selected_type || row.selected_type)   patch.selectedType  = (row._selected_type || row.selected_type || 'PRINT').toUpperCase();
      if (row._selected_frame || row.selected_frame) patch.selectedFrame = row._selected_frame || row.selected_frame || '';
      if (row.gift_option)   patch.gift          = row.gift_option;

      // Gift-wrap extras (ribbon colour + gift note) live in localStorage
      // keyed by order id — they're add-on attributes, not star-map render
      // data, so they're not in the `star_maps` row. Merge the ribbon colour
      // into the patch here so the swatch + preview ribbon round-trip; the
      // gift note is applied to the DOM after setState (below). The wrap
      // TYPE still comes from row.gift_option above, but fall back to the
      // saved extras if the row somehow lacks it.
      // state.orderId is always set by the caller (initFromUrl / resume)
      // before hydrateFromRow runs, and is the key saveGiftExtras() used.
      var giftExtras = loadGiftExtras(state.orderId);
      if (giftExtras) {
        if (giftExtras.ribbonColor) patch.ribbonColor = giftExtras.ribbonColor;
        if (giftExtras.giftPaper) patch.giftPaper = giftExtras.giftPaper;
        if (!row.gift_option && giftExtras.gift) patch.gift = giftExtras.gift;
      }
      // v2-only
      if (row.composition)          patch.composition = row.composition;
      if (row.theme_key)            patch.themeKey    = row.theme_key;
      if (row.print_style)          patch.printStyle  = row.print_style;
      if (row.show_milkyway != null) patch.showMilkyway = row.show_milkyway === 1 || row.show_milkyway === '1' || row.show_milkyway === true || row.show_milkyway === 'true';

      // Star-map element toggles are stored server-side only as the joined
      // label (elementsLabel() — "Constellations, Grid, Star & constellation
      // names"). Parse it back into the four booleans so user selections
      // persist on reload. Match item by item: "Star & constellation names"
      // contains "constellation" but means the names, not the lines (older
      // rows say "Labels").
      if (row.star_map_elements != null) {
        var items = String(row.star_map_elements).toLowerCase().split(',').map(function (s) { return s.trim(); });
        var isNames = function (s) { return s.indexOf('name') !== -1 || s.indexOf('label') !== -1; };
        var has = function (test) { return items.some(function (s) { return s.indexOf('no ') !== 0 && test(s); }); };
        patch.showConstellations = has(function (s) { return s.indexOf('constellation') !== -1 && !isNames(s); });
        patch.showGrid           = has(function (s) { return s.indexOf('grid') !== -1; });
        patch.showLabels         = has(isNames);
        patch.showPlanets        = has(function (s) { return s.indexOf('planet') !== -1; });
        // showBorder is already hydrated from row.border above; star_map_elements
        // carries it too ("Border") but row.border is the canonical source.
      }

      // Background: v2 combines the v1 color + image pickers into one state
      // field. Prefer image if bg_image_url is set, else color.
      if (row.bg_image_url) {
        patch.background = { type: 'image', value: row.bg_image_url };
      } else if (row.background_color) {
        patch.background = { type: 'color', value: row.background_color };
      }

      // rotate_coordinates seeds the milky-way projection rotation directly.
      if (row.rotate_coordinates) {
        var rc = String(row.rotate_coordinates).split(',').map(parseFloat);
        if (rc.length >= 3) milkywayConfig.center = rc;
      }
      setState(patch);

      // Restore the gift-note editor (message text, card design, font) from
      // the saved gift-wrap extras. The ribbon colour + wrap type are already
      // in `patch` above, so setState's render cascade (applyGiftUI + the
      // Alpine :class bindings) brings the wrap pills, ribbon swatch and
      // preview ribbon back. The gift note lives in the gift-note-card DOM,
      // so it's applied separately here.
      if (giftExtras) {
        applyGiftNoteFromExtras(giftExtras);
      } else if (row.gift_note != null || row.gift_card_design || row.gift_card_font) {
        // Server-side fallback: the order record now persists the gift note
        // (gift_note/gift_card_design/gift_card_font/gift_note_enabled), so a
        // resume on a different device, a paid re-edit, or a cleared-storage
        // visit still restores the message even with no localStorage extras.
        // The cart-line path (hydrateGiftNoteFromPairedAddon) still overrides
        // this afterwards when the order is in the cart.
        applyGiftNoteFromExtras({
          giftNote: row.gift_note || '',
          giftCardDesign: row.gift_card_design || 'editorial',
          giftCardFont: row.gift_card_font || 'handwritten',
          giftNoteEnabled: row.gift_note_enabled === 1 || row.gift_note_enabled === '1' || row.gift_note_enabled === true
        });
      }

      // After hydration, sync the date/time picker UI to match the restored
      // state. The scroll-wheel pickers read from the hidden .bear-* selects
      // and update their display text via MutationObserver / change events.
      syncPickersToState();
    }

    // Push state.date / state.time back into the picker UI elements so the
    // scroll-wheel pickers and trigger-button display match the hydrated order.
    function syncPickersToState() {
      if (state.date) {
        var parts = state.date.split('-');
        if (parts.length === 3) {
          var y = parseInt(parts[0], 10);
          var m = parseInt(parts[1], 10) - 1; // bear-months is 0-indexed
          var d = parseInt(parts[2], 10);
          var dayEl   = document.querySelector('.bear-dates');
          var monthEl = document.querySelector('.bear-months');
          var yearEl  = document.querySelector('.bear-years');
          if (dayEl)   { dayEl.value   = d; }
          if (monthEl) { monthEl.selectedIndex = m; }
          if (yearEl)  { yearEl.value  = y; }
          // Trigger change so the date-picker's MutationObserver updates
          // the display text and the v2 observer re-reads the values.
          if (window.jQuery) {
            if (yearEl)  window.jQuery(yearEl).trigger('change');
            if (monthEl) window.jQuery(monthEl).trigger('change');
            if (dayEl)   window.jQuery(dayEl).trigger('change');
          } else {
            [yearEl, monthEl, dayEl].forEach(function(el) {
              if (el) el.dispatchEvent(new Event('change'));
            });
          }
          // Also update the trigger button text directly
          var dateDisplay = document.getElementById('datePickerDisplay');
          if (dateDisplay) {
            var months = ['January','February','March','April','May','June',
                          'July','August','September','October','November','December'];
            dateDisplay.textContent = d + ' ' + months[m] + ' ' + y;
          }
        }
      }

      if (state.time) {
        var tp = state.time.split(':');
        if (tp.length === 2) {
          var h24 = parseInt(tp[0], 10);
          var mm  = parseInt(tp[1], 10);
          var meridiem = h24 >= 12 ? 'PM' : 'AM';
          var h12 = h24 % 12 || 12;
          var hSel = document.querySelector('#timePicker .hours-dropdown');
          var mSel = document.querySelector('#timePicker .minutes-dropdown');
          var amRadio = document.getElementById('timePickerMeridianAM');
          var pmRadio = document.getElementById('timePickerMeridianPM');
          if (hSel) hSel.value = h12;
          if (mSel) mSel.value = String(mm).padStart(2, '0');
          if (amRadio) amRadio.checked = (meridiem === 'AM');
          if (pmRadio) pmRadio.checked = (meridiem === 'PM');
          if (window.jQuery) {
            if (hSel) window.jQuery(hSel).trigger('change');
            if (mSel) window.jQuery(mSel).trigger('change');
          }
          var timeDisplay = document.getElementById('timePickerDisplay');
          if (timeDisplay) {
            timeDisplay.textContent = h12 + ':' + String(mm).padStart(2, '0') + ' ' + meridiem;
          }
        }
      }

      // Sync the "Show time on print" checkbox
      var showTimeCbx = document.getElementById('sm2-show-time');
      if (showTimeCbx) showTimeCbx.checked = !!state.showTime;

      // Sync text inputs (headline, message, location override, coords override)
      var hlInput = document.getElementById('sm2-headline-input');
      if (hlInput) hlInput.value = state.headline || '';
      var msgInput = document.getElementById('sm2-message-input');
      if (msgInput) msgInput.value = state.customMessage || '';
      if (typeof window.sm2HandwritingSyncUI === 'function') window.sm2HandwritingSyncUI();
      var locOv = document.getElementById('sm2-location-override');
      if (locOv) locOv.value = state.locationOverride || '';
      var coOv = document.getElementById('sm2-coords-override');
      if (coOv) coOv.value = state.coordsOverride || '';
      var dtOv = document.getElementById('sm2-datetime-override');
      if (dtOv) dtOv.value = state.dateTimeOverride || (dateTimeOverrideCleared ? '' : computedDateTime());
    }

    function fetchOrder(orderId, isPreset) {
      // isPreset appends ?preset=1 so the backend knows this is a "start from
      // template" load and skips the "Returned to draft" push (the row is the
      // template's, not the customer's draft).
      return fetch(PROXY_BASE + 'star-map-data/?order_no=' + encodeURIComponent(orderId) + (isPreset ? '&preset=1' : ''))
        .then(function(r) { return r.status === 200 ? r.json() : null; })
        .catch(function() { return null; });
    }

    // Fire-and-forget log of an "edit personalisations" revisit. Useful for
    // visibility into customers who returned to verify their design but
    // didn't change anything (those wouldn't produce a diff in autosave).
    function logEditPersonalisationsOpened(orderId) {
      try {
        fetch(ADMIN_BASE + 'api/order-event', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            order_id: orderId,
            event: 'edit_personalisations_opened',
            detail: window.location.href.slice(0, 200)
          })
        }).catch(function() {});
      } catch (e) {}
    }

    // A row is treated as "locked" when the order has progressed to paid AND
    // the orderId doesn't carry the DUP- prefix used by the admin-side
    // duplicate route. Locked orders must not be edited from a returning
    // customizer session — that would corrupt the rendered/shipped artwork.
    function isPaidLocked(row, orderId) {
      if (!row) return false;
      if (typeof orderId === 'string' && orderId.indexOf('DUP-') === 0) return false;
      // shopify_order_id is stripped by sanitizeForPublic; funnel_status
      // flips to 'paid' via the orders/paid webhook.
      return row.funnel_status === 'paid';
    }

    function initFromUrl() {
      var params = new URLSearchParams(window.location.search);
      var editId = params.get('order_id');
      var savedId = params.get('saved');
      var presetKey = params.get('preset');

      // Per-product default preset (set on themed product variants like
      // Wedding Star Map, Anniversary Star Map via the
      // customizer.default_preset metafield). URL ?preset= still wins so
      // the homepage bestseller cards override; this only fires when the
      // visitor lands directly on the product page with no preset param.
      if (!presetKey && !editId && !savedId &&
          typeof window.SKYLIT_PRODUCT_DEFAULT_PRESET === 'string' &&
          window.SKYLIT_PRODUCT_DEFAULT_PRESET) {
        presetKey = window.SKYLIT_PRODUCT_DEFAULT_PRESET;
        console.log('[sm2.cart] product default preset=' + presetKey);
      }

      // Google Shopping / automated-discount links arrive with ?variant=<id>
      // pointing at the exact advertised offer (size/finish). Pre-select that
      // variant so the customer lands on the advertised product — otherwise the
      // configurator boots to its 30x40 default and the Google discount code
      // (entitled to the advertised variant) can never apply at checkout.
      var variantParam = params.get('variant');
      if (variantParam && !editId && !savedId && !presetKey) {
        var wantId = String(variantParam);
        var pv = (window.sm2ProductVariants || []).filter(function (v) {
          return String(v.id) === wantId;
        })[0];
        if (pv) {
          var o1 = (pv.option1 || '').toLowerCase();
          var typeSel = o1 === 'framed' ? 'FRAMED' : o1 === 'canvas' ? 'CANVAS' : 'PRINT';
          var sizeSel = (pv.option2 || '').toLowerCase().replace(/\s|cm/g, '');
          var patch = {
            selectedType: typeSel,
            selectedFrame: typeSel === 'FRAMED' ? (pv.option3 || '') : ''
          };
          if (sizeSel) patch.selectedSize = sizeSel;
          console.log('[sm2.cart] url variant=' + wantId + ' → ' + JSON.stringify(patch));
          setState(patch);
        }
      }

      // ?prefill_date=YYYY-MM-DD — arrives from a date search on /search. Land the
      // customizer with that exact date already set (and the sky recalculated).
      var prefillDate = params.get('prefill_date');
      if (prefillDate && !editId && !savedId && /^\d{4}-\d{2}-\d{2}$/.test(prefillDate)) {
        var pp = prefillDate.split('-');
        var pdt = new Date(Date.UTC(+pp[0], +pp[1] - 1, +pp[2]));
        if (pdt.getUTCMonth() === +pp[1] - 1 && pdt.getUTCDate() === +pp[2]) {
          setState({ date: prefillDate });
          syncPickersToState();
          console.log('[sm2.cart] prefill_date=' + prefillDate);
        }
      }

      // ?prefill_lat/lng/place — the cart's cross-sell rail passing the
      // place from the moon map already in the basket (stamped there as
      // hidden _loc_lat/_loc_lng line properties). Exact coords, so no
      // geocoding; the label only dresses the caption.
      var prefillLat = parseFloat(params.get('prefill_lat'));
      var prefillLng = parseFloat(params.get('prefill_lng'));
      if (!editId && !savedId && isFinite(prefillLat) && isFinite(prefillLng) &&
          prefillLat >= -90 && prefillLat <= 90 && prefillLng >= -180 && prefillLng <= 180) {
        var prefillLocPatch = { coords: { lat: prefillLat, lng: prefillLng } };
        var prefillPlace = (params.get('prefill_place') || '').slice(0, 80);
        if (prefillPlace) prefillLocPatch.location = prefillPlace;
        setState(prefillLocPatch);
        syncPickersToState();
        console.log('[sm2.cart] prefill_location=' + prefillLat + ',' + prefillLng + ' ' + prefillPlace);
      }

      if (editId) {
        state.orderId  = editId;
        // cartOrderIds was populated by loadCartState(); decide edit vs resume.
        state.editMode = state.cartOrderIds.indexOf(editId) !== -1;
        console.log('[sm2.cart] url order_id=' + editId + ', editMode=' + state.editMode);
        return fetchOrder(editId).then(function(row) {
          if (isPaidLocked(row, editId)) {
            // Customer returned to a paid order's URL. Refuse to hydrate —
            // any edits would corrupt the rendered/shipped artwork. Drop
            // the param and reload as a fresh session.
            console.log('[sm2.cart] paid order revisited, starting fresh', editId);
            try { localStorage.removeItem('sm2_last_draft'); } catch (e) {}
            params.delete('order_id');
            var qs = params.toString();
            window.location.replace(window.location.pathname + (qs ? '?' + qs : ''));
            return;
          }
          hydrateFromRow(row);
          if (state.editMode) {
            var btn = document.getElementById('sm2-add-to-cart');
            if (btn) btn.textContent = 'Confirm edits';
            // Log the revisit so admin can see customers who returned to
            // verify their design — even if they leave without changes
            // (which would never produce a diff in the autosave pipeline).
            logEditPersonalisationsOpened(editId);
            // Pull existing gift message + design off the paired add-on
            // (new line-item-property model). No-op when there's no paired
            // add-on or no message.
            hydrateGiftNoteFromPairedAddon();
            // A frame bought from the cart's "Complete the look" upsell:
            // surface it as the Framed selection so the customer sees the
            // frame they own. addToCart() keeps the two-line structure —
            // the print re-adds as the Print variant and the frame add-on
            // line is synced separately.
            return adoptPairedFrame();
          }
        });
      }
      if (savedId) {
        state.orderId   = savedId;
        state.savedMode = true;
        console.log('[sm2.cart] url saved=' + savedId);
        return fetchOrder(savedId).then(function(row) {
          if (isPaidLocked(row, savedId)) {
            console.log('[sm2.cart] paid order saved-link revisited, starting fresh', savedId);
            try { localStorage.removeItem('sm2_last_draft'); } catch (e) {}
            params.delete('saved');
            var qs = params.toString();
            window.location.replace(window.location.pathname + (qs ? '?' + qs : ''));
            return;
          }
          hydrateFromRow(row);
        });
      }
      if (presetKey) {
        // Resolve slug → admin order ID (fall back to raw ID if unknown slug).
        var presetOrderId = PRESETS[presetKey.toLowerCase()] || presetKey;
        console.log('[sm2.cart] url preset=' + presetKey + ' → ' + presetOrderId);
        // Preset = explicit "start from this template" intent — abandon any
        // prior plain-URL draft pointer. Do NOT write a pointer to the fresh
        // orderId here: autosave only arms on first interaction, so a
        // bounce would leave a pointer to an orderId that never existed in
        // D1 (and the next plain visit would offer to "resume" nothing).
        // The first successful autosave writes the real pointer instead.
        // The previous D1 row remains; users who want to keep a specific
        // design should use Save for Later.
        try { localStorage.removeItem('sm2_last_draft'); } catch (e) {}
        // Keep the fresh orderId from seedDefaults() so the customer's save
        // creates a new order rather than overwriting the template.
        return fetchOrder(presetOrderId, true).then(function(row) {
          if (!row) return;
          // A preset is a DESIGN template only — never carry its saved gift-wrap
          // add-on (some templates were saved as 'ribbon'/'bundle') into the
          // customer's fresh order. Gift wrapping is a paid per-purchase choice;
          // pre-selecting it (e.g. Ribbon +£2.49) just from opening a preset is
          // wrong. Strip it before hydrate so the wrap defaults to None.
          row.gift_option = 'none';
          row.gift_note = '';
          row.gift_note_enabled = 0;
          // Printed-caption overrides never survive a preset load either.
          // hydrateFromRow treats ANY non-empty date_time_printed as a manual
          // override (dateTimeOverrideDirty), and any address/coordinates that
          // differ from the auto-derived value likewise — so a template saved
          // with one would freeze the printed dateline/place at the template's
          // placeholder while the customer changes the date and location. A
          // preset is a design template; its captions must auto-derive.
          delete row.date_time_printed;
          delete row.coordinates;
          if (row.location) row.address = row.location;
          // Google Shopping / automated-discount links carry ?variant=<id>
          // for the advertised offer AND land on occasion products that have
          // a default preset. The preset supplies the design; the variant
          // must still win on type/size/frame so the advertised variant's
          // price is what renders (Merchant Center's automated-discounts
          // review verifies the displayed sale price). Mutate the row before
          // hydrateFromRow so it all lands in the single hydration pass.
          if (variantParam) {
            var upv = (window.sm2ProductVariants || []).filter(function (v) {
              return String(v.id) === String(variantParam);
            })[0];
            if (upv) {
              var uo1 = (upv.option1 || '').toLowerCase();
              row.selected_type = uo1 === 'framed' ? 'FRAMED' : uo1 === 'canvas' ? 'CANVAS' : 'PRINT';
              row.selected_frame = row.selected_type === 'FRAMED' ? (upv.option3 || '') : '';
              // hydrateFromRow only strips 'cm' from row.size — normalise to
              // the customizer's size keys ('a4', '30x40', '50x70') here.
              if (upv.option2) row.size = String(upv.option2).toLowerCase().replace(/\s|cm/g, '');
              console.log('[sm2.cart] preset + url variant=' + variantParam + ' → type/size/frame override');
            }
          }
          // URL ?size= overrides the template's saved size — bestselling
          // cards pass size=a4 so customers land on the entry-level size
          // rather than whatever (often 50x70) the template was saved at.
          var sizeOverride = params.get('size');
          if (sizeOverride) row.size = sizeOverride;
          hydrateFromRow(row);
          // Keep the template's text, date, time, and location so the print
          // doesn't look empty — the customer overrides them if they want.
          // Only wipe contact details; assign directly so we don't clobber
          // hydrateFromRow's pending scheduleRender (its `prev` snapshot is
          // what triggers the composition-change → re-square celestial map
          // branch in render(); a second setState here would re-debounce
          // with a `prev` that's missing composition and the starfield would
          // render at the wrong size for header/panel/boxed presets).
          state.email = '';
          state.newsletter = false;
        });
      }
      // No URL params — keep the orderId that seedDefaults() generated.
      return Promise.resolve();
    }

    // ── admin payload (POST body for star-map-data) ─────────────────────

    function buildAdminData() {
      var th = theme();
      var rc = (milkywayConfig && milkywayConfig.center) ? milkywayConfig.center.join(',') : '0,0,0';
      // Persist the gift note on the order record so it rehydrates on reopen
      // even when the paired cart line is gone (paid re-edit / save-for-later).
      var giftDom = readStarGiftDom();

      // Match v1 keys so unchanged columns keep working. New v2 columns
      // (design_version/composition/theme_key/font_size/join_location_coords/
      // orientation) are appended and consumed by the extended route.ts.
      return {
        order: state.orderId,
        email: state.email || '',
        newsletter: !!state.newsletter,
        size: state.selectedSize,
        model: 'star-map-v2',
        bg_image_url: (state.background && state.background.type === 'image' && state.background.value) || '',
        rotate_coordinates: rc,
        // `location` stays the canonical geocoded value (used to re-seed the
        // search box on resume). `address` carries the printed display text,
        // which may be an override. Same split for lat/long (raw) vs.
        // `coordinates` (printed).
        location: state.location,
        address: effectiveLocation(),
        date: state.date,
        time: state.time,
        time_visible: !!state.showTime,
        date_time: dateTimeLabel(),
        // Display-only override for the printed date/time line. Empty = use the
        // auto value. Does NOT affect date/time/date_time (star chart calc).
        date_time_printed: dateTimeOverrideCleared ? '\u00A0' : ((state.dateTimeOverride || '').trim() || ''),
        local_year:   state.date ? parseInt(state.date.slice(0, 4), 10) : null,
        local_month:  state.date ? parseInt(state.date.slice(5, 7), 10) - 1 : null,
        local_date:   state.date ? parseInt(state.date.slice(8, 10), 10) : null,
        local_hours:  state.time ? parseInt(state.time.slice(0, 2), 10) : null,
        local_minutes: state.time ? parseInt(state.time.slice(3, 5), 10) : null,
        // tz offset for the selected (location, local date). `Celestial.timezone()`
        // is populated from timezonedb for the chosen location; the fallback uses
        // the browser's tz but applied to the *selected* date (not current date)
        // so DST is correct when the user picks a date in a different season. The
        // renderer additionally re-validates this against the location's IANA
        // zone, so any residual drift gets corrected server-side.
        timezone_offset: (function() {
          if (typeof Celestial !== 'undefined' && typeof Celestial.timezone === 'function') {
            var tz = Celestial.timezone();
            if (tz != null) return tz;
          }
          if (state.date && state.time) {
            var y  = parseInt(state.date.slice(0, 4), 10);
            var mo = parseInt(state.date.slice(5, 7), 10) - 1;
            var d  = parseInt(state.date.slice(8, 10), 10);
            var hh = parseInt(state.time.slice(0, 2), 10);
            var mm = parseInt(state.time.slice(3, 5), 10);
            return -(new Date(y, mo, d, hh, mm)).getTimezoneOffset();
          }
          return -(new Date()).getTimezoneOffset();
        })(),
        // iso_timestamp is the absolute UTC instant. Previously this built
        // `YYYY-MM-DDTHH:MM:00Z` which labels local time as UTC (wrong by the
        // offset). Compute proper UTC using the same offset written above.
        iso_timestamp: (function() {
          if (!state.date || !state.time) return null;
          var y  = parseInt(state.date.slice(0, 4), 10);
          var mo = parseInt(state.date.slice(5, 7), 10) - 1;
          var d  = parseInt(state.date.slice(8, 10), 10);
          var hh = parseInt(state.time.slice(0, 2), 10);
          var mm = parseInt(state.time.slice(3, 5), 10);
          var off;
          if (typeof Celestial !== 'undefined' && typeof Celestial.timezone === 'function') {
            off = Celestial.timezone();
          }
          if (off == null) {
            off = -(new Date(y, mo, d, hh, mm)).getTimezoneOffset();
          }
          var utcMillis = Date.UTC(y, mo, d, hh, mm) - (off * 60000);
          return new Date(utcMillis).toISOString();
        })(),
        title: state.headline,
        message: state.customMessage,
        message_preview: (state.customMessage || '').split('\n').slice(0, 4).join('\n'),
        handwriting_enabled: state.handwritingEnabled ? 1 : 0,
        handwriting_strokes: (state.handwritingStrokes && state.handwritingStrokes.length)
          ? JSON.stringify({ vbw: 1000, vbh: 240, strokes: state.handwritingStrokes }) : null,
        handwriting_size: state.handwritingSize,
        handwriting_height: state.handwritingHeight,
        lat:  state.coords && state.coords.lat  != null ? state.coords.lat  : null,
        long: state.coords && state.coords.lng != null ? state.coords.lng : null,
        coordinates: effectiveCoords(),
        star_map_elements: elementsLabel(),
        shape: state.shape,
        background_color: (state.background && state.background.type === 'color') ? state.background.value : (th ? th.bg : '#000000'),
        text_color: th ? th.fg : '#ffffff',
        star_map_color: th ? th.stars : '#ffffff',
        border: state.showBorder ? 'true' : '',
        message_font: state.font,
        details_font: state.detailsFont,
        _selected_type:  state.selectedType,
        _selected_frame: state.selectedFrame,
        gift_option: state.gift && state.gift !== 'none' ? state.gift : null,
        gift_note: giftDom.note || '',
        gift_card_design: giftDom.design || 'editorial',
        gift_card_font: giftDom.font || 'handwritten',
        gift_note_enabled: giftDom.enabled ? 1 : 0,

        // ── v2-only fields (wired by migration 0004_star_map_v2_fields.sql) ──
        // print_style intentionally omitted: composition replaced photographer/
        // explorer, so the column is frozen — do not write to it.
        design_version: 2,
        composition: state.composition,
        theme_key: state.themeKey,
        orientation: 'portrait',
        show_milkyway: !!state.showMilkyway,

        // Device / browser context so admin can segment orders by the device
        // the design was authored on. Server parses this into
        // device_type / _os / _browser columns; raw UA + viewport are kept
        // so the category scheme can be re-derived later. Server only stores
        // this on first save for a given order — resumes on a different
        // device don't overwrite the acquisition signal.
        device: captureDeviceInfo()
      };
    }

    function captureDeviceInfo() {
      // Prefer navigator.userAgentData (Client Hints) when available — it
      // exposes `.mobile` and `.platform` without UA-sniffing — but always
      // include the raw UA too so the server can fall back on iOS / older
      // browsers where Client Hints aren't supported.
      var uad = null;
      try {
        var n = navigator;
        if (n && n.userAgentData) {
          uad = {
            brands: n.userAgentData.brands || null,
            mobile: !!n.userAgentData.mobile,
            platform: n.userAgentData.platform || ''
          };
        }
      } catch (e) { /* noop */ }
      var info = {
        userAgent: (navigator && navigator.userAgent) || '',
        userAgentData: uad,
        viewport: { w: window.innerWidth, h: window.innerHeight },
        screen: {
          w: (window.screen && window.screen.width) || null,
          h: (window.screen && window.screen.height) || null,
          dpr: window.devicePixelRatio || 1
        },
        language: (navigator && navigator.language) || '',
        touch: (navigator && navigator.maxTouchPoints ? navigator.maxTouchPoints : 0) > 0
      };
      try {
        info.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      } catch (e) { info.timezone = ''; }
      return info;
    }

    function postAdmin(sendEmail, funnelStatus, extra) {
      var params = sendEmail ? '?send_email=1' : '';
      var payload = buildAdminData();
      if (extra) { for (var k in extra) { if (extra[k] != null) payload[k] = extra[k]; } }
      // Default 'draft' for autosaves; addToCart explicitly passes 'cart'.
      payload.funnel_status = funnelStatus || 'draft';
      var body = JSON.stringify(payload);
      var opts = { method: 'POST', headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }, body: body };
      return fetch(PROXY_BASE + 'star-map-data/' + params, opts)
        .then(function(r) { if (!r.ok) throw new Error(r.status); return r.json(); })
        .catch(function() {
          // Retry once on failure (CF Worker cold-start / transient error)
          return fetch(PROXY_BASE + 'star-map-data/' + params, opts)
            .then(function(r) { return r.json(); })
            .catch(function(e) { console.warn('[sm2] postAdmin failed after retry', e); return {}; });
        });
    }

    // ── cart payload ────────────────────────────────────────────────────

    // Canonical size token for the `_selected_size` cart property ('A4',
    // '30x40') — the shape the admin webhook stores in D1 and the renderers
    // key their templates on. Strips any display noise (legacy 'Marimea '
    // prefix, 'cm' suffix, inner spaces) and uppercases A-sizes.
    function canonSizeToken(raw) {
      var s = String(raw || '').replace(/^Marimea\s+/i, '').replace(/cm/gi, '').replace(/\s+/g, '');
      return /^a\d+$/i.test(s) ? s.toUpperCase() : s;
    }

    // Auto vs printed caption values.
    //
    // The cart line and the Shopify order carry the SEARCHED place and the
    // COMPUTED dateline, and gain a matching "Printed …" property only when
    // the customer typed something different into a printed-caption override.
    // Showing both is what makes the disagreement visible — to the customer on
    // the cart page before they pay, and to whoever packs the order afterwards
    // (the mismatch skylit-admin's app/lib/printed-date-check.ts exists to
    // catch). The admin order record POSTed by buildAdminData() still stores
    // the printed value, because that is what the renderer draws.
    var PRINTED_BLANK = '\u00A0';
    function printedCaptionProps() {
      var out = {};
      // autoKey null = buildCartItem always writes the auto value itself, so
      // only the printed half is conditional.
      function pair(autoKey, printedKey, auto, printed) {
        auto = (auto || '').trim();
        if (printed === PRINTED_BLANK) {
          // Deliberately cleared: the print shows an empty line. Say so in
          // words rather than shipping an invisible whitespace property.
          if (autoKey) out[autoKey] = auto;
          out[printedKey] = '(blank line)';
          return;
        }
        printed = (printed || '').trim();
        if (!printed || printed === auto) return;
        if (autoKey) out[autoKey] = auto;
        out[printedKey] = printed;
      }
      pair(null, 'Printed location', state.location, effectiveLocation());
      pair(null, 'Printed date', computedDateTime(), effectiveDateTime());
      // Coordinates aren't a cart line at all unless they were overridden —
      // an unedited coords line tells nobody anything the location doesn't.
      pair('Coordinates', 'Printed coordinates', state.coordsDisplay, effectiveCoords());
      return out;
    }

    // v1 builds an `_aspect_ratio` string like "142.86" that the cart template
    // renders the thumbnail placeholder against.
    function buildCartItem(variantId, thumbnail) {
      var props = {
          _OrderID:        state.orderId,
          // Copy reference tying this print to its own gift add-on. _OrderID is
          // shared between copies of one design, so add-ons pair on this.
          _cart_line_ref:  state.cartLineRef,
          // Display-facing: "13th April 2026 - 11:11 PM". These are the AUTO
          // values — the dateline computed from the pickers and the place the
          // customer searched. When a printed-caption override makes the print
          // say something else, printedCaptionProps() adds a "Printed …" twin
          // alongside rather than replacing these. dateTimeLabel() is kept for
          // the admin row where the canonical ISO-ish string is stored for the
          // render pipeline.
          'Date & time':   computedDateTime(),
          Location:        state.location || '',
          // Display-only summary for the cart row + admin order. Strip blank /
          // whitespace-only lines so a leading empty line (customer pressed
          // Enter before typing) doesn't render a misleading " / " slash. The
          // raw multi-line message is preserved on the admin order (see above).
          // A handwritten message replaces the typed one on the print, so the
          // cart row must not echo the (often default) typed text.
          Message:         smHwHasContent()
                             ? 'Handwritten (as drawn in your preview)'
                             : (state.customMessage || '').split('\n').map(function (l) { return l.trim(); }).filter(Boolean).slice(0, 4).join('\n'),
          Title:           state.headline,
          // Coordinates always print on a star map; say so with the value
          // rather than leaving the customer to wonder.
          Coordinates:     state.coordsDisplay || '',
          'Time on print': timeOnPrintLabel(),
          Style:           styleLabel(),
          'Included on print': elementsLabel(),
          _print_url:      ADMIN_BASE + 'api/download/' + state.orderId,
          // Stale-session fallback: cart liquid polls this URL and upgrades
          // the line item's <img> when it becomes available. Backed by R2
          // (cart-thumbs/<orderId>.jpg) populated by the upload helper.
          _thumbnail_final: ADMIN_BASE + 'api/cart-thumbnail-img/' + state.orderId,
          // The BARE print (no room, no frame). The cart composes the scene
          // live in CSS around this, which is what lets a frame bought from
          // the cart's own upsell appear or disappear instantly instead of
          // needing the thumbnail re-rendered. Falls back to
          // _thumbnail_final's baked composite when this 404s.
          _thumbnail_print: ADMIN_BASE + 'api/cart-print-img/' + state.orderId,
          _aspect_ratio:   aspectRatioPercent(state.selectedSize),
          _selected_type:  state.selectedType,
          _selected_size:  canonSizeToken(state.selectedSize),
          _selected_frame: state.selectedFrame,
          _print_style:    state.printStyle,
          // Hidden coords so the cart's cross-sell rail can prefill the moon
          // customizer with the same place, no geocoding needed.
          _loc_lat:        state.coords && state.coords.lat != null ? String(state.coords.lat) : '',
          _loc_lng:        state.coords && state.coords.lng != null ? String(state.coords.lng) : ''
      };
      var printed = printedCaptionProps();
      for (var k in printed) {
        if (Object.prototype.hasOwnProperty.call(printed, k)) props[k] = printed[k];
      }
      if (window.SkylitGalleryWall) window.SkylitGalleryWall.applyProperties(props);
      return { id: variantId, quantity: 1, properties: props };
    }

    function giftVariantId() {
      if (state.gift === 'none') return null;
      if (!GIFT_IDS[state.gift]) return null;
      // Gift wrap and the bundle are size-tiered by the print being wrapped;
      // SkylitGifts maps the selected size to the right variant. GIFT_IDS
      // stays as the fallback (the old ids are the smallest tier).
      try {
        var tiered = window.SkylitGifts && window.SkylitGifts.variantFor(state.gift, state.selectedSize, state.giftPaper);
        if (tiered) return String(tiered);
      } catch (e) {}
      return GIFT_IDS[state.gift];
    }

    // ── add to cart ─────────────────────────────────────────────────────

    function _post(url, body) {
      return fetch(window.Shopify.routes.root + url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      }).then(function(r) {
        // Shopify returns HTML (not JSON) for rate-limit / bot-challenge
        // responses, so r.json() throws SyntaxError and callers can't tell
        // throttling apart from a genuine cart error. Catch non-OK statuses
        // explicitly and tag them on a thrown Error so the click handler can
        // show a specific message.
        if (!r.ok) {
          var err = new Error('cart endpoint returned ' + r.status);
          err.httpStatus = r.status;
          throw err;
        }
        return r.json();
      });
    }

    /**
     * Edit mode only: if this design's frame came from the cart upsell (a
     * separate add-on line), hydrate the customizer to show FRAMED + that
     * colour, and remember it so addToCart() preserves the add-on structure
     * instead of switching the print to the unified Framed variant.
     */
    function adoptPairedFrame() {
      if (!window.SkylitPairing || !window.SkylitPairing.pairedFrame) return;
      return window.SkylitPairing.pairedFrame(state.orderId).then(function (f) {
        if (!f || state.selectedType !== 'PRINT') return;
        state.frameFromAddon = true;
        setState({ selectedType: 'FRAMED', selectedFrame: f.colour });
        refreshFinishTiles();
      });
    }

    function addToCart() {
      // Frame adopted from the cart add-on: the print goes back as the
      // Print variant and the frame stays its own add-on line, synced (or
      // removed, if the customer deselected it) after the add below.
      var addonFrameOwned = !!state.frameFromAddon;
      var addonFrameColour = null;
      if (addonFrameOwned && state.selectedType === 'FRAMED') {
        addonFrameColour = state.selectedFrame || 'Black';
        setState({ selectedType: 'PRINT', selectedFrame: '' });
        refreshFinishTiles();
      }
      var variant = resolveVariant({});
      if (!variant) {
        alert('No matching product variant. Please pick a different size/frame combination.');
        return Promise.reject(new Error('no variant'));
      }
      state.variantId = variant.id;
      if (variant.available === false && !(window.SkylitClaim && window.SkylitClaim.active)) {
        var soldErr = new Error('Sorry, this size and finish is sold out. Please choose another.');
        soldErr.soldOut = true;
        return Promise.reject(soldErr);
      }

      // Claim mode: this design was already paid for through another channel
      // (Shop app / TikTok / an off-site order) — there is no cart to add
      // to. Gated on `active`, never `claim`/`usable` alone: a customer must
      // never fall through to the normal /cart/add.js chain below while
      // ?claim= is present, whether the claim turns out pending, already
      // used, expired, or the lookup itself failed. In practice the CTA
      // stays disabled+relabelled by design-claim.js/the skylit:claim-ready
      // handler below whenever !usable, so this inner check is a defensive
      // backstop against a re-enabled button, not the primary guard.
      if (window.SkylitClaim && window.SkylitClaim.active) {
        if (!(window.SkylitClaim.usable && window.SkylitClaim.claim)) {
          var claimErr = new Error(window.SkylitClaim.message ||
            'This design link cannot be used right now. Please contact hello@skylitstudio.co.uk.');
          claimErr.claimBlocked = true;
          return Promise.reject(claimErr);
        }
        // Save the finished design (star_maps row) exactly as the normal
        // path does below, then attach it to the paid Shopify order via the
        // claim endpoint instead of /cart/add.js.
        return postAdmin(false, 'cart').then(function () {
          return window.SkylitClaim.complete(state.orderId);
        });
      }

      // 1. Push the full star_maps row to admin so the webhook has data to render.
      //    2. Build the Shopify cart payload; edit mode removes the old line first.
      return postAdmin(false, 'cart').then(function() {
        // The cart thumbnail is rendered server-side, triggered INSIDE the
        // postAdmin save above (admin app/api/star-map-data → the shared
        // cart-thumbnail trigger). It used to be a separate fire-and-forget
        // POST from here, but that request raced this page's own navigation
        // to /cart and was sometimes dropped before it left the device even
        // with keepalive set — orders t7t9wf9otsb, trdux9d1lq and zf6ck7d1yu
        // lost their cart preview that way, with nothing logged anywhere. The
        // save is awaited, so it cannot be lost. The cart page still polls
        // /api/cart-thumbnail-img and shows a loader until the render lands.
        return '';
      }).then(function(thumb) {

        // ── New gift-addon model (Step 3 of the inline refactor) ──────────
        // Read the customer's gift-message state from the local DOM (the
        // gift-note-card snippet is in `local_only` mode on the customizer
        // and never writes to cart attributes anymore — we ship it as
        // line-item properties on the paired add-on instead).
        function legacyGiftMode() {
          try {
            var u = new URLSearchParams(window.location.search);
            if (u.get('legacy_gift') === '1') return true;
          } catch (_) {}
          return !!window.SKYLIT_LEGACY_GIFT;
        }
        function readGiftDom() {
          var out = { note: '', design: 'editorial', font: 'handwritten', enabled: false };
          try {
            var ta = document.getElementById('Gift-note-starmap') ||
                     document.getElementById('Gift-note'); // legacy id
            if (ta) out.note = ta.value || '';
            var root = document.querySelector('.gnc[data-gnc="starmap"]');
            if (root) {
              var d = root.getAttribute('data-design');
              if (d === 'editorial' || d === 'minimal') out.design = d;
              var f = root.getAttribute('data-font');
              if (f === 'handwritten' || f === 'classic') out.font = f;
              out.enabled = root.getAttribute('data-enabled') === 'true';
            }
          } catch (_) {}
          return out;
        }

        var giftDom = readGiftDom();
        var useLegacy = legacyGiftMode();

        // One fresh copy reference per add-to-cart, shared by the print and
        // its paired add-on so the cart/drawer controls bind to this exact copy.
        state.cartLineRef = window.SkylitPairing
          ? window.SkylitPairing.makeLineRef()
          : state.orderId;

        var items = [ buildCartItem(variant.id, thumb) ];
        var gift = giftVariantId();
        // Initialised up front so the legacy cart-attribute write below is
        // never undefined if the gift-variant lookup fails to resolve.
        var paperKey = 'regular';
        if (gift) {
          var giftProps = {};
          if (state.gift === 'ribbon' || state.gift === 'bundle') {
            var ribbonColor = state.ribbonColor || 'Red';
            giftProps['Ribbon Colour'] = ribbonColor;
            giftProps._ribbon_color = ribbonColor;
          }
          if (state.gift === 'giftwrap' || state.gift === 'bundle') {
            paperKey = window.SkylitGifts ? window.SkylitGifts.designKey(state.giftPaper) : 'regular';
            giftProps['Gift Wrap Theme'] = window.SkylitGifts ? window.SkylitGifts.labelFor(paperKey) : 'Regular';
            giftProps._gift_wrap_theme = paperKey;
          }
          giftProps._paired_with_order_id = state.orderId;
          giftProps._paired_with_line_ref = state.cartLineRef;

          // Attach gift message + design as line-item properties on the
          // paired add-on. Only when ribbon/bundle (wrap-only doesn't carry
          // a card) and the customer has actually opted in by typing.
          if (!useLegacy &&
              (state.gift === 'ribbon' || state.gift === 'bundle') &&
              giftDom.enabled && giftDom.note.trim()) {
            giftProps._gift_note = giftDom.note;
            giftProps._gift_card_design = giftDom.design;
            giftProps._gift_card_font = giftDom.font;
          }

          items.push({ id: gift, quantity: 1, properties: giftProps });
        }

        // Cart attributes still carry the bundle bookkeeping flags so the
        // cart-footer promo banner + custom-bundle SKU continue to behave.
        // The gift-specific cart attributes are gone — they live on the
        // paired add-on's properties now (or via legacy fallback below).
        var attrs = {};
        if (state.gift === 'bundle') {
          attrs._custom_bundle_active = 'true';
          attrs._custom_bundle_quantity = '1';
        } else {
          attrs._custom_bundle_active = 'false';
        }
        if (useLegacy) {
          // Legacy escape hatch — restore the pre-refactor cart-attribute
          // writes verbatim so a rollback flag (?legacy_gift=1) recovers
          // the old behaviour end-to-end.
          var legacyRibbonColor = (state.ribbonColor || 'Red').toLowerCase();
          if (state.gift === 'bundle') {
            attrs._addon_ribbon_color = legacyRibbonColor;
            attrs._addon_gift_wrap_theme = paperKey;
            attrs._addon_gift_note = giftDom.note;
          } else if (state.gift === 'ribbon') {
            attrs._addon_ribbon_color = legacyRibbonColor;
            attrs._addon_gift_note = giftDom.note;
          } else if (state.gift === 'giftwrap') {
            attrs._addon_gift_wrap_theme = paperKey;
          }
        }

        var addReq = function() { return _post('cart/add.js', { items: items }); };
        var attrReq = function() {
          return Object.keys(attrs).length
            ? _post('cart/update.js', { attributes: attrs })
            : Promise.resolve();
        };

        // Drop anything this design already owns in the cart (print + paired
        // ribbon/wrap/bundle/wall strips) before adding. Always driven by a
        // LIVE /cart.js read: state.editMode comes from a page-load snapshot,
        // and a page restored from the back-forward cache still believes the
        // cart is empty — skipping this is what produced duplicate paid gift
        // lines. The purge also raises __skylitCartEditInFlight so
        // cart-attrs-sync doesn't reap the mid-flight "orphan".
        if (window.SkylitPairing) {
          return window.SkylitPairing.purgeDesign(state.orderId)
            .then(addReq)
            .then(function(resp) { return attrReq().then(function() { return resp; }); });
        }
        if (state.editMode) {
          // Helper missing (asset blocked) — old snapshot path.
          var idx = state.cartOrderIds.indexOf(state.orderId);
          var key = state.cartOrderKeys[idx];
          window.__skylitCartEditInFlight = true;
          return _post('cart/change.js', { id: key, quantity: 0 })
            .then(addReq)
            .then(function(resp) { return attrReq().then(function() { return resp; }); });
        }
        return addReq().then(function(resp) { return attrReq().then(function() { return resp; }); });
      }).then(function(resp) {
        if (resp && resp.status >= 400) {
          if (_cartFlight) _cartFlight.cancel();
          alert(resp.message || 'Unable to add to cart.');
          // Hand the button back (it used to stay disabled on "Adding…").
          var atcBtn = document.getElementById('sm2-add-to-cart');
          atcMorph.reset(false);
          if (atcBtn) {
            atcBtn.disabled = false;
            if (!atcBtn.classList.contains('skylit-atc-morphing') && /…$/.test(atcBtn.textContent)) {
              atcBtn.textContent = state.editMode ? 'Confirm edits' : 'Add to cart';
            }
          }
          return;
        }
        // Pair with the edit_personalisations_opened event so the activity
        // log shows whether a customer who entered edit mode actually
        // confirmed the changes (vs. just opened and abandoned the tab).
        if (state.editMode) {
          try {
            fetch(ADMIN_BASE + 'api/order-event', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                order_id: state.orderId,
                event: 'edit_personalisations_confirmed',
                detail: window.location.href.slice(0, 200)
              })
            }).catch(function() {});
          } catch (e) {}
        }
        // Persist the gift-wrap extras keyed to this order so a later
        // edit-mode resume restores the ribbon colour and gift note even
        // before the paired add-on has been re-read from cart.
        saveGiftExtras();
        if (addonFrameOwned && window.SkylitPairing && window.SkylitPairing.syncFrameAddon) {
          return window.SkylitPairing.syncFrameAddon(state.orderId, addonFrameColour).then(function () {
            goToCart();
          });
        }
        goToCart();
      });
    }

    // ── save for later ──────────────────────────────────────────────────

    function saveForLater(email, newsletter) {
      if (!email) return Promise.reject(new Error('email required'));
      state.email = email;
      state.newsletter = !!newsletter;

      // Invisible bot check (assets/skylit-turnstile.js): one single-use
      // token for the newsletter signup, another for the email. Resolves to
      // null on any failure, so the save never waits on it for long.
      var botToken = function() { return window.SkylitTurnstile ? window.SkylitTurnstile.token() : Promise.resolve(null); };
      return (state.newsletter ? botToken() : Promise.resolve(null)).then(function(t1) {
        return postAdmin(true, undefined, { turnstile_token: t1 });
      }).then(function() {
        // Persist the gift-wrap extras so a later resume of this saved
        // order restores the ribbon colour + gift note.
        saveGiftExtras();
        return _generateThumbnail().catch(function() { return ''; });
      }).then(function(thumb) {
        try { if (thumb) localStorage.setItem('thumbnail_' + state.orderId, thumb); } catch (e) {}
        return botToken().then(function(t2) {
          return fetch(ADMIN_BASE + 'api/send-saved/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              order: state.orderId,
              email: email,
              product_type: 'star',
              thumbnail: thumb || '',
              turnstile_token: t2 || undefined
            })
          });
        }).then(function(r) { return r.json().then(function(j) { return { status: r.status, body: j }; }); });
      });
    }

    // ── thumbnail (canvas composite of the live frame) ──────────────────
    //
    // Simpler than v1's _generateCanvasThumbnail — the v2 frame already puts
    // the celestial canvas + milkyway on top of the themed background in CSS,
    // so rasterizing just the #sm2-frame via html2canvas-lite (scroll of
    // child canvases) gives us what we need. If the celestial preview image
    // is present (Celestial converts its canvas to an IMG after render to
    // make CSS masks work) we include that too.

    // ── Mockup compositing ──────────────────────────────────────────────
    // Takes a raw print thumbnail canvas and composites it into a lifestyle
    // mockup photo (framed print on wall). The overlay PNG has the frame
    // interior transparent so the thumbnail shows through.
    var _mockupOverlay = null;     // cached Image
    var _mockupLoading = null;     // in-flight promise
    var MOCKUP_URL = (function() {
      var el = document.querySelector('[data-mockup-overlay]');
      return el ? el.getAttribute('data-mockup-overlay') : '';
    })();
    // Frame area coordinates as fractions of the mockup image
    var MF = { left: 0.341, top: 0.216, right: 0.6425, bottom: 0.79 };

    function _loadMockupOverlay() {
      if (_mockupOverlay) return Promise.resolve(_mockupOverlay);
      if (_mockupLoading) return _mockupLoading;
      if (!MOCKUP_URL) return Promise.resolve(null);
      _mockupLoading = new Promise(function(res) {
        var img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = function() { _mockupOverlay = img; res(img); };
        img.onerror = function() { res(null); };
        img.src = MOCKUP_URL;
      });
      return _mockupLoading;
    }

    function _compositeIntoMockup(printCanvas, opts) {
      opts = opts || {};
      return _loadMockupOverlay().then(function(overlay) {
        if (!overlay) {
          try { return printCanvas.toDataURL('image/jpeg', 0.85); }
          catch (e) { return ''; }
        }
        var mw = 800, mh = 600;
        var mc = document.createElement('canvas');
        mc.width = mw; mc.height = mh;
        var mx = mc.getContext('2d');

        // Frame area in px
        var fx = Math.round(MF.left * mw);
        var fy = Math.round(MF.top * mh);
        var fw = Math.round((MF.right - MF.left) * mw);
        var fh = Math.round((MF.bottom - MF.top) * mh);

        // Draw the print thumbnail scaled to fill the frame area (cover fit)
        var srcAr = printCanvas.width / printCanvas.height;
        var dstAr = fw / fh;
        var dx, dy, dw, dh;
        if (srcAr > dstAr) {
          // thumbnail is wider — fit height, crop sides
          dh = fh; dw = fh * srcAr;
          dx = fx + (fw - dw) / 2; dy = fy;
        } else {
          // thumbnail is taller — fit width, crop top/bottom
          dw = fw; dh = fw / srcAr;
          dx = fx; dy = fy + (fh - dh) / 2;
        }

        // Clip to frame area so thumbnail doesn't bleed
        mx.save();
        mx.beginPath();
        mx.rect(fx, fy, fw, fh);
        mx.clip();
        try { mx.drawImage(printCanvas, dx, dy, dw, dh); } catch (e) {}

        // Inset border drawn INSIDE the frame cutout (so it never gets
        // clipped by the cover-fit aspect mismatch between print and frame).
        if (opts.border) {
          var insetPx = Math.max(3, Math.round(Math.min(fw, fh) * 0.035));
          var bwPx = Math.max(1, Math.round(Math.min(fw, fh) * 0.006));
          mx.strokeStyle = opts.borderColor || '#1a1a1a';
          mx.lineWidth = bwPx;
          mx.strokeRect(
            fx + insetPx + bwPx / 2,
            fy + insetPx + bwPx / 2,
            fw - insetPx * 2 - bwPx,
            fh - insetPx * 2 - bwPx
          );
        }
        mx.restore();

        // Draw mockup overlay (frame, wall, decor) on top
        try { mx.drawImage(overlay, 0, 0, mw, mh); } catch (e) {}

        try { return mc.toDataURL('image/jpeg', 0.85); }
        catch (e) { return ''; }
      });
    }

    function _generateThumbnail() {
      return new Promise(function(resolve) {
        try {
          var frame = document.getElementById('sm2-frame');
          if (!frame) {
            if (window.skylitBeaconThumbnailSkip) {
              try { window.skylitBeaconThumbnailSkip(state.orderId, 'star', 'no_custom_frame', null); } catch (e) {}
            }
            return resolve('');
          }
          // .sm2-frame uses box-sizing: content-box and Framed finishes add a
          // 25px border-width that wraps OUTSIDE the print area. Children
          // positioned with bottom:0 / left:0 etc. anchor to the padding-box
          // (inside the border), so capturing the full bounding rect leaves
          // a border-width strip of empty print background around all four
          // edges of the cart thumbnail — most visible on the panel
          // composition where the white panel ends up floating above the
          // mockup-frame's bottom edge. Strip the border to capture only
          // the actual print content.
          var fs = getComputedStyle(frame);
          var bL = parseFloat(fs.borderLeftWidth)   || 0;
          var bR = parseFloat(fs.borderRightWidth)  || 0;
          var bT = parseFloat(fs.borderTopWidth)    || 0;
          var bB = parseFloat(fs.borderBottomWidth) || 0;
          var fullRect = frame.getBoundingClientRect();
          var rect = {
            left:   fullRect.left   + bL,
            top:    fullRect.top    + bT,
            right:  fullRect.right  - bR,
            bottom: fullRect.bottom - bB,
            width:  fullRect.width  - bL - bR,
            height: fullRect.height - bT - bB,
          };
          var w = 600;
          var h = Math.round(w * (rect.height / Math.max(rect.width, 1)));
          var sx = w / rect.width, sy = h / rect.height;
          var canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h;
          var ctx = canvas.getContext('2d');

          function project(r) {
            return {
              x: (r.left - rect.left) * sx,
              y: (r.top  - rect.top)  * sy,
              w: r.width  * sx,
              h: r.height * sy
            };
          }
          function loadImg(src) {
            return new Promise(function(res) {
              if (!src) return res(null);
              var i = new Image();
              i.crossOrigin = 'anonymous';
              i.onload = function() { res(i); };
              i.onerror = function() { res(null); };
              i.src = src;
            });
          }

          var th = theme();
          var bg = state.background || { type: 'color', value: th ? th.bg : '#000' };
          var isClassic = state.printStyle === 'classic';

          var azEl = document.getElementById('sm2-azimuth-ring');
          var zodiacEl = document.getElementById('sm2-zodiac-ring');
          var heartEl = document.getElementById('sm2-heart-border');
          var azVis = azEl && getComputedStyle(azEl).display !== 'none';
          var zodiacVis = zodiacEl && getComputedStyle(zodiacEl).display !== 'none';
          var heartVis = heartEl && getComputedStyle(heartEl).display !== 'none';

          Promise.all([
            (isClassic && bg.type === 'image' && bg.value) ? loadImg(bg.value) : Promise.resolve(null),
            azVis ? loadImg(azEl.src) : Promise.resolve(null),
            heartVis ? loadImg(heartEl.src) : Promise.resolve(null),
            zodiacVis ? loadImg(zodiacEl.src) : Promise.resolve(null)
          ]).then(function(loaded) {
            var bgImg = loaded[0], azImg = loaded[1], heartImg = loaded[2], zodiacImg = loaded[3];

            // ── 1. Background ───────────────────────────────────────────
            var panelFit = frame._sm2PanelFit;
            if (isClassic && bg.type === 'image' && bgImg && panelFit) {
              // Panel + Cartoon: same layout as the preview (base dropped under
              // the panel on paper colour, then each animal group).
              var fr0 = frame.getBoundingClientRect();
              var P = function (x, y, ww, hh) {
                return project({ left: fr0.left + frame.clientLeft + x, top: fr0.top + frame.clientTop + y, width: ww, height: hh });
              };
              ctx.fillStyle = CARTOON_PAPER;
              ctx.fillRect(0, 0, w, h);
              var nb = P(panelFit.base.x, panelFit.base.y, panelFit.base.w, panelFit.base.h);
              try { ctx.drawImage(bgImg, nb.x, nb.y, nb.w, nb.h); } catch (e) {}
              panelFit.groups.forEach(function (pg) {
                var nw = bgImg.naturalWidth, nh = bgImg.naturalHeight, d = P(pg.x, pg.y, pg.w, pg.h);
                try {
                  ctx.drawImage(bgImg, pg.g.x0 * nw, pg.g.y0 * nh, (pg.g.x1 - pg.g.x0) * nw, (pg.g.y1 - pg.g.y0) * nh, d.x, d.y, d.w, d.h);
                } catch (e) {}
              });
            } else if (isClassic && bg.type === 'image' && bgImg) {
              var ar = bgImg.naturalWidth / bgImg.naturalHeight;
              var canvasAr = w / h;
              var dw, dh, dx, dy;
              if (ar > canvasAr) { dh = h; dw = h * ar; dx = (w - dw) / 2; dy = 0; }
              else               { dw = w; dh = w / ar; dx = 0; dy = (h - dh) / 2; }
              try { ctx.drawImage(bgImg, dx, dy, dw, dh); }
              catch (e) { ctx.fillStyle = '#000'; ctx.fillRect(0, 0, w, h); }
            } else if (isClassic) {
              ctx.fillStyle = bg.value || '#000';
              ctx.fillRect(0, 0, w, h);
            } else {
              // Photographer/explorer have fixed mattes per CSS
              ctx.fillStyle = state.printStyle === 'photographer' ? '#f4ede2' : '#0a0a0a';
              ctx.fillRect(0, 0, w, h);
            }

            // ── 2. Star disc (milky way + stars) ────────────────────────
            // Mirror the per-shape CSS clipping so the canvas stamp matches
            // what's visible:
            //   azimuth → clip-path: circle(41%) on #sm2-celestial-map
            //   heart   → clip-path: url(#sm2-heart-clip), with inner content
            //             transformed scale(1.22) from origin (50%, 42%)
            //             via .sm2-frame[data-shape="heart"] rules.
            //   circle  → no clip (matches default).
            var map = document.getElementById('sm2-celestial-map');
            if (map) {
              var mp = project(map.getBoundingClientRect());
              var starCanvas = map.querySelector('canvas');
              var starImg = sm2StarPreviewSource(map.querySelector('.sm2-celestial-preview'));
              var mwImg = map.querySelector('.sm2-milkyway-overlay');

              if (state.shape === 'heart') {
                // Heart path matches sections/starmap-v2.liquid:104. Path2D
                // accepts SVG path data — we apply the mp transform so the
                // 0..1 path coordinates map to the celestial map rect.
                //
                // No extra shrink: the path itself now carries the 1/1.08
                // meet-fit scale the CSS overlay and the fulfilment render
                // use, so it spans 0.056..0.944 of the map box and already
                // sits well clear of the inset border. (An older 0.86 shrink
                // lived here to buy that clearance; it now double-counts.)
                var hmp = mp;
                var HEART_PATH = 'M0.5,0.9074 C0.2685,0.6481 0.0556,0.5093 0.0556,0.2778 C0.0556,0.1389 0.1204,0.0185 0.2593,0.0185 C0.3796,0.0185 0.463,0.0926 0.5,0.1667 C0.537,0.0926 0.6204,0.0185 0.7407,0.0185 C0.8796,0.0185 0.9444,0.1389 0.9444,0.2778 C0.9444,0.5093 0.7315,0.6481 0.5,0.9074Z';
                ctx.save();
                ctx.translate(hmp.x, hmp.y);
                ctx.scale(hmp.w, hmp.h);
                try { ctx.clip(new Path2D(HEART_PATH)); } catch (e) {}
                // Undo path-space transform manually so we keep the clip but
                // draw in the same user space as the rest of the capture.
                ctx.scale(1 / hmp.w, 1 / hmp.h);
                ctx.translate(-hmp.x, -hmp.y);
                // Mirror the CSS scale(1.22) transform-origin: 50% 42%.
                var hcx = hmp.x + hmp.w * 0.5;
                var hcy = hmp.y + hmp.h * 0.42;
                ctx.translate(hcx, hcy);
                ctx.scale(1.22, 1.22);
                ctx.translate(-hcx, -hcy);
                try {
                  if (starImg) {
                    ctx.drawImage(starImg, hmp.x, hmp.y, hmp.w, hmp.h);
                  } else if (starCanvas) {
                    ctx.drawImage(starCanvas, hmp.x, hmp.y, hmp.w, hmp.h);
                  }
                } catch (e) {}
                if (mwImg && mwImg.complete && mwImg.naturalWidth) {
                  var prevOpH = ctx.globalCompositeOperation;
                  ctx.globalCompositeOperation = 'lighter';
                  try { ctx.drawImage(mwImg, hmp.x, hmp.y, hmp.w, hmp.h); } catch (e) {}
                  ctx.globalCompositeOperation = prevOpH;
                }
                ctx.restore();
              } else if (state.composition === 'header') {
                // Header composition: starfield fills the entire rectangle
                // below the white header band (CSS oversizes #sm2-celestial-
                // map to 175% with overflow clipped by the frame). No
                // circular disc clip — draw the star image at its native
                // (oversized) projected box and let the capture canvas's
                // own bounds clip the overflow. The header band itself is
                // re-painted later via the caption background section.
                ctx.save();
                try {
                  if (starImg) {
                    ctx.drawImage(starImg, mp.x, mp.y, mp.w, mp.h);
                  } else if (starCanvas) {
                    ctx.drawImage(starCanvas, mp.x, mp.y, mp.w, mp.h);
                  }
                } catch (e) {}
                if (mwImg && mwImg.complete && mwImg.naturalWidth) {
                  var prevOpHd = ctx.globalCompositeOperation;
                  ctx.globalCompositeOperation = 'lighter';
                  try { ctx.drawImage(mwImg, mp.x, mp.y, mp.w, mp.h); } catch (e) {}
                  ctx.globalCompositeOperation = prevOpHd;
                }
                ctx.restore();
              } else {
                // Both azimuth (41%) and default circle need a disc clip to
                // match the CSS clip-path on #sm2-celestial-map.
                // Classic circle is slightly reduced (44% vs CSS 48%) for
                // the thumbnail so it doesn't touch the frame edges in the
                // lifestyle mockup — matters most on widest aspect (40x50).
                var clipRadius = (state.shape === 'azimuth' || state.shape === 'zodiac') ? 0.41 : 0.44;
                ctx.save();
                ctx.beginPath();
                var acx = mp.x + mp.w * 0.5;
                var acy = mp.y + mp.h * 0.5;
                var ar = Math.min(mp.w, mp.h) * clipRadius;
                ctx.arc(acx, acy, ar, 0, Math.PI * 2);
                ctx.clip();
                try {
                  if (starImg) {
                    ctx.drawImage(starImg, mp.x, mp.y, mp.w, mp.h);
                  } else if (starCanvas) {
                    ctx.drawImage(starCanvas, mp.x, mp.y, mp.w, mp.h);
                  }
                } catch (e) {}
                if (mwImg && mwImg.complete && mwImg.naturalWidth) {
                  var prevOp = ctx.globalCompositeOperation;
                  ctx.globalCompositeOperation = 'lighter';
                  try { ctx.drawImage(mwImg, mp.x, mp.y, mp.w, mp.h); } catch (e) {}
                  ctx.globalCompositeOperation = prevOp;
                }
                ctx.restore();
              }
            }

            // ── 3. Disc border ring ─────────────────────────────────────
            var db = document.getElementById('sm2-disc-border');
            if (db && getComputedStyle(db).display !== 'none') {
              var dbR = project(db.getBoundingClientRect());
              var ring = db.querySelector('.sm2-disc-ring');
              if (ring) {
                var rcs = getComputedStyle(ring);
                ctx.save();
                ctx.beginPath();
                var cx = dbR.x + dbR.w * 0.5, cy = dbR.y + dbR.h * 0.5;
                // Match the reduced classic-circle clipRadius used above for
                // the disc itself so the ring stays hugging the star disc.
                var ringR = (state.shape === 'azimuth') ? 0.48 : 0.44;
                var rr = Math.min(dbR.w, dbR.h) * ringR;
                ctx.arc(cx, cy, rr, 0, Math.PI * 2);
                ctx.strokeStyle = rcs.stroke || '#fff';
                // strokeWidth is in the ring SVG's 100-unit viewBox; scale
                // it to this canvas's disc box (dbR is already projected).
                var sw = parseFloat(rcs.strokeWidth) || 0.64;
                ctx.lineWidth = rcs.vectorEffect === 'non-scaling-stroke' ? sw : sw * Math.min(dbR.w, dbR.h) / 100;
                ctx.stroke();
                ctx.restore();
              }
            }

            // ── 4. Azimuth + heart overlays ─────────────────────────────
            if (azImg) {
              var azR = project(azEl.getBoundingClientRect());
              // CSS uses filter:invert(1) on light backgrounds; canvas ignores
              // CSS filters so we must invert manually via an offscreen canvas.
              var needsInvert = frame.classList.contains('sm2-frame--light-bg');
              if (needsInvert) {
                try {
                  var azOff = document.createElement('canvas');
                  azOff.width = Math.round(azR.w); azOff.height = Math.round(azR.h);
                  var azCtx = azOff.getContext('2d');
                  azCtx.drawImage(azImg, 0, 0, azOff.width, azOff.height);
                  var azData = azCtx.getImageData(0, 0, azOff.width, azOff.height);
                  var d = azData.data;
                  for (var pi = 0; pi < d.length; pi += 4) {
                    d[pi] = 255 - d[pi];
                    d[pi+1] = 255 - d[pi+1];
                    d[pi+2] = 255 - d[pi+2];
                  }
                  azCtx.putImageData(azData, 0, 0);
                  ctx.drawImage(azOff, azR.x, azR.y, azR.w, azR.h);
                } catch (e) {
                  ctx.drawImage(azImg, azR.x, azR.y, azR.w, azR.h);
                }
              } else {
                try { ctx.drawImage(azImg, azR.x, azR.y, azR.w, azR.h); } catch (e) {}
              }
            }
            if (zodiacImg) {
              // getBoundingClientRect() on the rotated ring returns the
              // enlarged rotated bbox, so derive the layout box from the
              // frame geometry instead (ring is top 5% of height, left 5%,
              // width 90%, square — same as the celestial map box), then
              // re-apply the sun-sign rotation around its centre.
              var zR = {
                x: w * 0.05,
                y: h * 0.05,
                w: w * 0.9,
                h: w * 0.9
              };
              var zRot = (sunSignIndex(state.date) * 30) * Math.PI / 180;
              var zSrc = zodiacImg;
              if (frame.classList.contains('sm2-frame--light-bg')) {
                try {
                  var zOff = document.createElement('canvas');
                  zOff.width = Math.round(zR.w); zOff.height = Math.round(zR.h);
                  var zCtx = zOff.getContext('2d');
                  zCtx.drawImage(zodiacImg, 0, 0, zOff.width, zOff.height);
                  var zData = zCtx.getImageData(0, 0, zOff.width, zOff.height);
                  var zd = zData.data;
                  for (var zi = 0; zi < zd.length; zi += 4) {
                    zd[zi] = 255 - zd[zi];
                    zd[zi+1] = 255 - zd[zi+1];
                    zd[zi+2] = 255 - zd[zi+2];
                  }
                  zCtx.putImageData(zData, 0, 0);
                  zSrc = zOff;
                } catch (e) { /* draw uninverted */ }
              }
              try {
                ctx.save();
                ctx.translate(zR.x + zR.w / 2, zR.y + zR.h / 2);
                ctx.rotate(zRot);
                ctx.drawImage(zSrc, -zR.w / 2, -zR.h / 2, zR.w, zR.h);
                ctx.restore();
              } catch (e) {}
            }
            if (heartImg) {
              // The element rect already carries the meet-fit sizing from
              // .sm2-heart-border (width 83.333%, left 8.333%), which matches
              // the scaled clip path applied to the disc above — so draw it
              // 1:1 with no extra shrink.
              var hR = project(heartEl.getBoundingClientRect());
              var hRx = hR.x, hRy = hR.y, hRw = hR.w, hRh = hR.h;
              // CSS uses filter:invert(1) on light backgrounds to flip the
              // white-stroked heart asset to black; canvas ignores CSS
              // filters so we mirror the azimuth inversion above.
              var heartNeedsInvert = frame.classList.contains('sm2-frame--light-bg');
              if (heartNeedsInvert) {
                try {
                  var hOff = document.createElement('canvas');
                  hOff.width = Math.max(1, Math.round(hRw));
                  hOff.height = Math.max(1, Math.round(hRh));
                  var hCtx = hOff.getContext('2d');
                  hCtx.drawImage(heartImg, 0, 0, hOff.width, hOff.height);
                  var hData = hCtx.getImageData(0, 0, hOff.width, hOff.height);
                  var hd = hData.data;
                  for (var hi = 0; hi < hd.length; hi += 4) {
                    hd[hi]   = 255 - hd[hi];
                    hd[hi+1] = 255 - hd[hi+1];
                    hd[hi+2] = 255 - hd[hi+2];
                  }
                  hCtx.putImageData(hData, 0, 0);
                  ctx.drawImage(hOff, hRx, hRy, hRw, hRh);
                } catch (e) {
                  ctx.drawImage(heartImg, hRx, hRy, hRw, hRh);
                }
              } else {
                try { ctx.drawImage(heartImg, hRx, hRy, hRw, hRh); } catch (e) {}
              }
            }

            // ── 4b. Inner matte border ──────────────────────────────────
            // Drawn later in _compositeIntoMockup aligned to the mockup
            // frame cutout edges — drawing it on the print canvas gets its
            // left/right sides clipped when cover-fit into the frame.

            // ── 5. Caption background (panel / header / boxed) ──────────
            var captionEl = document.getElementById('sm2-caption');
            if (captionEl && getComputedStyle(captionEl).display !== 'none') {
              var ccs = getComputedStyle(captionEl);
              var cBg = ccs.backgroundColor;
              var transparent = !cBg || cBg === 'transparent' ||
                                cBg === 'rgba(0, 0, 0, 0)' || cBg === 'rgba(0,0,0,0)';
              var bw = parseFloat(ccs.borderTopWidth) || 0;
              var bc = ccs.borderTopColor;
              var br = parseFloat(ccs.borderTopLeftRadius) || 0;
              if (!transparent || bw > 0) {
                var cr = project(captionEl.getBoundingClientRect());
                var brS = br * sx;
                function roundRect(x, y, w2, h2, r) {
                  r = Math.min(r, Math.min(w2, h2) / 2);
                  ctx.beginPath();
                  ctx.moveTo(x + r, y);
                  ctx.lineTo(x + w2 - r, y);
                  ctx.quadraticCurveTo(x + w2, y, x + w2, y + r);
                  ctx.lineTo(x + w2, y + h2 - r);
                  ctx.quadraticCurveTo(x + w2, y + h2, x + w2 - r, y + h2);
                  ctx.lineTo(x + r, y + h2);
                  ctx.quadraticCurveTo(x, y + h2, x, y + h2 - r);
                  ctx.lineTo(x, y + r);
                  ctx.quadraticCurveTo(x, y, x + r, y);
                  ctx.closePath();
                }
                if (!transparent) {
                  ctx.save();
                  ctx.fillStyle = cBg;
                  roundRect(cr.x, cr.y, cr.w, cr.h, brS);
                  ctx.fill();
                  ctx.restore();
                }
                if (bw > 0 && bc && bc !== 'transparent' && bc !== 'rgba(0, 0, 0, 0)') {
                  ctx.save();
                  ctx.strokeStyle = bc;
                  ctx.lineWidth = bw * sx;
                  roundRect(cr.x + (bw * sx) / 2, cr.y + (bw * sy) / 2,
                            cr.w - bw * sx, cr.h - bw * sy, Math.max(0, brS - (bw * sx) / 2));
                  ctx.stroke();
                  ctx.restore();
                }
              }
            }

            // ── 6. Caption text ─────────────────────────────────────────
            function paintText(id) {
              var el = document.getElementById(id);
              if (!el) return;
              var txt = el.textContent;
              if (!txt || !txt.trim()) return;
              var cs = getComputedStyle(el);
              if (cs.display === 'none' || cs.visibility === 'hidden') return;

              // Apply CSS text-transform (panel/header use uppercase on details)
              var tt = cs.textTransform;
              if (tt === 'uppercase') txt = txt.toUpperCase();
              else if (tt === 'lowercase') txt = txt.toLowerCase();

              var er = project(el.getBoundingClientRect());
              var fs = parseFloat(cs.fontSize) * sy;
              var fam = cs.fontFamily || 'sans-serif';
              var fw = cs.fontWeight || 'normal';
              var fst = cs.fontStyle || 'normal';
              ctx.fillStyle = cs.color || '#fff';

              // Apply parent opacity (e.g. .sm2-caption-details opacity:0.9)
              var ancestor = el.parentElement;
              var combinedOpacity = parseFloat(cs.opacity) || 1;
              while (ancestor && ancestor !== frame) {
                var ao = parseFloat(getComputedStyle(ancestor).opacity);
                if (!isNaN(ao) && ao < 1) combinedOpacity *= ao;
                ancestor = ancestor.parentElement;
              }
              if (combinedOpacity < 1) ctx.globalAlpha = combinedOpacity;

              ctx.font = fst + ' ' + fw + ' ' + fs + 'px ' + fam;

              // Apply letter-spacing if the canvas API supports it
              var ls = parseFloat(cs.letterSpacing);
              if (ls && ctx.letterSpacing !== undefined) {
                ctx.letterSpacing = (ls * sx) + 'px';
              }

              var align = cs.textAlign === 'center' ? 'center' : (cs.textAlign === 'right' ? 'right' : 'left');
              ctx.textAlign = align;
              ctx.textBaseline = 'top';
              var tx = align === 'center' ? er.x + er.w / 2 : (align === 'right' ? er.x + er.w : er.x);
              var lines = txt.split('\n');
              var lh = parseFloat(cs.lineHeight);
              lh = isNaN(lh) ? fs * 1.2 : lh * sy;
              for (var i = 0; i < lines.length; i++) {
                ctx.fillText(lines[i], tx, er.y + i * lh);
              }
              // Reset state
              ctx.globalAlpha = 1;
              if (ctx.letterSpacing !== undefined) ctx.letterSpacing = '0px';
            }
            // When handwriting is active, the message element holds an SVG
            // not text — rasterize the strokes onto the thumbnail at the
            // SVG's projected rect using the inherited caption text colour.
            function paintHandwriting() {
              if (!state.handwritingEnabled || !state.handwritingStrokes || !state.handwritingStrokes.length) return false;
              var msgEl = document.getElementById('sm2-caption-message');
              if (!msgEl) return false;
              var svg = msgEl.querySelector('svg');
              if (!svg) return false;
              var sr = project(svg.getBoundingClientRect());
              if (sr.w <= 0 || sr.h <= 0) return false;
              var cs = getComputedStyle(msgEl);
              ctx.save();
              ctx.strokeStyle = cs.color || '#fff';
              ctx.lineCap = 'round'; ctx.lineJoin = 'round';
              var vbw = 1000, vbh = 240;
              var scale = Math.min(sr.w / vbw, sr.h / vbh);
              for (var i = 0; i < state.handwritingStrokes.length; i++) {
                var s = state.handwritingStrokes[i];
                if (!s.pts || !s.pts.length) continue;
                ctx.lineWidth = Math.max(1, (s.w || 4) * scale);
                ctx.beginPath();
                var px = function(p) { return [sr.x + p[0] / vbw * sr.w, sr.y + p[1] / vbh * sr.h]; };
                ctx.moveTo(px(s.pts[0])[0], px(s.pts[0])[1]);
                if (s.pts.length === 1) {
                  ctx.lineTo(px(s.pts[0])[0] + 0.01, px(s.pts[0])[1] + 0.01);
                } else if (s.pts.length === 2) {
                  ctx.lineTo(px(s.pts[1])[0], px(s.pts[1])[1]);
                } else {
                  // Midpoint quadratic smoothing — mirrors the pad/preview/renderer.
                  for (var j = 1; j < s.pts.length - 1; j++) {
                    var cp = px(s.pts[j]), np = px(s.pts[j + 1]);
                    ctx.quadraticCurveTo(cp[0], cp[1], (cp[0] + np[0]) / 2, (cp[1] + np[1]) / 2);
                  }
                  var pe = px(s.pts[s.pts.length - 1]);
                  ctx.lineTo(pe[0], pe[1]);
                }
                ctx.stroke();
              }
              ctx.restore();
              return true;
            }
            // Preload caption webfonts so canvas doesn't silently fall back
            // to Times Italic / Arial when rasterizing Great Vibes / Josefin
            // Sans / etc. document.fonts.load(spec) gates on the EXACT
            // size+style+weight+family the canvas will request — without
            // this the cart thumbnail script-font text reads visibly heavier
            // than the live customizer preview.
            var captionIds = ['sm2-caption-headline', 'sm2-caption-date',
                              'sm2-caption-location', 'sm2-caption-coords',
                              'sm2-caption-message'];
            var fontSpecs = [];
            for (var fi = 0; fi < captionIds.length; fi++) {
              var fEl = document.getElementById(captionIds[fi]);
              if (!fEl) continue;
              var fTxt = fEl.textContent;
              if (!fTxt || !fTxt.trim()) continue;
              var fCs = getComputedStyle(fEl);
              if (fCs.display === 'none' || fCs.visibility === 'hidden') continue;
              var fSize = parseFloat(fCs.fontSize) * sy;
              fontSpecs.push(
                (fCs.fontStyle || 'normal') + ' ' +
                (fCs.fontWeight || 'normal') + ' ' +
                fSize + 'px ' +
                (fCs.fontFamily || 'sans-serif')
              );
            }
            var fontReady = (document.fonts && document.fonts.load)
              ? Promise.all(fontSpecs.map(function(s) {
                  return document.fonts.load(s).catch(function() {});
                }))
              : Promise.resolve();

            fontReady.then(function() {
              var hwPainted = paintHandwriting();
              ['sm2-caption-headline', 'sm2-caption-date',
               'sm2-caption-location', 'sm2-caption-coords'].forEach(paintText);
              if (!hwPainted) paintText('sm2-caption-message');

              // ── 7. Composite into lifestyle mockup frame ────────────────
              var hasBorder = frame.classList.contains('sm2-frame--border');
              var isLight = frame.classList.contains('sm2-frame--light-bg');
              _compositeIntoMockup(canvas, {
                border: hasBorder,
                borderColor: isLight ? '#1a1a1a' : '#ffffff',
                shape: state.shape,
                // The mockup overlay is a framed-on-wall scene shared across
                // all three finishes. Framed prints already include a 25px
                // matte (assets/starmap-v2.css:1772), so the celestial circle
                // sits inset from the mockup frame edges. Canvas/Print have
                // no matte and would otherwise touch the inset border. Pass
                // matteless=true so the compositor letterboxes the print
                // inside MF for cosmetic breathing room. Real per-finish
                // mockup overlays are tracked as a follow-up.
                matteless: state.selectedType === 'CANVAS' || state.selectedType === 'PRINT'
              }).then(function(url) {
                resolve(url);
              });
            });
          });
        } catch (e) {
          if (window.skylitBeaconThumbnailSkip) {
            try { window.skylitBeaconThumbnailSkip(state.orderId, 'star', 'generator_error', { error: String(e).slice(0, 200) }); } catch (_e) {}
          }
          resolve('');
        }
      });
    }

    // ── gift-wrap extras persistence ────────────────────────────────────
    //
    // The star-map DESIGN round-trips through the server `star_maps` row
    // (hydrateFromRow / buildAdminData). The gift-wrap selections are gift
    // add-on attributes, not render data, so they don't belong in the
    // star-map schema. The wrap TYPE (`gift_option`) already persists
    // server-side, but the ribbon-colour swatch and the gift-note message
    // are session-only `state`/DOM and reset on a draft resume.
    //
    // Persist them in localStorage keyed by the draft's order id — the same
    // lightest mechanism the `sm2_last_draft` pointer already uses. Written
    // alongside every autosave / explicit save; rehydrated in hydrateFromRow.
    var GIFT_EXTRAS_PREFIX = 'sm2_gift_';

    function saveGiftExtras() {
      try {
        if (!state.orderId) return;
        // Snapshot the gift-note editor straight from the DOM — in the
        // customizer the gift-note-card snippet runs in local_only mode and
        // never writes the message anywhere persistent.
        var note = '', design = 'editorial', font = 'handwritten', enabled = false;
        var ta = document.getElementById('Gift-note-starmap') ||
                 document.getElementById('Gift-note');
        if (ta) note = ta.value || '';
        var gncRoot = document.querySelector('.gnc[data-gnc="starmap"]');
        if (gncRoot) {
          var d = gncRoot.getAttribute('data-design');
          if (d === 'editorial' || d === 'minimal') design = d;
          var f = gncRoot.getAttribute('data-font');
          if (f === 'handwritten' || f === 'classic') font = f;
          enabled = gncRoot.getAttribute('data-enabled') === 'true';
        }
        var rec = {
          gift: state.gift || 'none',
          ribbonColor: state.ribbonColor || 'Red',
          giftPaper: state.giftPaper || 'dots',
          giftNote: note,
          giftCardDesign: design,
          giftCardFont: font,
          giftNoteEnabled: enabled,
          ts: Date.now()
        };
        localStorage.setItem(GIFT_EXTRAS_PREFIX + state.orderId, JSON.stringify(rec));
      } catch (e) { /* localStorage unavailable — non-fatal */ }
    }

    function loadGiftExtras(orderId) {
      try {
        if (!orderId) return null;
        var raw = localStorage.getItem(GIFT_EXTRAS_PREFIX + orderId);
        if (!raw) return null;
        var rec = JSON.parse(raw);
        if (!rec) return null;
        // Drop stale entries on the same 30-day horizon as sm2_last_draft.
        if (rec.ts && (Date.now() - rec.ts) > 30 * 86400000) {
          localStorage.removeItem(GIFT_EXTRAS_PREFIX + orderId);
          return null;
        }
        return rec;
      } catch (e) { return null; }
    }

    // Push a saved gift-extras record back into the gift-note editor DOM.
    // Mirrors hydrateGiftNoteFromPairedAddon but sources from localStorage,
    // so a plain draft resume (no paired add-on in cart yet) still restores
    // the customer's message, card design and font.
    function applyGiftNoteFromExtras(rec, _attempt) {
      if (!rec) return;
      _attempt = _attempt || 0;
      try {
        var root = document.querySelector('.gnc[data-gnc="starmap"]');
        // The gift-note-card snippet runs its own inline boot script. If it
        // hasn't rendered yet, retry a few times so we apply on top of it
        // (rather than have its init overwrite our restored values).
        if (!root) {
          if (_attempt < 20) {
            setTimeout(function() { applyGiftNoteFromExtras(rec, _attempt + 1); }, 100);
          }
          return;
        }
        var design = (rec.giftCardDesign === 'minimal' || rec.giftCardDesign === 'editorial')
          ? rec.giftCardDesign : 'editorial';
        var font = (rec.giftCardFont === 'classic' || rec.giftCardFont === 'handwritten')
          ? rec.giftCardFont : 'handwritten';
        var note = rec.giftNote || '';
        document.querySelectorAll('.gnc[data-gnc="starmap"]').forEach(function(el) {
          el.setAttribute('data-design', design);
          el.setAttribute('data-font', font);
          el.setAttribute('data-enabled', (rec.giftNoteEnabled && note) ? 'true' : 'false');
        });
        var ta = document.getElementById('Gift-note-starmap') ||
                 document.getElementById('Gift-note');
        if (ta) ta.value = note;
        var cb = document.querySelector('.gnc[data-gnc="starmap"] [data-gnc-enabled]');
        if (cb) cb.checked = !!(rec.giftNoteEnabled && note);
        // Trigger an input event so the snippet's render() repaints the card
        // preview with the restored message at the right font size.
        if (ta) ta.dispatchEvent(new Event('input', { bubbles: true }));
      } catch (e) {
        console.warn('[sm2.cart] gift-extras note hydrate failed', e);
      }
    }

    // Debounced draft autosave. Fires after the user has been idle for
    // AUTOSAVE_DELAY_MS following any state change. Writes status='draft' to
    // the admin row so the funnel can distinguish "started but didn't add to
    // cart" from "added to cart". The no-regress guard in the route handler
    // prevents this from clobbering a 'cart' / 'paid' row.
    var AUTOSAVE_DELAY_MS = 2500;
    var autosaveTimer = null;
    var autosavePrimed = false;
    function scheduleAutosaveDraft() {
      // Don't autosave during the initial seed/hydrate window — saveForLater /
      // addToCart are the explicit user-driven saves. Once the user makes a
      // post-boot change, autosave takes over for resume.
      if (!autosavePrimed) return;
      if (state.savedMode) return; // viewing a saved order, not editing
      // Edit mode = the design is in the live cart. Its D1 row is what gets
      // printed, so unconfirmed edits must NOT autosave over it — the cart
      // line would keep showing the old design while the print silently
      // changed. Edits persist only via "Confirm edits" (addToCart), same
      // as the moon / hor-moon / location customizers. What they try is
      // still noted, on its own (cart-edit), for the admin to see.
      if (state.editMode) { scheduleCartEditLog(); return; }
      if (!state.orderId) return;
      if (autosaveTimer) clearTimeout(autosaveTimer);
      autosaveTimer = setTimeout(function() {
        autosaveTimer = null;
        postAdmin(false, 'draft').then(function() {
          try {
            localStorage.setItem('sm2_last_draft', JSON.stringify({
              orderId: state.orderId,
              ts: Date.now()
            }));
          } catch (e) {}
          // Gift-wrap extras (ribbon colour + gift note) aren't part of the
          // star-map row — persist them alongside the draft pointer.
          saveGiftExtras();
        });
      }, AUTOSAVE_DELAY_MS);
    }
    // Edit mode only: the options tried on a carted design, sent to the
    // admin's cart-edit log and never to the design itself (star-map-data).
    // Fire-and-forget; a failure changes nothing for the customer.
    var cartEditTimer = null;
    function scheduleCartEditLog() {
      if (!state.orderId) return;
      if (cartEditTimer) clearTimeout(cartEditTimer);
      cartEditTimer = setTimeout(function() {
        cartEditTimer = null;
        try {
          fetch(PROXY_BASE + 'cart-edit', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ product_type: 'star', data: buildAdminData() }, function (k, v) { return (typeof v === 'string' && v.length > 1000 && v.indexOf('data:') === 0) ? undefined : v; })
          }).catch(function() {});
        } catch (e) {}
      }, AUTOSAVE_DELAY_MS);
    }
    function primeAutosave() { autosavePrimed = true; }
    // Preset loads hydrate a template but must NOT be treated as a customer
    // draft until they actually engage. Arm autosave on the FIRST real
    // interaction instead of at boot, so merely opening a preset doesn't write
    // a draft (and fire a spurious "new customisation" push) — the hydrate's
    // debounced renders would otherwise trip autosave once primed. The first
    // interaction itself then persists.
    function primeAutosaveOnFirstInteraction() {
      if (autosavePrimed) return;
      var root = document.querySelector('.sm2-wrapper') || document;
      var evs = ['pointerdown', 'keydown', 'input', 'change'];
      function onFirst() {
        evs.forEach(function (ev) { root.removeEventListener(ev, onFirst, true); });
        // Only prime — a genuine design change fires its own scheduleAutosaveDraft
        // (via setState) right after, so draft_started lands on a real edit, not
        // on the first stray click/scroll.
        primeAutosave();
      }
      evs.forEach(function (ev) { root.addEventListener(ev, onFirst, true); });
    }

    // Resume prompt — generic two-button modal. Used both for plain-URL
    // resume ("we have a draft, want it?") and preset-URL resume ("we have
    // a saved version of this preset, continue or start fresh from template?").
    function showResumeModal(opts) {
      var title = opts.title || 'Resume your previous design?';
      var body  = opts.body  || 'We saved your last star map. Pick up where you left off, or start fresh.';
      var primaryLabel   = opts.primaryLabel   || 'Resume';
      var secondaryLabel = opts.secondaryLabel || 'Start fresh';
      var onPrimary   = opts.onPrimary   || function() {};
      var onSecondary = opts.onSecondary || function() {};
      // Dismissal (backdrop click / Escape) is NOT the same as "Start fresh":
      // it must never destroy the draft pointer. A stray click outside the
      // card used to fire onSecondary and permanently lose the draft.
      var onDismiss   = opts.onDismiss   || function() {};
      var overlay = document.createElement('div');
      overlay.id = 'sm2-resume-modal';
      overlay.innerHTML =
        '<style>' +
        '#sm2-resume-modal{position:fixed;inset:0;z-index:2147483646;display:flex;align-items:center;justify-content:center;background:rgba(20,18,16,0.55);backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px);font-family:inherit;animation:sm2rmFade 220ms ease-out}' +
        '#sm2-resume-modal .sm2rm-card{background:#f5efe6;color:#1c1a17;border-radius:14px;padding:26px 26px 22px;width:min(360px,86vw);box-shadow:0 18px 60px rgba(0,0,0,0.35);text-align:center;animation:sm2rmPop 240ms cubic-bezier(.2,.9,.3,1.1)}' +
        '#sm2-resume-modal h3{margin:0 0 6px;font-size:18px;font-weight:600;letter-spacing:.01em}' +
        '#sm2-resume-modal p{margin:0 0 18px;font-size:13px;line-height:1.5;color:#6b6357}' +
        '#sm2-resume-modal .sm2rm-row{display:flex;gap:10px}' +
        '#sm2-resume-modal button{flex:1;padding:11px 14px;border-radius:10px;border:1px solid #d6cdbf;background:#fff;color:#1c1a17;font:inherit;font-size:14px;cursor:pointer;transition:background 120ms,transform 120ms}' +
        '#sm2-resume-modal button:hover{background:#ece4d6}' +
        '#sm2-resume-modal button.sm2rm-primary{background:#1c1a17;color:#f5efe6;border-color:#1c1a17}' +
        '#sm2-resume-modal button.sm2rm-primary:hover{background:#000}' +
        '@keyframes sm2rmFade{from{opacity:0}to{opacity:1}}' +
        '@keyframes sm2rmPop{from{transform:scale(.94);opacity:0}to{transform:scale(1);opacity:1}}' +
        '</style>' +
        '<div class="sm2rm-card" role="dialog" aria-modal="true" aria-labelledby="sm2rm-title">' +
          '<h3 id="sm2rm-title"></h3>' +
          '<p></p>' +
          '<div class="sm2rm-row">' +
            '<button type="button" class="sm2rm-dismiss"></button>' +
            '<button type="button" class="sm2rm-primary"></button>' +
          '</div>' +
        '</div>';
      overlay.querySelector('h3').textContent = title;
      overlay.querySelector('p').textContent  = body;
      overlay.querySelector('.sm2rm-primary').textContent   = primaryLabel;
      overlay.querySelector('.sm2rm-dismiss').textContent   = secondaryLabel;
      document.body.appendChild(overlay);
      var close = function() { if (overlay.parentNode) overlay.parentNode.removeChild(overlay); };
      overlay.querySelector('.sm2rm-primary').addEventListener('click', function() { close(); onPrimary(); });
      overlay.querySelector('.sm2rm-dismiss').addEventListener('click', function() { close(); onSecondary(); });
      overlay.addEventListener('click', function(e) {
        if (e.target === overlay) { close(); onDismiss(); }
      });
      document.addEventListener('keydown', function onEsc(e) {
        if (e.key === 'Escape') {
          document.removeEventListener('keydown', onEsc, true);
          if (overlay.parentNode) { close(); onDismiss(); }
        }
      }, true);
    }
    // Open a saved design in place — no full page reload. Mirrors the
    // ?order_id= boot path: set state.orderId, resolve editMode against the
    // current cart state, swap the ATC label, and stamp ?order_id= via
    // replaceState so a refresh re-enters the same session instead of
    // re-prompting. Ported from locationmap (commit a6e126b).
    function resumeOrderInPlace(orderId) {
      state.orderId = orderId;
      state.editMode = state.cartOrderIds && state.cartOrderIds.indexOf(orderId) !== -1;
      try {
        var u = new URL(window.location.href);
        u.searchParams.set('order_id', orderId);
        history.replaceState(null, '', u.toString());
      } catch (e) {}
      fetchOrder(orderId).then(function(row) {
        if (!row) { primeAutosave(); return; }
        hydrateFromRow(row);
        if (state.editMode) {
          var btn = document.getElementById('sm2-add-to-cart');
          if (btn) btn.textContent = 'Confirm edits';
          if (typeof logEditPersonalisationsOpened === 'function') {
            logEditPersonalisationsOpened(orderId);
          }
        }
        primeAutosave();
      });
    }

    function resumeDraftIfAny() {
      // Boot skips primeAutosave() whenever a draft pointer exists (so a
      // pending modal doesn't get raced by a stub autosave). Every path below
      // that ends without a modal must therefore prime autosave itself, or the
      // whole session runs with autosave dead.
      try {
        var url = new URL(window.location.href);
        // Skip on direct-edit / view URLs. Preset URLs reset the active
        // pointer in initFromUrl, so we don't surface the resume modal there.
        if (url.searchParams.get('order_id') ||
            url.searchParams.get('saved') ||
            url.searchParams.get('preset')) return;
        // Deep-links with a prefilled date (search results, the cart's
        // "matching print" rail) are a deliberate fresh start — offering to
        // resume an old draft on top would bury the prefill. Same rule the
        // moon customizer applies.
        if (url.searchParams.get('prefill_date') || url.searchParams.get('prefill_lat')) { primeAutosave(); return; }

        var rec = null;
        var raw = localStorage.getItem('sm2_last_draft');
        if (raw) {
          try { rec = JSON.parse(raw); } catch (pe) {}
          if (!rec || !rec.orderId ||
              // Drafts older than 30 days are likely abandoned and confusing
              // to resurface.
              (rec.ts && (Date.now() - rec.ts) > 30 * 86400000)) {
            localStorage.removeItem('sm2_last_draft');
            rec = null;
          }
        }

        // Resolve the saved order's status before offering it. Gone server-side
        // (GC'd, never autosaved, API unreachable) or already paid: drop the
        // pointer silently — never offer to "continue editing" a printed order.
        var draftRow = rec ? fetchOrder(rec.orderId).then(function(row) {
          if (!row) {
            console.log('[sm2.cart] last draft has no server row, clearing localStorage', rec.orderId);
            localStorage.removeItem('sm2_last_draft');
            return null;
          }
          if (isPaidLocked(row, rec.orderId)) {
            console.log('[sm2.cart] last draft is a paid order, clearing localStorage', rec.orderId);
            localStorage.removeItem('sm2_last_draft');
            return null;
          }
          return row;
        }) : Promise.resolve(null);

        draftRow.then(function(row) {
          var picker = window.SkylitResumePicker;
          var draft = row ? {
            orderId: rec.orderId,
            primary: String(row.message || row.title || '').split('\n')[0].trim(),
            secondary: row.location || ''
          } : null;
          return (picker ? picker.gather('star', draft) : Promise.resolve([])).then(function(items) {
            // Two or more star maps between the cart and the last draft: list
            // them all, so the customer isn't limited to the last one they
            // touched.
            if (items.length >= 2) {
              setTimeout(function() {
                picker.show({
                  family: 'star',
                  items: items,
                  onPick: function(item) { resumeOrderInPlace(item.orderId); },
                  onNew: function() {
                    localStorage.removeItem('sm2_last_draft');
                    primeAutosave();
                  },
                  onDismiss: function() { primeAutosave(); }
                });
              }, 800);
              return;
            }
            if (!row) { primeAutosave(); return; }
            // A design that's already in the live cart isn't a dangling draft —
            // the cart's "Edit personalisations" already covers it. Offer
            // cart-aware copy so "start a new one" doesn't read like it would
            // lose the carted design. Deferred so the user sees the default
            // state first; then offers to swap in their draft.
            var inCart = state.cartOrderIds && state.cartOrderIds.indexOf(rec.orderId) !== -1;
            setTimeout(function() {
              showResumeModal({
                title: inCart ? 'This design is in your cart' : 'Resume your previous design?',
                body: inCart
                  ? 'Your last star map is already in your cart, safe and sound. Edit it here, or start a brand-new design.'
                  : 'We saved your last star map. Pick up where you left off, or start fresh.',
                primaryLabel: inCart ? 'Edit cart design' : 'Resume',
                secondaryLabel: inCart ? 'Start a new design' : 'Start fresh',
                onPrimary: function() { resumeOrderInPlace(rec.orderId); },
                onSecondary: function() {
                  localStorage.removeItem('sm2_last_draft');
                  primeAutosave();
                },
                // Backdrop click / Escape: keep the pointer so a refresh
                // re-offers the draft; just let this session proceed fresh.
                onDismiss: function() {
                  primeAutosave();
                }
              });
            }, 800);
          });
        }).catch(function() { primeAutosave(); });
      } catch (e) {
        // Never leave the session with autosave unarmed.
        try { primeAutosave(); } catch (e2) {}
      }
    }


    return {
      newOrderId: newOrderId,
      loadCartState: loadCartState,
      initFromUrl: initFromUrl,
      buildAdminData: buildAdminData,
      buildCartItem: buildCartItem,
      resolveVariant: resolveVariant,
      hasExactVariant: hasExactVariant,
      addToCart: addToCart,
      saveForLater: saveForLater,
      generateThumbnail: _generateThumbnail,
      hydrateFromRow: hydrateFromRow,
      scheduleAutosaveDraft: scheduleAutosaveDraft,
      primeAutosave: primeAutosave,
      primeAutosaveOnFirstInteraction: primeAutosaveOnFirstInteraction,
      resumeDraftIfAny: resumeDraftIfAny
    };
  })();

  // ── Public API ───────────────────────────────────────────────────────────

  window.SM2 = {
    state: state,
    setState: setState,
    render: render,
    cart: Cart,
    get milkywayConfig() { return milkywayConfig; }
  };

  // ACP headless preview hook — called by the admin customizer to push a
  // snapshot of options and get a rendered frame back.
  window.SM2.hydrateAndRender = function(snapshot) {
    return new Promise(function(resolve, reject) {
      try {
        // Use setState so Alpine store bindings re-evaluate (handles proxy writes).
        if (snapshot && typeof snapshot === 'object') {
          setState(snapshot);
        }
        render();
        // Two rAF ticks: first tick starts the paint, second catches the
        // celestial canvas-to-img promotion that happens on the trailing frame.
        requestAnimationFrame(function() {
          requestAnimationFrame(function() { resolve(); });
        });
      } catch (e) { reject(e); }
    });
  };

  // ACP Phase 1: full headless boot — translates ACP-plan state keys to
  // internal SM2 state keys, then runs the heavy boot sequence (initCelestial,
  // applyTheme, applyGeoposToCelestial, etc.) that init() does on a real
  // page load. Resolves once the celestial canvas has rendered and the
  // milkyway has settled (via waitForStableRender).
  //
  // setState writes through Alpine if present and otherwise Object.assigns
  // into `state`; in headless preview-host there is no Alpine so the patch
  // lands directly on state. setState also triggers downstream side effects
  // (applyGeoposToCelestial, scheduleRender, syncLegacyInputs etc.) but
  // those tolerate missing DOM via early returns / typeof checks, so calling
  // it is safe pre-Celestial.
  window.SM2.bootHeadless = function(externalState) {
    return new Promise(function(resolve, reject) {
      try {
        // Translate external ACP-plan keys → internal state keys.
        var INTERNAL_KEY_MAP = {
          theme: 'themeKey',
          title: 'headline',
          message: 'customMessage',
          show_milkyway: 'showMilkyway',
          // Without this, `show_time` arrived from the cart-thumbnail /ACP state
          // and was set as an unknown key, leaving internal `showTime` at its
          // default of false — so an order WITH the time toggled on rendered its
          // cart thumbnail without the time, contradicting the customer's design
          // (star_maps.time_visible was correct all along).
          show_time: 'showTime',
          show_constellations: 'showConstellations',
          show_grid: 'showGrid',
          show_labels: 'showLabels',
          show_planets: 'showPlanets',
          show_border: 'showBorder',
          message_font: 'font',
          details_font: 'detailsFont'
          // background, composition, shape, date, time, timezone, location,
          // coords pass through unchanged (key names already match).
        };
        var patch = {};
        Object.keys(externalState || {}).forEach(function(k) {
          var mappedKey = INTERNAL_KEY_MAP[k] || k;
          patch[mappedKey] = externalState[k];
        });
        setState(patch);

        // The printed date/time dirty + cleared flags are module-local (not in
        // state), so a headless render — e.g. the cart-thumbnail host — must
        // sync them from the incoming state for effectiveDateTime() to honour a
        // custom or deliberately-blank printed date. (locationOverrideCleared /
        // coordsOverrideCleared ARE state keys, so setState above covers those.)
        if ('dateTimeOverride' in patch || 'dateTimeOverrideCleared' in patch) {
          dateTimeOverrideDirty = !!(patch.dateTimeOverride && String(patch.dateTimeOverride).trim());
          dateTimeOverrideCleared = !!patch.dateTimeOverrideCleared;
        }

        // Seed milkyway center (from init() line ~4216)
        milkywayConfig.center = [209.75, 53.48, -110];

        // Boot the celestial pipeline.
        initCelestial();

        // Trimmed from 500ms/800ms — CF Browser Rendering already waits for
        // domcontentloaded, initCelestial + apply*() are synchronous.
        setTimeout(function() {
          try { ensureMilkywayContainer(); } catch (e) { console.error('[sm2.bootHeadless] ensureMW', e); }
          try { render({}); } catch (e) { console.error('[sm2.bootHeadless] render', e); }
          try { squareUpCelestialMap(); } catch (e) { console.error('[sm2.bootHeadless] squareUp', e); }
          try { applyTheme(); } catch (e) { console.error('[sm2.bootHeadless] applyTheme', e); }

          setTimeout(function() {
            try { applyGeoposToCelestial(); } catch (e) {}
            try { applyDateToCelestial(); } catch (e) {}
            try { applyElements(); } catch (e) {}
            try { applyBorder(); } catch (e) {}
            try { applyBackground(); } catch (e) {}
            try { updateCaption(); } catch (e) { console.error('[sm2.bootHeadless] updateCaption', e); }

            // Poll for canvas paint with a minimum wait so stars finish
            // rendering before the screenshot is taken.
            var POLL_INTERVAL = 100;
            var MIN_WAIT_MS = 1500;
            var MAX_WAIT_MS = 4000;
            var pollStart = Date.now();
            var pollId = setInterval(function() {
              var elapsed = Date.now() - pollStart;
              var canvases = document.querySelectorAll('canvas');
              var hasContent = canvases.length > 0 && canvases[0].width > 0;
              // Allow exit only after MIN_WAIT_MS so first stars finish painting
              if (elapsed >= MIN_WAIT_MS && hasContent) {
                clearInterval(pollId);
                requestAnimationFrame(function() {
                  resolve({ stable: true, elapsed: elapsed, canvasCount: canvases.length });
                });
              } else if (elapsed >= MAX_WAIT_MS) {
                clearInterval(pollId);
                resolve({ stable: false, elapsed: elapsed, canvasCount: canvases.length });
              }
            }, POLL_INTERVAL);
          }, 200);
        }, 50);
      } catch (e) { reject(e); }
    });
  };

  // Re-export legacy helpers so anything still calling them works unchanged
  window.waitForStableRender = function() {
    return new Promise(function(resolve) {
      var maxWait = 3000, interval = 50, elapsed = 0;
      function check() {
        var stable = !_isLoading && !_isZooming && _currentAnimation === null && _animFrameId === null;
        if (stable || elapsed >= maxWait) {
          if (_currentAnimation) { cancelAnimationFrame(_currentAnimation); _currentAnimation = null; }
          if (_animFrameId) { cancelAnimationFrame(_animFrameId); _animFrameId = null; }
          _isLoading = false;
          renderMilkyWay(milkywayConfig.center, true);
          setTimeout(function() {
            resolve({ stable: stable, elapsed: elapsed, center: [].concat(milkywayConfig.center) });
          }, 100);
        } else {
          elapsed += interval;
          setTimeout(check, interval);
        }
      }
      check();
    });
  };

  window.forceStableRedraw = function() {
    return new Promise(function(resolve) {
      if (_currentAnimation) { cancelAnimationFrame(_currentAnimation); _currentAnimation = null; }
      if (_animFrameId) { cancelAnimationFrame(_animFrameId); _animFrameId = null; }
      _isLoading = false; _isZooming = false;
      if (typeof Celestial !== 'undefined' && typeof Celestial.redraw === 'function') Celestial.redraw();
      renderMilkyWay(milkywayConfig.center, true);
      setTimeout(function() { convertCanvasToImage(); resolve(); }, 200);
    });
  };

  // ── Visibility / touch handlers (mobile) ─────────────────────────────────
  document.addEventListener('visibilitychange', function() {
    if (document.hidden && isMobileDevice()) {
      // Free memory but keep the GL context — losing it mid-session triggers
      // a cascading reinit when the user returns to the tab.
      limitCacheSize(1);
    }
  }, { passive: true });

  // ── Boot ─────────────────────────────────────────────────────────────────
  function seedDefaults() {
    // Seed an order_id synchronously so Add-to-Cart works even if the user
    // clicks before Cart.loadCartState()/Cart.initFromUrl() resolve. If the
    // URL has ?order_id= or ?saved= the cart boot overwrites this.
    if (!state.orderId) state.orderId = Cart.newOrderId();

    // Preselect the paper and ribbon colour picked on the gift wrap page.
    // The star map keeps no SkylitGifts memory of its own (a saved design's
    // choice comes back through loadGiftExtras in hydrateFromRow, which runs
    // after this and wins). Wrapping itself stays off until chosen.
    if (window.SkylitGifts && window.SkylitGifts.recallPaperFor) {
      sm2StoreSet('giftPaper', window.SkylitGifts.recallPaperFor(null, state.selectedSize) || state.giftPaper);
      sm2StoreSet('ribbonColor', window.SkylitGifts.recallRibbonFor(null) || state.ribbonColor);
    }

    // v1's calender.js selects today's date by default (selected option on the
    // matching day/month/year). The legacy-input observer in bindEvents pushes
    // that into state once the selects are populated; here we just preset a
    // plausible default so render() has a non-null value from the first frame.
    var today = new Date();
    state.date = today.getFullYear() + '-' +
      String(today.getMonth() + 1).padStart(2, '0') + '-' +
      String(today.getDate()).padStart(2, '0');
    state.time = '23:11'; // matches v1's 11:11 PM preset in myjs.js:initDateTimePickers
    state.coordsDisplay = formatCoords(state.coords.lat, state.coords.lng);
    var disp = document.getElementById('sm2-coords-display');
    if (disp) disp.textContent = state.coordsDisplay;
    // Intentionally NOT pre-filling the location input value — keeps the
    // field blank with state.location ('London, UK') as placeholder so
    // users can tap and type without first deleting the default. The disc
    // still renders against state.location for the initial view.
    var hlInput = document.getElementById('sm2-headline-input');
    if (hlInput && !hlInput.value) hlInput.value = state.headline;
    var msgInput = document.getElementById('sm2-message-input');
    if (msgInput && !msgInput.value) msgInput.value = state.customMessage;
    var locOv = document.getElementById('sm2-location-override');
    if (locOv) locOv.placeholder = state.location || '';
    var coOv = document.getElementById('sm2-coords-override');
    if (coOv) coOv.placeholder = state.coordsDisplay || '';

    // Align selectedSize with the product's actual variants. Hardcoded
    // '30x40' is fine for portrait starmap, but landscape/moon list sizes
    // as '40x30' etc. Resolve against the rendered tiles: pick the
    // current state if available, else the Liquid-marked active tile (read
    // via data-attr — Alpine hasn't booted yet), else first tile. Push the
    // resolved value through sm2StoreSet so Alpine's :class on the tiles
    // reactively reflects it once it boots.
    var tiles = document.querySelectorAll('[data-sm2-size]');
    if (tiles.length) {
      var resolved = null;
      for (var i = 0; i < tiles.length; i++) {
        if (tiles[i].getAttribute('data-sm2-size') === state.selectedSize) { resolved = state.selectedSize; break; }
      }
      if (!resolved) {
        var activeTile = document.querySelector('[data-sm2-size].active');
        resolved = (activeTile || tiles[0]).getAttribute('data-sm2-size');
      }
      sm2StoreSet('selectedSize', resolved);
    }
  }

  // Shared between bindMobilePreview (the scroll-linked collapse) and
  // bindRoomView (which knows the scene geometry the collapse has to move).
  var sm2RoomCollapse = null;     // { key, end: {k, tx, ty}, items: [[el, Ox, Oy]] }
  var sm2OnRoomLayout = null;     // set by bindMobilePreview
  var sm2GetCollapseD = null;     // set by bindMobilePreview
  var sm2RoomRelayout = null;     // set by bindRoomView
  var sm2RoomCheck = null;        // set by bindRoomView: re-map if the print moved

  function bindMobilePreview() {
    var main = document.querySelector('.sm2-main');
    var toggle = document.getElementById('sm2-preview-toggle');
    var label = document.getElementById('sm2-preview-toggle-label');
    if (!main || !toggle) return;

    var siteHeader = document.querySelector('.shopify-section-header');
    var frameWrap = document.querySelector('.sm2-frame-wrap');
    // The follow-along preview is deliberately compact: 76% leaves materially
    // more room for the controls below; scrolling back to the top restores it.
    var STUCK_FRAME_SCALE = 0.76;
    var themeColorMeta = document.querySelector('meta[name="theme-color"]');
    var originalThemeColor = themeColorMeta ? themeColorMeta.getAttribute('content') || '' : '';
    var previewMinimized = false;
    var stickyPaintQueued = false;
    var headerTrackUntil = 0;
    var expandedHeightTimer = null;
    var headerPositionCache = null;
    var animTimer = null;

    // ── Notch backdrop ────────────────────────────────────────────────────
    // Safari 26 draws only the page's normal scrolling content under the
    // status bar (fixed and sticky layers are clipped at its edge), so this
    // is in-flow content (see .sm2-notch-band in the CSS) kept parked just
    // above the viewport, under the preview. Where CSS scroll-driven
    // animations exist it rides the scroll inside the engine, in step with
    // the sticky preview; otherwise script moves it, a frame behind.
    var notchBand = document.querySelector('body > .sm2-notch-band');
    if (!notchBand) {
      notchBand = document.createElement('div');
      notchBand.className = 'sm2-notch-band';
      notchBand.setAttribute('aria-hidden', 'true');
      document.body.appendChild(notchBand);
    }
    var NOTCH_REACH = 300;   // px above the viewport top (also in the CSS keyframes)
    var NOTCH_SCROLL_LINKED = !!(window.CSS && CSS.supports && CSS.supports('animation-timeline', 'scroll()'));
    // Scroll-driven collapse (.sm2-main--sd in the CSS). Every moving part
    // of the collapse is a straight line in the scroll position, so where
    // the browser has scroll-driven animations the whole thing runs as one:
    // JS only writes each element's end state (and the scroll range) when
    // the layout changes, and the compositor does every frame. Nothing then
    // depends on the main thread keeping up with the finger; before, a busy
    // frame left the notch band's copy of the room photo a frame behind the
    // real one (a visible jitter under the status bar). Elsewhere the JS
    // path below still drives it frame by frame.
    var SCROLL_DRIVEN = NOTCH_SCROLL_LINKED && !!CSS.supports('animation-range', '0px 1px');
    try { if (localStorage.getItem('sm2sd') === 'off') SCROLL_DRIVEN = false; } catch (e) { /* A/B switch only */ }
    var sdOn = false;
    var sdRange = null;
    notchBand.classList.toggle('is-scroll-linked', NOTCH_SCROLL_LINKED);
    var lastBandY = null, lastBandH = null;

    // In Room view the band carries a copy of the room photo, laid exactly
    // over the real one, so under the notch the scene simply carries on.
    var bandImg = null, lastBandImgT = null, lastBandImgSize = null;
    function paintBandPhoto(show, mainRect, p) {
      var g = show ? sm2RoomCollapse : null;
      if (!g || !g.photo) {
        if (bandImg && bandImg.style.display !== 'none') bandImg.style.display = 'none';
        return;
      }
      if (!bandImg) {
        bandImg = document.createElement('img');
        bandImg.className = 'sm2-notch-band__img';
        bandImg.alt = '';
        bandImg.decoding = 'async';
        notchBand.appendChild(bandImg);
      }
      var ph = g.photo, e = g.end;
      if (bandImg.getAttribute('src') !== ph.src) bandImg.setAttribute('src', ph.src);
      var size = Math.round(ph.w) + 'x' + Math.round(ph.h);
      if (size !== lastBandImgSize) {
        lastBandImgSize = size;
        bandImg.style.width = ph.w + 'px';
        bandImg.style.height = ph.h + 'px';
      }
      // The scroll-driven copy is laid for a band the engine holds at exactly
      // -NOTCH_REACH; a JS-moved band is a frame behind that, so then the
      // copy is placed by JS too, in the same frame as the band.
      if (sdOn && NOTCH_SCROLL_LINKED) {
        // Its state at p = 0 and p = 1; the panel's top is stickyTop - p*D.
        var st = lastStickyTop || 0, Dc = geom ? geom.D : 0;
        var bx = mainRect.left + main.clientLeft;
        var b0 = 'translate3d(' + (bx + ph.left).toFixed(2) + 'px,' + (st + main.clientTop + ph.top + NOTCH_REACH).toFixed(2) + 'px,0) scale(1)';
        var b1 = 'translate3d(' + (bx + e.k * ph.left + e.tx).toFixed(2) + 'px,' + (st - Dc + main.clientTop + e.k * ph.top + e.ty + NOTCH_REACH).toFixed(2) + 'px,0) scale(' + (+e.k.toFixed(5)) + ')';
        var sdKey = b0 + b1 + sdRange;
        if (sdKey !== lastBandImgT) {
          lastBandImgT = sdKey;
          // New keyframes need a new animation on iOS (see applyCollapse).
          if (bandImg.hasAttribute('data-sm2-collapse')) {
            bandImg.removeAttribute('data-sm2-collapse');
            getComputedStyle(bandImg).animationName;
          }
          bandImg.style.transform = '';
          bandImg.style.setProperty('--sm2-b0', b0);
          bandImg.style.setProperty('--sm2-b1', b1);
          if (sdRange) {
            bandImg.style.setProperty('--sm2-c-start', sdRange[0] + 'px');
            bandImg.style.setProperty('--sm2-c-end', sdRange[1] + 'px');
          }
          bandImg.setAttribute('data-sm2-collapse', 'band');
        }
        if (bandImg.style.display === 'none') bandImg.style.display = '';
        return;
      }
      if (bandImg.hasAttribute('data-sm2-collapse')) { bandImg.removeAttribute('data-sm2-collapse'); lastBandImgT = null; }
      // Same map applyCollapse gives the real photo (P -> kP + p*t in the
      // panel's padding box), then into the band's coordinates. Once pinned
      // none of this changes, so while scrolling the copy holds still.
      var k = 1 + p * (e.k - 1);
      var bandTop = NOTCH_SCROLL_LINKED ? -NOTCH_REACH
        : lastBandY - (window.pageYOffset || document.documentElement.scrollTop || 0);
      var x = mainRect.left + main.clientLeft + k * ph.left + p * e.tx;
      var y = mainRect.top + main.clientTop + k * ph.top + p * e.ty - bandTop;
      var t = 'translate3d(' + x.toFixed(2) + 'px,' + y.toFixed(2) + 'px,0) scale(' + (+k.toFixed(5)) + ')';
      if (t !== lastBandImgT) { lastBandImgT = t; bandImg.style.transform = t; }
      if (bandImg.style.display === 'none') bandImg.style.display = '';
    }

    // iOS 26 Safari can leave the scroll-linked animation behind when the
    // page shortens under the reader (e.g. "Show fewer themes" clamps the
    // scroll): the band then sits hundreds of px too low and its room photo
    // shows over the sidebar. The band is already laid out here (mainRect was
    // just read), so checking it is cheap: restart the animation once, and if
    // it is still off, follow the scroll from JS for the rest of the visit.
    // Armed only once the band has been seen in place: on the first frames
    // the animation has not applied yet (transform none), which is not drift.
    // Mid-scroll the main thread's reading of a scroll-linked transform is
    // whole frames behind the real scroll (measured hundreds of px in a
    // fling on iPhone), so one off reading proves nothing: judged on that,
    // the band was dropped to JS on the first fling of every visit, and its
    // photo copy (still on the compositor, laid for a band exactly at
    // -NOTCH_REACH) then jumped by the JS lag on every frame. Re-read only
    // once the scroll has held still.
    var bandSeenInPlace = false, bandCheckTimer = null;
    function bandOff() {
      return Math.abs(notchBand.getBoundingClientRect().top + NOTCH_REACH) > 3;
    }
    function checkBandTimeline() {
      if (!NOTCH_SCROLL_LINKED || !notchBand.getClientRects().length) return;   // hidden (landscape)
      if (!bandOff()) { bandSeenInPlace = true; return; }
      if (!bandSeenInPlace || bandCheckTimer) return;
      var sy0 = window.pageYOffset || document.documentElement.scrollTop || 0;
      bandCheckTimer = window.setTimeout(function () {
        bandCheckTimer = null;
        var sy1 = window.pageYOffset || document.documentElement.scrollTop || 0;
        if (sy1 !== sy0 || !NOTCH_SCROLL_LINKED || !bandOff()) return;
        notchBand.style.animation = 'none';
        void notchBand.offsetWidth;
        notchBand.style.animation = '';
        if (!bandOff()) return;
        NOTCH_SCROLL_LINKED = false;
        notchBand.classList.remove('is-scroll-linked');
        lastBandY = null;
        lastBandImgT = null;
        queueStickyPaint(false, true);
      }, 150);
    }

    function paintNotchBand(minimized, mainRect, p) {
      checkBandTimeline();
      // Reach below the viewport edge, hidden behind the preview; never past
      // its bottom (minimised it is ~49px; at the end of the page the preview
      // scrolls away and the band must not be left showing over the footer).
      var below = Math.min(minimized ? 40 : 200, Math.round(mainRect.bottom) - (minimized ? 9 : 80));
      var h = Math.max(0, NOTCH_REACH + below);
      if (h !== lastBandH) { lastBandH = h; notchBand.style.height = h + 'px'; }
      if (!NOTCH_SCROLL_LINKED) {
        var y = Math.round((window.pageYOffset || document.documentElement.scrollTop || 0) - NOTCH_REACH);
        if (y !== lastBandY) { lastBandY = y; notchBand.style.transform = 'translate3d(0,' + y + 'px,0)'; }
      }
      var room = main.classList.contains('sm2-main--room') && !minimized && !main.classList.contains('sm-gallery-viewing');
      if (notchBand.classList.contains('is-room') !== room) notchBand.classList.toggle('is-room', room);
      paintBandPhoto(room, mainRect, p);
    }

    // The theme's <sticky-header> keeps a scroll listener that toggles
    // -sticky/-hidden/animate on the header. We pin the header instead (see the
    // inline style in starmap-v2.liquid), so that listener only adds scroll-time
    // work and class churn. Swapping the element for a plain div fires its
    // disconnectedCallback, which tears the listener down for good.
    var stickyHeaderEl = document.querySelector('sticky-header');
    if (stickyHeaderEl) {
      var plain = document.createElement('div');
      plain.className = stickyHeaderEl.className;
      for (var i = 0; i < stickyHeaderEl.attributes.length; i++) {
        var attr = stickyHeaderEl.attributes[i];
        if (attr.name !== 'class') plain.setAttribute(attr.name, attr.value);
      }
      while (stickyHeaderEl.firstChild) plain.appendChild(stickyHeaderEl.firstChild);
      stickyHeaderEl.replaceWith(plain);
    }
    if (siteHeader) {
      siteHeader.classList.remove(
        'shopify-section-header-sticky',
        'shopify-section-header-hidden',
        'animate'
      );
      document.body.classList.remove(
        'shopify-body-section-header-sticky',
        'shopify-body-section-header-hidden'
      );
    }

    function paintToggle(minimized) {
      if (expandedHeightTimer) {
        window.clearTimeout(expandedHeightTimer);
        expandedHeightTimer = null;
      }
      main.classList.toggle('sm2-main--minimized', minimized);
      toggle.setAttribute('aria-expanded', minimized ? 'false' : 'true');
      toggle.setAttribute('aria-label', minimized ? 'Show preview' : 'Minimise preview');
      if (label) label.textContent = minimized ? 'Show preview' : 'Minimise';
      if (!minimized && main.style.getPropertyValue('--sm2-preview-expanded-height')) {
        expandedHeightTimer = window.setTimeout(function () {
          main.style.removeProperty('--sm2-preview-expanded-height');
          expandedHeightTimer = null;
        }, 340);
      }
    }

    function queueStickyPaint(trackHeader, force) {
      if (trackHeader) headerTrackUntil = Date.now() + 450;
      if (force) forceNextApply = true;
      if (stickyPaintQueued) return;
      stickyPaintQueued = true;
      window.requestAnimationFrame(paintStickyState);
    }

    // Last values written to the CSS custom properties. Writing a custom property
    // on an element invalidates style for it and its subtree (and on
    // documentElement, for the whole document) whether or not the value actually
    // changed — at 60fps during a scroll that is the difference between a smooth
    // resize and the sequence of stutters this panel had. Only write on change.
    var lastStickyTop = null;
    var lastCollapseD = null;
    var lastFrameBottom = null;

    // ── Scroll-linked collapse (phones, stacked layout) ──────────────────
    // The panel used to flip between two layouts 8px into the scroll: 133px
    // shorter, print at 76%, Room view re-laid out a frame later. Everything
    // below jumped up at once. Now the panel keeps ONE layout and sticks
    // --sm2-collapse px above the viewport (see .sm2-main in the CSS), so for
    // the first D px of scroll it slides under the top edge with the page, and
    // the print (or the whole room scene) is scaled into the part still
    // showing. Only `translate`/`scale` change per frame: no layout, and the
    // controls below move exactly with the finger.
    // Stuck-state paddings the compact preview is built around: 0.6rem above
    // the print, 0.6rem + the 16px sheet lip below it.
    var STUCK_PAD_TOP = 6;
    var STUCK_PAD_BOTTOM = 22;
    var stackedMq = window.matchMedia ? window.matchMedia('(max-width: 900px)') : null;
    var shortLandscapeMq = window.matchMedia ? window.matchMedia('(orientation: landscape) and (max-height: 500px)') : null;
    var geom = null;          // flat-layout measurements, see measureCollapse()
    var geomDirty = true;
    var lastP = -1;
    var lastRoomKey = null;
    var willChangeTimer = null;
    var collapseEls = null;

    function kids(sel) {
      return Array.prototype.filter.call(main.children, function (el) { return el.matches(sel); });
    }
    function collectEls() {
      collapseEls = {
        // Pinned to the panel's top edge: ride down with the visible top.
        top: kids('.sm2-chrome-btn'),
        // Centred on the panel: follow the centre of what is visible.
        middle: kids('.sm-gallery'),
        // Sit on the print's bottom edge (--sm2-frame-bottom).
        bottom: kids('.sm2-toolbar, .sm2-room-fab, .sm2-expand-fab, .sm2-toolbar-fab, .sm-gallery-mobile-toggle, .sm2-preview-toggle'),
        count: main.childElementCount
      };
    }

    function measureCollapse() {
      geomDirty = false;
      if (!frameWrap || !frameWrap.offsetHeight) return;
      var H = main.offsetHeight;
      var fw = frameWrap.offsetWidth, fh = frameWrap.offsetHeight;
      var ox = frameWrap.offsetLeft, oy = frameWrap.offsetTop;
      var k = STUCK_FRAME_SCALE;
      var D = Math.max(0, Math.round(H - STUCK_PAD_TOP - k * fh - STUCK_PAD_BOTTOM));
      // Flat end state as a similarity P -> kP + t in panel coordinates: the
      // print at 76%, centred, its top STUCK_PAD_TOP below the visible top (D).
      var Ox = ox + fw / 2, Oy = oy + fh / 2;   // .sm2-frame-wrap scales about its centre
      geom = {
        H: H,
        D: D,
        flat: { k: k, tx: (1 - k) * Ox, ty: D + STUCK_PAD_TOP + k * fh / 2 - k * Oy },
        flatOrigin: [Ox, Oy],
        bottom0: Math.max(6, Math.round(H - (oy + fh))),
        bottom1: Math.max(6, Math.round(H - (D + STUCK_PAD_TOP + k * fh)))
      };
    }

    function setMotion(el, tx, ty, k) {
      var t = (Math.abs(tx) < 0.01 && Math.abs(ty) < 0.01) ? '' : (tx.toFixed(2) + 'px ' + ty.toFixed(2) + 'px');
      if (el.style.translate !== t) el.style.translate = t;
      if (k !== undefined) {
        var sc = Math.abs(k - 1) < 0.0001 ? '' : String(+k.toFixed(5));
        if (el.style.scale !== sc) el.style.scale = sc;
      }
    }

    // Where each moving element is at collapse progress p, as [el, tx, ty, k].
    // All of it is linear in p (so in the scroll position).
    function collapseTargets(p, roomGeom) {
      var out = [];
      var D = geom ? geom.D : 0;
      // The picture: the print alone, or in Room view the scene, its light map
      // and the print lying in it, moved as one rigid piece so the print stays
      // on the poster. Each element has its own transform-origin O, so the
      // shared map P -> kP + t becomes translate = t - (1 - k)O per element.
      var end = roomGeom ? roomGeom.end : (geom ? geom.flat : null);
      var items = roomGeom ? roomGeom.items : (geom && frameWrap ? [[frameWrap, geom.flatOrigin[0], geom.flatOrigin[1]]] : []);
      if (end) {
        var k = 1 + p * (end.k - 1);
        items.forEach(function (it) {
          out.push([it[0], p * end.tx - (1 - k) * it[1], p * end.ty - (1 - k) * it[2], k]);
        });
      }
      collapseEls.top.forEach(function (el) { out.push([el, 0, p * D]); });
      var peek = document.getElementById('sm2-gift-peek-btn');
      if (peek) out.push([peek, 0, peek.parentNode === main ? p * D : 0]);
      collapseEls.middle.forEach(function (el) { out.push([el, 0, p * D / 2]); });
      var drop = (!roomGeom && geom) ? p * (geom.bottom0 - geom.bottom1) : 0;
      collapseEls.bottom.forEach(function (el) { out.push([el, 0, drop]); });
      return out;
    }

    var sdEls = [];
    var lastSdKey = null;
    function applyCollapse(p, force) {
      var roomGeom = main.classList.contains('sm2-main--room') ? sm2RoomCollapse : null;
      var roomKey = roomGeom ? roomGeom.key : 'flat';
      // starmap-gallery.js adds its toggle after this binds: re-collect when
      // the panel's children change.
      if (!collapseEls || collapseEls.count !== main.childElementCount) collectEls();

      if (sdOn) {
        // Scroll-driven: write each element's end state, only when it changed.
        var ends = collapseTargets(1, roomGeom);
        var key = roomKey + '|' + ends.map(function (t) { return [t[1], t[2], t[3] || 1].map(function (n) { return n.toFixed(2); }).join(','); }).join(';') + '|' + sdRange;
        if (!force && key === lastSdKey) return;
        lastSdKey = key;
        lastP = -1;
        var now = [], restart = [], start = [];
        ends.forEach(function (t) {
          var el = t[0];
          now.push(el);
          var tx = t[1].toFixed(2) + 'px', ty = t[2].toFixed(2) + 'px';
          var kk = String(+(t[3] === undefined ? 1 : t[3]).toFixed(5));
          var elKey = roomKey + '|' + tx + ',' + ty + ',' + kk;
          // An element already animating to this end state is left alone:
          // on iPhone, re-setting even identical --sm2-c-* values or the
          // attribute on a running scroll-driven animation makes Safari
          // re-commit it, and that frame paints at the main thread's stale
          // scroll position (measured 100s of px behind mid-fling). Forced
          // passes come with every toolbar show/hide, so the photo jumped
          // whenever a scroll began.
          if (el.hasAttribute('data-sm2-collapse') && el.sm2CollapseKey === elKey) return;
          // iOS Safari runs these on the compositor and keeps the keyframes
          // (and transform-origin) it started with: new end values under a
          // running animation are ignored. Room -> flat -> room left the
          // print collapsing with the flat end state and sliding off the
          // poster. So an element whose end state moves gets a new animation.
          if (el.hasAttribute('data-sm2-collapse')) {
            el.removeAttribute('data-sm2-collapse');
            restart.push(el);
          }
          setMotion(el, 0, 0, 1);   // no inline leftovers under the animation
          el.style.willChange = '';
          el.sm2CollapseKey = elKey;
          el.style.setProperty('--sm2-c-tx', tx);
          el.style.setProperty('--sm2-c-ty', ty);
          el.style.setProperty('--sm2-c-k', kk);
          start.push(el);
        });
        if (restart.length) getComputedStyle(restart[0]).animationName;   // flush: cancel the old ones
        start.forEach(function (el) { el.setAttribute('data-sm2-collapse', ''); });
        sdEls.forEach(function (el) { if (now.indexOf(el) === -1) el.removeAttribute('data-sm2-collapse'); });
        sdEls = now;
        return;
      }
      if (sdEls.length) {
        sdEls.forEach(function (el) { el.removeAttribute('data-sm2-collapse'); });
        sdEls = [];
        lastSdKey = null;
        force = true;
      }

      if (!force && Math.abs(p - lastP) < 0.0005 && roomKey === lastRoomKey) return;
      lastP = p;
      lastRoomKey = roomKey;
      if (!roomGeom && sm2RoomCollapse) {
        // Room view just switched off: leave nothing on the scene layers.
        sm2RoomCollapse.items.forEach(function (it) { if (it[0] !== frameWrap) setMotion(it[0], 0, 0, 1); });
      }
      var targets = collapseTargets(p, roomGeom);
      targets.forEach(function (t) { setMotion(t[0], t[1], t[2], t[3]); });

      // A composited layer for the picture while it moves, dropped 180ms after
      // it stops (end of the range, or the finger resting mid-way) so the
      // browser re-rasters it sharp at the scale it settled on.
      var items = roomGeom ? roomGeom.items : (frameWrap ? [[frameWrap]] : []);
      items.forEach(function (it) { if (it[0].style.willChange !== 'transform') it[0].style.willChange = 'transform'; });
      if (willChangeTimer) window.clearTimeout(willChangeTimer);
      willChangeTimer = window.setTimeout(function () {
        willChangeTimer = null;
        [frameWrap].concat(sm2RoomCollapse ? sm2RoomCollapse.items.map(function (it) { return it[0]; }) : [])
          .forEach(function (el) { if (el) el.style.willChange = ''; });
      }, 180);
    }
    // Room view re-lays its scene out (resize, finish, size change); re-apply.
    sm2OnRoomLayout = function () { queueStickyPaint(false, true); };
    sm2GetCollapseD = function () { return geom ? geom.D : 0; };

    var forceNextApply = false;
    function paintStickyState() {
      stickyPaintQueued = false;
      var mobile = window.innerWidth <= 900;
      var stacked = !!(stackedMq && stackedMq.matches && !(shortLandscapeMq && shortLandscapeMq.matches));
      var headerBottom = 0;
      if (mobile && siteHeader) {
        // getComputedStyle forces a style recalc; the header is pinned to
        // position:static by this section's inline CSS and never moves, so read
        // it once per scroll burst rather than per frame.
        if (headerPositionCache === null) {
          headerPositionCache = window.getComputedStyle(siteHeader).position;
        }
        if (headerPositionCache !== 'static') {
          headerBottom = Math.max(0, Math.ceil(siteHeader.getBoundingClientRect().bottom));
        }
      }
      if (headerBottom !== lastStickyTop) {
        lastStickyTop = headerBottom;
        document.documentElement.style.setProperty('--sm2-sticky-top', headerBottom + 'px');
      }
      var stickyTop = headerBottom;
      var minimized = main.classList.contains('sm2-main--minimized');
      var viewing = main.classList.contains('sm-gallery-viewing');
      var animating = main.classList.contains('sm2-main--anim');

      // Measure the one layout. Only while it is the real, full panel: not
      // minimised, not mid-toggle, and with the print on show (the gallery
      // viewer display:none's it). Otherwise keep the last good numbers.
      var geomChanged = false;
      if (geomDirty && stacked && !minimized && !viewing && !animating) {
        var before = geom ? geom.D + ',' + geom.bottom0 + ',' + geom.H : '';
        measureCollapse();
        geomChanged = (geom ? geom.D + ',' + geom.bottom0 + ',' + geom.H : '') !== before;
      }
      var D = (stacked && geom) ? geom.D : 0;
      if (D !== lastCollapseD) {
        lastCollapseD = D;
        main.style.setProperty('--sm2-collapse', D + 'px');
        // The room scene's end state depends on D.
        if (sm2RoomRelayout) sm2RoomRelayout();
      }
      var bottom0 = (stacked && geom) ? geom.bottom0 : null;
      var frameBottom = main.classList.contains('sm2-main--room') ? 24 : bottom0;
      if (frameBottom !== null && frameBottom !== lastFrameBottom) {
        lastFrameBottom = frameBottom;
        main.style.setProperty('--sm2-frame-bottom', frameBottom + 'px');
      }

      var mainRect = main.getBoundingClientRect();
      if (sm2RoomCheck) sm2RoomCheck();
      var p = 0;
      if (stacked && !minimized && !viewing && D > 0) {
        p = Math.min(1, Math.max(0, (stickyTop - mainRect.top) / D));
      }
      var wasStuck = main.classList.contains('sm2-main--stuck');
      var stuck;
      if (minimized) {
        // Minimised it pins at the top edge; it is "stuck" once the page has
        // scrolled it there. Hysteresis stops a flicker on the exact line.
        var scrolled = (window.pageYOffset || document.documentElement.scrollTop || 0) > (wasStuck ? 0 : 8);
        stuck = mobile && scrolled && mainRect.top <= stickyTop + (wasStuck ? 8 : 1);
      } else {
        stuck = p >= (wasStuck ? 0.9 : 0.98);
      }
      if (stuck !== wasStuck) main.classList.toggle('sm2-main--stuck', stuck);

      // Scroll-driven collapse: on while this is the real, collapsing panel
      // and the header can't move (the sticky offset is then a constant).
      var sdForce = false;
      var wantSd = SCROLL_DRIVEN && stacked && !minimized && !viewing && !animating && D > 0 &&
        (!mobile || !siteHeader || headerPositionCache === 'static');
      if (wantSd) {
        // p = 0 at scrollY = (the panel's page top) - stickyTop, 1 at + D.
        // While not pinned the panel sits at its page position, so read it
        // exactly; pinned on arrival, estimate it from the wrapper once.
        var docTop = null;
        var sy = window.pageYOffset || document.documentElement.scrollTop || 0;
        if (p < 1) docTop = mainRect.top + sy;
        else if (!sdRange && main.parentElement) {
          var par = main.parentElement;
          docTop = par.getBoundingClientRect().top + sy + (parseFloat(getComputedStyle(par).paddingTop) || 0);
        }
        if (docTop !== null) {
          var r0 = Math.round((docTop - stickyTop) * 100) / 100;
          if (!sdRange || Math.abs(sdRange[0] - r0) > 0.5 || sdRange[1] !== r0 + D) {
            sdRange = [r0, r0 + D];
            main.style.setProperty('--sm2-c-start', sdRange[0] + 'px');
            main.style.setProperty('--sm2-c-end', sdRange[1] + 'px');
            sdForce = true;
          }
        } else if (sdRange && sdRange[1] !== sdRange[0] + D) {
          // Pinned, but the panel was re-measured (a reload restores the
          // scroll before the layout settles: D grew 99 -> 105px). The start
          // can't be read while pinned; the end must follow D, or the picture
          // finishes collapsing before the panel does.
          sdRange = [sdRange[0], sdRange[0] + D];
          main.style.setProperty('--sm2-c-end', sdRange[1] + 'px');
          sdForce = true;
        }
        if (!sdRange) wantSd = false;
      }
      if (wantSd !== sdOn) {
        sdOn = wantSd;
        main.classList.toggle('sm2-main--sd', sdOn);
        sdForce = true;
      }
      paintNotchBand(minimized, mainRect, p);

      var force = forceNextApply || geomChanged || sdForce;
      forceNextApply = false;
      applyCollapse(p, force);

      if (!stuck && !previewCollapsedByUs && minimized) {
        previewMinimized = false;
        paintToggle(false);
      }
      // Only worth re-painting across a scroll burst if the header can actually
      // move under us, or while a minimise/expand is animating the panel's top.
      if ((headerPositionCache && headerPositionCache !== 'static' && Date.now() < headerTrackUntil) || animating) {
        queueStickyPaint(false);
      }
    }

    // Run the customer-initiated toggles with transitions on, then take them off
    // again so no scroll-driven change can inherit them.
    // Scroll anchoring is off for the length of the toggle: Chromium would
    // otherwise hold the controls still by scrolling the page up as the panel
    // shrinks, landing at the top, which reads as "back at the top" and
    // expands the preview again at once. (WebKit has no scroll anchoring.)
    var rootStyle = document.documentElement.style;
    function withAnim(fn) {
      if (animTimer) window.clearTimeout(animTimer);
      main.classList.add('sm2-main--anim');
      rootStyle.overflowAnchor = 'none';
      fn();
      queueStickyPaint(false);   // repaints every frame while --anim is on
      animTimer = window.setTimeout(function () {
        animTimer = null;
        rootStyle.overflowAnchor = '';
        main.classList.remove('sm2-main--anim');
        geomDirty = true;
        queueStickyPaint(false, true);
      }, 320);
    }

    paintToggle(false);

    // Collapse/restore the panel from outside the customer's own toggle — the
    // location autocomplete does this while it holds the body scroll-lock (see
    // _pacLock). Instant, not animated: the caller measures the collapsed height
    // straight after, and transitions are opt-in. If the customer had already
    // minimised it themselves, leave their choice alone in both directions.
    var previewCollapsedByUs = false;
    window.sm2SetPreviewCollapsed = function (collapsed) {
      if (window.innerWidth > 900) return;
      if (collapsed) {
        if (previewCollapsedByUs || main.classList.contains('sm2-main--minimized')) return;
        previewCollapsedByUs = true;
      } else {
        if (!previewCollapsedByUs) return;
        previewCollapsedByUs = false;
      }
      paintToggle(collapsed);
      main.getBoundingClientRect();
      if (!collapsed) queueStickyPaint(false);
    };

    toggle.addEventListener('click', function () {
      previewMinimized = !main.classList.contains('sm2-main--minimized');
      withAnim(function () {
        if (previewMinimized) {
          main.style.setProperty('--sm2-preview-expanded-height', main.getBoundingClientRect().height + 'px');
          // Lock the expanded pixel height before toggling the class so WebKit
          // can interpolate from it rather than snapping between auto heights.
          main.getBoundingClientRect();
        }
        paintToggle(previewMinimized);
      });
      queueStickyPaint(false);
    });
    window.addEventListener('scroll', function () { queueStickyPaint(true); }, { passive: true });
    window.addEventListener('resize', function () {
      headerPositionCache = null;
      // Keep lastStickyTop / lastFrameBottom: the next paint re-measures and
      // compares. Clearing them rewrote --sm2-sticky-top on <html> with the
      // same value on every resize, and iOS fires one per toolbar show/hide
      // mid-scroll; a root custom-property write restyles the whole document
      // (~350ms here), which held the scroll-linked notch band back.
      geomDirty = true;
      queueStickyPaint(true, true);
    });
    // A new size / finish / composition resizes the print; minimise resizes the
    // panel. Transforms don't fire this, so the collapse itself never does.
    if (window.ResizeObserver) {
      var collapseRo = new ResizeObserver(function () {
        geomDirty = true;
        queueStickyPaint(false, true);
      });
      collapseRo.observe(main);
      if (frameWrap) collapseRo.observe(frameWrap);
    }
    if (siteHeader) {
      siteHeader.addEventListener('transitionrun', function () { queueStickyPaint(true); });
      siteHeader.addEventListener('transitionend', function () { queueStickyPaint(true); });
    }

    // starmap-gallery.js adds/removes .sm-gallery-viewing on this same element.
    // Re-measure the moment the frame comes back, rather than waiting for the
    // next scroll to put the chevron where it belongs.
    if (window.MutationObserver) {
      var galleryViewing = main.classList.contains('sm-gallery-viewing');
      var roomOn = main.classList.contains('sm2-main--room');
      new MutationObserver(function () {
        var nowViewing = main.classList.contains('sm-gallery-viewing');
        var nowRoom = main.classList.contains('sm2-main--room');
        if (nowViewing === galleryViewing && nowRoom === roomOn) return;
        galleryViewing = nowViewing;
        roomOn = nowRoom;
        geomDirty = true;
        queueStickyPaint(false, true);
      }).observe(main, { attributes: true, attributeFilter: ['class'] });
    }

    queueStickyPaint(true);
  }

  // Mobile: the helper toolbar flies out of a bottom-right button instead of
  // living permanently in the preview's top-right, which is the gift-peek eye's
  // corner. Desktop keeps the always-on toolbar (CSS hides the button ≥901px),
  // so everything here is a no-op there.
  function bindToolbarFab() {
    var main = document.querySelector('.sm2-main');
    var fab = document.getElementById('sm2-toolbar-fab');
    var toolbar = document.getElementById('sm2-toolbar');
    if (!main || !fab || !toolbar) return;

    function setOpen(open) {
      main.classList.toggle('sm2-main--tools-open', open);
      fab.setAttribute('aria-expanded', open ? 'true' : 'false');
      fab.setAttribute('aria-label', open ? 'Hide tools' : 'Show tools');
      if (open) {
        document.addEventListener('pointerdown', onOutside, true);
        document.addEventListener('keydown', onKey);
      } else {
        document.removeEventListener('pointerdown', onOutside, true);
        document.removeEventListener('keydown', onKey);
      }
    }
    function isOpen() { return main.classList.contains('sm2-main--tools-open'); }
    function onOutside(e) {
      if (fab.contains(e.target) || toolbar.contains(e.target)) return;
      setOpen(false);
    }
    function onKey(e) {
      if (e.key !== 'Escape') return;
      setOpen(false);
      fab.focus();
    }

    fab.addEventListener('click', function () { setOpen(!isOpen()); });

    // Info and Save open a modal, so the flyout has served its purpose. Text and
    // Font cycle through options — those are tapped repeatedly, so leave the
    // flyout up rather than making the customer reopen it for every step.
    ['sm2-toolbar-info', 'sm2-toolbar-save'].forEach(function (id) {
      var btn = document.getElementById(id);
      if (btn) btn.addEventListener('click', function () { setOpen(false); });
    });

    // The flyout is anchored to the print's bottom edge, which the gallery
    // viewer and the minimised bar both take away.
    if (window.MutationObserver) {
      new MutationObserver(function () {
        if (!isOpen()) return;
        if (main.classList.contains('sm-gallery-viewing') || main.classList.contains('sm2-main--minimized')) {
          setOpen(false);
        }
      }).observe(main, { attributes: true, attributeFilter: ['class'] });
    }
  }

  // ── Step tabs: sliding indicator ────────────────────────────────────────
  // One pill slides between 1 Design / 2 Moment / 3 Message / 4 Finish instead
  // of each tab recolouring (shared with the other customisers: skylit-motion.js).
  function bindStepIndicator() {
    if (!window.SkylitTabPill) return;
    window.SkylitTabPill.attach(document.getElementById('sm2-steps-nav'), {
      tab: '.sm2-step-tab',
      className: 'sm2-steps-indicator'
    });
  }

  // ── Add-to-cart button morph ────────────────────────────────────────────
  // Shared with the moons (skylit-motion.js): pill -> spinner -> tick ->
  // "Added". The stand-in keeps the plain text path if that file is missing.
  var atcMorph = window.SkylitAtcMorph || {
    start: function () { return false; }, success: function () {}, reset: function () {},
    settle: function () { return Promise.resolve(); }
  };

  // ── Add-to-cart star trail ──────────────────────────────────────────────
  // A comet flies from the button into the basket icon while the cart
  // requests run (assets/skylit-star-trail.js). The redirect to the cart
  // waits for it to land, so the page never cuts it off mid-flight; a failed
  // add cancels it before the pop. Without the script (or with reduced
  // motion) goToCart() navigates straight away, as before.
  var _cartFlight = null;
  function launchCartFlight(fromEl) {
    _cartFlight = null;
    if (!window.SkylitStarTrail) return;
    _cartFlight = window.SkylitStarTrail.launch(fromEl, [
      document.getElementById('sm2-cart-btn'),       // mobile: bag on the preview
      document.getElementById('cart-icon-bubble')    // desktop: header bag
    ], {
      onLand: function () {
        // Edit mode replaces the design's existing cart line rather than
        // adding one, so the count stays put (the bag still pops).
        if (state.editMode) return;
        var badge = document.getElementById('sm2-cart-btn-count');
        if (!badge) return;
        var n = (parseInt(badge.textContent, 10) || 0) + 1;
        badge.textContent = n > 99 ? '99+' : String(n);
        badge.hidden = false;
      }
    });
  }
  function goToCart() {
    var url = window.SkylitGalleryWall ? window.SkylitGalleryWall.destination() : '/cart';
    atcMorph.success(state.editMode ? 'Updated' : 'Added');
    var hold = Promise.all([_cartFlight ? _cartFlight.land() : Promise.resolve(), atcMorph.settle()]);
    hold.then(function () { window.location.href = url; }, function () { window.location.href = url; });
  }

  // ── Room view ─────────────────────────────────────────────────────────
  // Lays the live print onto the blank poster in a photographed scene
  // (#sm2-room-scenes in the section: image, poster corners, months). The
  // print is the real .sm2-frame-wrap, transformed with a matrix3d, so every
  // edit shows up in the room immediately and the text stays exact.
  //
  // Geometry: the poster is a 5:7 sheet. The print keeps its own aspect and
  // is sized to COVER the sheet (a 30x40 overhangs a 5:7 sheet by ~5% on the
  // sides), so no blank paper ever peeks out and nothing is stretched.
  var SHEET_ASPECT = 5 / 7;

  function homography(src, dst) {
    // Solve the 8 unknowns of H (h33 = 1) mapping 4 src points onto 4 dst.
    var A = [], b = [];
    for (var i = 0; i < 4; i++) {
      var x = src[i][0], y = src[i][1], u = dst[i][0], v = dst[i][1];
      A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]); b.push(u);
      A.push([0, 0, 0, x, y, 1, -v * x, -v * y]); b.push(v);
    }
    for (var c = 0; c < 8; c++) {
      var piv = c;
      for (var r = c + 1; r < 8; r++) if (Math.abs(A[r][c]) > Math.abs(A[piv][c])) piv = r;
      var tA = A[c]; A[c] = A[piv]; A[piv] = tA;
      var tb = b[c]; b[c] = b[piv]; b[piv] = tb;
      for (var r2 = c + 1; r2 < 8; r2++) {
        var f = A[r2][c] / A[c][c];
        for (var k = c; k < 8; k++) A[r2][k] -= f * A[c][k];
        b[r2] -= f * b[c];
      }
    }
    var h = new Array(8);
    for (var r3 = 7; r3 >= 0; r3--) {
      var sum = b[r3];
      for (var k2 = r3 + 1; k2 < 8; k2++) sum -= A[r3][k2] * h[k2];
      h[r3] = sum / A[r3][r3];
    }
    return [[h[0], h[1], h[2]], [h[3], h[4], h[5]], [h[6], h[7], 1]];
  }
  function applyH(H, x, y) {
    var w = H[2][0] * x + H[2][1] * y + H[2][2];
    return [(H[0][0] * x + H[0][1] * y + H[0][2]) / w, (H[1][0] * x + H[1][1] * y + H[1][2]) / w];
  }
  function matrix3d(H) {
    return 'matrix3d(' + [
      H[0][0], H[1][0], 0, H[2][0],
      H[0][1], H[1][1], 0, H[2][1],
      0, 0, 1, 0,
      H[0][2], H[1][2], 0, H[2][2]
    ].map(function (n) { return +n.toFixed(8); }).join(',') + ')';
  }

  function bindRoomView() {
    var main = document.querySelector('.sm2-main');
    var wrap = document.getElementById('sm2-frame-wrap');
    var bg = document.getElementById('sm2-room-bg');
    var light = document.getElementById('sm2-room-light');
    var dataEl = document.getElementById('sm2-room-scenes');
    var toggles = document.querySelectorAll('[data-sm2-room-toggle]');
    if (!main || !wrap || !bg || !light || !dataEl || !toggles.length) return;
    var scenes;
    try { scenes = JSON.parse(dataEl.textContent); } catch (e) { return; }
    if (!scenes || !scenes.length) return;

    // ?room=<id> previews a scene; otherwise the one in season.
    var scene = null;
    try {
      var want = new URLSearchParams(window.location.search).get('room');
      if (want) scene = scenes.filter(function (s) { return s.id === want; })[0] || null;
    } catch (e) { /* noop */ }
    if (!scene) {
      var month = new Date().getMonth() + 1;
      scene = scenes.filter(function (s) { return (s.months || []).indexOf(month) !== -1; })[0] || scenes[0];
    }

    var on = false, raf = 0;
    var roomEl = document.getElementById('sm2-room') || bg.parentNode;
    var backdrop = null;

    // Which photo of the scene is showing: the portrait original, or its wide
    // landscape outpaint (scene.wide) on desktop-shaped panels. Picked by
    // whichever aspect is closer to the panel's. A change waits until the new
    // photo is decoded, then swaps and re-lays out in the same frame, so the
    // old photo is never drawn with the new geometry.
    var view = scene, viewSwitching = false;
    function pickView(W, H) {
      if (!scene.wide || !W || !H) return scene;
      var a = W / H;
      return Math.abs(Math.log(a * scene.wide.h / scene.wide.w)) < Math.abs(Math.log(a * scene.h / scene.w)) ? scene.wide : scene;
    }
    function useView(v) {
      view = v;
      bg.src = v.src;
      light.src = v.light || v.src;
    }

    // Scale and place the scene for a W x H panel. Cover the panel, centred
    // on the poster, never showing past the photo's edges; but never let the
    // poster grow past POSTER_MAX of the panel height. The photos are
    // portrait, so on a wide desktop panel "cover" alone blew the print up
    // past the panel and cropped it. When the cap wins, the photo stops short
    // of the panel on that axis and stays centred on the poster.
    var POSTER_MAX = 0.8;
    function roomFit(W, H) {
      var q = view.quad;
      var qTop = Math.min(q[0][1], q[1][1], q[2][1], q[3][1]);
      var qBot = Math.max(q[0][1], q[1][1], q[2][1], q[3][1]);
      var qcx = (q[0][0] + q[1][0] + q[2][0] + q[3][0]) / 4;
      var qcy = (q[0][1] + q[1][1] + q[2][1] + q[3][1]) / 4;
      var sCover = Math.max(W / view.w, H / view.h);
      var sCap = POSTER_MAX * H / Math.max(1, qBot - qTop);
      var sc = Math.min(sCover, sCap);
      var iw = view.w * sc, ih = view.h * sc;
      var left = iw >= W ? Math.min(0, Math.max(W - iw, W / 2 - qcx * sc)) : W / 2 - qcx * sc;
      var top = ih >= H ? Math.min(0, Math.max(H - ih, H / 2 - qcy * sc)) : H / 2 - qcy * sc;
      return { s: sc, left: left, top: top };
    }

    function schedule() { if (on && !raf) raf = requestAnimationFrame(layout); }

    function layout() {
      raf = 0;
      if (!on) return;
      var W = main.clientWidth, Hc = main.clientHeight;
      var fw = wrap.offsetWidth, fh = wrap.offsetHeight;
      if (!W || !Hc || !fw || !fh) return;

      // Cover the panel with the scene, centred on the poster, never showing
      // past the photo's edges.
      var want = pickView(W, Hc);
      if (want !== view && !viewSwitching) {
        viewSwitching = true;
        var pre = new Image();
        pre.src = want.src;
        (pre.decode ? pre.decode() : Promise.resolve()).catch(function () {}).then(function () {
          viewSwitching = false;
          if (!on || pickView(main.clientWidth, main.clientHeight) !== want) { schedule(); return; }
          useView(want);
          layout();
        });
      }
      var q = view.quad;
      var qcx = (q[0][0] + q[1][0] + q[2][0] + q[3][0]) / 4;
      var qcy = (q[0][1] + q[1][1] + q[2][1] + q[3][1]) / 4;
      var fit = roomFit(W, Hc);
      var s = fit.s, left = fit.left, top = fit.top;
      var iw = view.w * s, ih = view.h * s;
      [bg, light].forEach(function (img) {
        img.style.width = iw + 'px'; img.style.height = ih + 'px';
        img.style.left = left + 'px'; img.style.top = top + 'px';
      });
      // Where the photo stops short of the panel (wide desktop panels), a
      // blurred copy of the scene fills the rest and the photo's edges fade
      // into it, so it reads as one room rather than a picture in a box.
      var gapX = iw < W - 0.5, gapY = ih < Hc - 0.5;
      roomEl.classList.toggle('is-contained', gapX || gapY);
      var fade = [];
      if (gapX) fade.push('linear-gradient(to right, transparent 0, #000 9%, #000 91%, transparent 100%)');
      if (gapY) fade.push('linear-gradient(to bottom, transparent 0, #000 9%, #000 91%, transparent 100%)');
      var mask = fade.length ? fade.join(', ') : '';
      bg.style.webkitMaskImage = bg.style.maskImage = mask;
      bg.style.webkitMaskComposite = fade.length > 1 ? 'source-in' : '';
      bg.style.maskComposite = fade.length > 1 ? 'intersect' : '';
      if (gapX || gapY) {
        if (!backdrop) {
          backdrop = document.createElement('div');
          backdrop.className = 'sm2-room__backdrop';
          roomEl.insertBefore(backdrop, roomEl.firstChild);
        }
        var src = 'url("' + (bg.currentSrc || bg.src) + '")';
        if (backdrop.style.backgroundImage !== src) backdrop.style.backgroundImage = src;
      }

      // The PRINT goes on the sheet; with a Framed finish the moulding sits
      // around it (.sm2-frame is content-box with a border), so map the
      // frame's content box and let the frame extend past the sheet, as a
      // real frame around a print that size would.
      var frame = wrap.querySelector('.sm2-frame');
      var px0 = 0, py0 = 0, pWidth = fw, pHeight = fh;
      if (frame && frame.clientWidth && frame.clientHeight) {
        px0 = frame.offsetLeft + frame.clientLeft;
        py0 = frame.offsetTop + frame.clientTop;
        pWidth = frame.clientWidth;
        pHeight = frame.clientHeight;
      }

      // Print rectangle in sheet units (sheet = SHEET_ASPECT x 1), covering it.
      var ap = pWidth / pHeight;
      var ph = Math.max(1, SHEET_ASPECT / ap) * 1.012;
      var pw = ph * ap;
      var u0 = (SHEET_ASPECT - pw) / 2 / SHEET_ASPECT, u1 = (SHEET_ASPECT + pw) / 2 / SHEET_ASPECT;
      var v0 = (1 - ph) / 2, v1 = (1 + ph) / 2;
      var sheet = homography([[0, 0], [1, 0], [1, 1], [0, 1]], q);
      var corners = [[u0, v0], [u1, v0], [u1, v1], [u0, v1]].map(function (c) {
        var p = applyH(sheet, c[0], c[1]);
        return [p[0] * s + left, p[1] * s + top];   // panel coordinates
      });

      // The wrap keeps its layout box; map that box onto the corners.
      var ox = wrap.offsetLeft, oy = wrap.offsetTop;
      placedAt = placementKey();
      var M = homography(
        [[px0, py0], [px0 + pWidth, py0], [px0 + pWidth, py0 + pHeight], [px0, py0 + pHeight]],
        corners.map(function (c) { return [c[0] - ox, c[1] - oy]; }));
      wrap.style.setProperty('transform-origin', '0 0', 'important');
      wrap.style.setProperty('transform', matrix3d(M), 'important');

      // Lamplight across the whole object: the print, a Framed moulding and
      // any gift wrap / ribbon (they overhang the wrap box by 2px). The light
      // map is a smooth field fitted to the blank sheet, so extending it past
      // the sheet adds no floor texture.
      var outer = [[-2, -2], [fw + 2, -2], [fw + 2, fh + 2], [-2, fh + 2]].map(function (c) {
        var q = applyH(M, c[0], c[1]);
        return [q[0] + ox, q[1] + oy];
      });
      // Whole pixels: a fractional polygon makes Chromium draw a hairline
      // seam along the multiplied layer's bounding box.
      light.style.clipPath = 'polygon(' + outer.map(function (c) {
        return Math.round(c[0] - left) + 'px ' + Math.round(c[1] - top) + 'px';
      }).join(', ') + ')';

      // Where the scene ends up once the phone preview has collapsed (see
      // applyCollapse): the same cover fit, into the D px shorter strip still
      // showing below the viewport edge. Expressed as one similarity
      // P -> kP + t on the panel, it moves photo, light map and print together;
      // linear steps between two covering fits always cover, so no edge of
      // the photo shows mid-scroll.
      var D = sm2GetCollapseD ? sm2GetCollapseD() : 0;
      var Hc1 = Math.max(1, Hc - D);
      var fit1 = roomFit(W, Hc1);
      var s1 = fit1.s, left1 = fit1.left, top1 = fit1.top;
      var kR = s1 / s;
      bg.style.transformOrigin = light.style.transformOrigin = '0 0';
      sampleEdgeColor(s1, left1, top1, W);
      sm2RoomCollapse = {
        key: [W, Hc, D, left, top, s, ox, oy].map(function (n) { return Math.round(n * 100); }).join(','),
        end: { k: kR, tx: left1 - kR * left, ty: D + top1 - kR * top },
        items: [[bg, left, top], [light, left, top], [wrap, ox, oy]],
        // For the notch backdrop's copy of the photo (paintBandPhoto).
        photo: { src: bg.currentSrc || bg.src, w: iw, h: ih, left: left, top: top }
      };
      if (sm2OnRoomLayout) sm2OnRoomLayout();
    }
    sm2RoomRelayout = schedule;

    // The print is pinned to the photo by its layout position in the panel.
    // Resize, size changes and panel classes re-map it, but the print can also
    // move without any of those firing (iOS applying the layout late after a
    // rotation, the panel's contents shifting): it then sits off its sheet in
    // the photo until something else re-maps it. Compare on every sticky paint
    // (layout is already clean there) and after resizes settle.
    var placedAt = null;
    function placementKey() {
      var frame = wrap.querySelector('.sm2-frame');
      return [main.clientWidth, main.clientHeight, wrap.offsetLeft, wrap.offsetTop, wrap.offsetWidth, wrap.offsetHeight,
        frame ? frame.offsetLeft + frame.clientLeft : 0, frame ? frame.offsetTop + frame.clientTop : 0].join(',');
    }
    sm2RoomCheck = function () {
      if (!on || raf) return;
      if (placementKey() !== placedAt) schedule();
    };
    var settleTimers = [];
    function settleChecks() {
      settleTimers.forEach(clearTimeout);
      settleTimers = [250, 700, 1500].map(function (ms) { return setTimeout(function () { if (sm2RoomCheck) sm2RoomCheck(); }, ms); });
    }
    window.addEventListener('resize', settleChecks);
    window.addEventListener('orientationchange', settleChecks);
    window.addEventListener('pageshow', settleChecks);

    // What sits above the viewport edge once the preview has collapsed, as a
    // left-to-right gradient of the photo's colours there: the notch backdrop
    // (.sm2-notch-band) wears it in Room view. No vertical detail on purpose:
    // the band is repositioned by script, which trails iOS scrolling by a
    // frame, and a flat-in-y fill makes that lag invisible. Theme assets are
    // same-origin, so the canvas read is allowed; if not, the CSS fallback stays.
    var EDGE_COLUMNS = 12, EDGE_SPAN = 16;   // columns across; px of photo just above the edge
    var edgeKey = null, edgeCanvas = null;
    function sampleEdgeColor(s1, left1, top1, W) {
      var key = [view.src, Math.round(s1 * 1e4), Math.round(left1), Math.round(top1), W].join(',');
      if (key === edgeKey || !bg.naturalWidth) return;
      edgeKey = key;
      try {
        var sx = Math.max(0, -left1 / s1);
        var sy1 = Math.max(0, -top1 / s1);                 // photo row at the viewport edge
        var sy0 = Math.max(0, sy1 - EDGE_SPAN / s1);       // ...and EDGE_SPAN px above it
        if (sy1 - sy0 < 1) sy1 = Math.min(view.h, sy0 + EDGE_SPAN / s1);
        var sw = Math.min(view.w - sx, W / s1);
        if (sw <= 0) return;
        var r = bg.naturalWidth / view.w;                  // CDN may serve a resized file
        // A heavy downscale in one drawImage only point-samples (one stray
        // wood-grain line can set a whole column), so draw at 8x4 per column
        // and average each block.
        var CW = 8, CH = 4, cw = EDGE_COLUMNS * CW;
        edgeCanvas = edgeCanvas || document.createElement('canvas');
        edgeCanvas.width = cw; edgeCanvas.height = CH;
        var ctx = edgeCanvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(bg, sx * r, sy0 * r, sw * r, (sy1 - sy0) * r, 0, 0, cw, CH);
        var d = ctx.getImageData(0, 0, cw, CH).data, stops = [], avg = [0, 0, 0];
        for (var c = 0; c < EDGE_COLUMNS; c++) {
          var sum = [0, 0, 0];
          for (var yy = 0; yy < CH; yy++) {
            for (var xx = c * CW; xx < (c + 1) * CW; xx++) {
              var o = (yy * cw + xx) * 4;
              sum[0] += d[o]; sum[1] += d[o + 1]; sum[2] += d[o + 2];
            }
          }
          var col = sum.map(function (v) { return Math.round(v / (CW * CH)); });
          stops.push('rgb(' + col.join(',') + ') ' + ((c + 0.5) / EDGE_COLUMNS * 100).toFixed(1) + '%');
          avg[0] += col[0]; avg[1] += col[1]; avg[2] += col[2];
        }
        var band = document.querySelector('body > .sm2-notch-band');
        if (!band) return;
        band.style.setProperty('--sm2-edge-color', 'rgb(' + avg.map(function (v) { return Math.round(v / EDGE_COLUMNS); }).join(',') + ')');
        band.style.setProperty('--sm2-edge-fill', 'linear-gradient(to right, ' + stops.join(', ') + ')');
      } catch (e) { /* tainted or unavailable: keep the CSS fallback */ }
    }

    function paint() {
      Array.prototype.forEach.call(toggles, function (t) {
        t.setAttribute('aria-pressed', on ? 'true' : 'false');
        t.setAttribute('aria-label', on ? 'Switch to the flat preview' : 'See it in a room');
        t.title = on ? 'Flat preview' : 'See it in a room';
        // The toolbar label names what a press will do next.
        var label = t.querySelector('span');
        if (label) label.textContent = on ? 'Flat' : 'Room';
      });
    }

    // First time room view opens on its own, point at the button that turns
    // it off. Once per browser; fades after a few seconds or the first tap.
    var HINT_KEY = 'skylit-room-hint-seen';
    function showHint() {
      try { if (localStorage.getItem(HINT_KEY)) return; localStorage.setItem(HINT_KEY, '1'); } catch (e) { return; }
      var btn = Array.prototype.filter.call(toggles, function (t) { return t.offsetParent !== null; })[0];
      if (!btn) return;
      var hint = document.createElement('div');
      hint.className = 'sm2-room-hint';
      hint.setAttribute('role', 'status');
      hint.textContent = (window.matchMedia && window.matchMedia('(pointer: coarse)').matches ? 'Tap' : 'Click') + ' for flat view';
      main.appendChild(hint);
      var place = function () {
        var m = main.getBoundingClientRect(), b = btn.getBoundingClientRect();
        hint.style.top = (b.top - m.top + b.height / 2) + 'px';
        hint.style.right = (m.right - b.left + 10) + 'px';
      };
      place();
      requestAnimationFrame(function () { hint.classList.add('is-shown'); });
      var done = false;
      var dismiss = function () {
        if (done) return;
        done = true;
        hint.classList.remove('is-shown');
        document.removeEventListener('pointerdown', dismiss, true);
        window.removeEventListener('scroll', dismiss);
        setTimeout(function () { if (hint.parentNode) hint.parentNode.removeChild(hint); }, 300);
      };
      setTimeout(dismiss, 5000);
      setTimeout(function () {
        document.addEventListener('pointerdown', dismiss, true);
        window.addEventListener('scroll', dismiss, { passive: true });
      }, 400);
    }

    // ── On / off, with a crossfade ─────────────────────────────────────
    // Room view is the default: the page first paints the flat print (the
    // LCP poster), the scene loads after the page has, and then the flat
    // print fades out and fades back in lying in the room. A customer who
    // switches to flat is remembered (per browser) and starts flat next time.
    var PREF_KEY = 'skylit-room-view';
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var sceneReady = null, fadeTimer = 0, userChose = false;

    function loadScene() {
      if (sceneReady) return sceneReady;
      view = pickView(main.clientWidth, main.clientHeight);
      sceneReady = Promise.all([[bg, view.src], [light, view.light || view.src]].map(function (pair) {
        return new Promise(function (resolve) {
          var img = pair[0];
          img.onload = img.onerror = function () { resolve(); };
          img.src = pair[1];
          if (img.complete && img.naturalWidth) resolve();
        });
      }));
      return sceneReady;
    }

    // The gift-peek eye lives on the print. In room view the print is
    // transformed onto the floor, so the eye would tilt with it: park it on
    // the panel instead (CSS places it) and put it back on the way out.
    var peek = document.getElementById('sm2-gift-peek-btn');
    var peekHome = peek ? { parent: peek.parentNode, next: peek.nextSibling } : null;
    function parkPeek(inRoom) {
      if (!peek || !peekHome) return;
      if (inRoom && peek.parentNode !== main) main.appendChild(peek);
      else if (!inRoom && peek.parentNode !== peekHome.parent) peekHome.parent.insertBefore(peek, peekHome.next);
    }

    function apply(next) {
      on = next;
      parkPeek(on);
      if (on) {
        main.classList.add('sm2-main--room');
        layout();
      } else {
        main.classList.remove('sm2-main--room');
        wrap.style.removeProperty('transform');
        wrap.style.removeProperty('transform-origin');
        light.style.clipPath = '';
        if (sm2OnRoomLayout) sm2OnRoomLayout();
      }
      paint();
    }

    function crossfade(fn) {
      clearTimeout(fadeTimer);
      if (reduce) { fn(); return; }
      main.classList.add('sm2-main--fading');
      fadeTimer = setTimeout(function () {
        fn();
        main.classList.add('sm2-main--room-enter');
        void main.offsetWidth;
        main.classList.remove('sm2-main--fading');
        fadeTimer = setTimeout(function () { main.classList.remove('sm2-main--room-enter'); }, 480);
      }, 190);
    }

    function set(next) {
      if (next) {
        loadScene().then(function () {
          if (!on) crossfade(function () { apply(true); });
        });
      } else if (on) {
        crossfade(function () { apply(false); });
      }
    }

    Array.prototype.forEach.call(toggles, function (t) {
      t.addEventListener('click', function () {
        userChose = true;
        var next = !on;
        try { localStorage.setItem(PREF_KEY, next ? 'room' : 'flat'); } catch (e) { /* not remembered */ }
        set(next);
      });
    });

    var pref = null;
    try { pref = localStorage.getItem(PREF_KEY); } catch (e) { /* storage blocked */ }
    if (pref !== 'flat') {
      var startDefault = function () {
        var go = function () {
          if (userChose) return;
          set(true);
          loadScene().then(function () { setTimeout(function () { if (on && !userChose) showHint(); }, 900); });
        };
        if (window.requestIdleCallback) window.requestIdleCallback(go, { timeout: 1500 });
        else setTimeout(go, 300);
      };
      if (document.readyState === 'complete') startDefault();
      else window.addEventListener('load', startDefault, { once: true });
    }

    window.addEventListener('resize', schedule);
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(schedule);
      ro.observe(main); ro.observe(wrap);
    }
    // Stuck/unstuck and minimise change the wrap's layout offset.
    if (window.MutationObserver) {
      new MutationObserver(schedule).observe(main, { attributes: true, attributeFilter: ['class'] });
    }
    paint();
  }

  // ── Full-screen print viewer ────────────────────────────────────────────
  // Any [data-sm2-viewer-open] button opens #sm2-viewer with a snapshot of the
  // live frame. The clone keeps the on-page pixel size and is scaled with a
  // transform (fit × zoom) instead of being re-laid out at viewer size: the
  // caption runs on cqh/cqw and the Framed border is a fixed 25px, so scaling
  // is the only way every element keeps its exact proportion to the print.
  // "100%" = the whole print fitted to the stage.
  function bindPrintViewer() {
    var viewer = document.getElementById('sm2-viewer');
    var stage = document.getElementById('sm2-viewer-stage');
    var canvas = document.getElementById('sm2-viewer-canvas');
    var level = document.getElementById('sm2-viewer-level');
    var fsBtn = document.getElementById('sm2-viewer-fs');
    var hint = document.getElementById('sm2-viewer-hint');
    var openers = document.querySelectorAll('[data-sm2-viewer-open]');
    if (!viewer || !stage || !canvas || !openers.length) return;

    // Park the dialog on <body>: inside the section it would inherit the
    // stacking context of the sticky preview on mobile.
    if (viewer.parentNode !== document.body) document.body.appendChild(viewer);

    var MIN = 1, MAX = 5;
    var STEPS = [1, 1.5, 2, 3, 4, 5];
    var baseW = 0, baseH = 0, fit = 1;
    var zoom = 1, tx = 0, ty = 0;
    var returnFocus = null;
    var prevOverflow = '';
    var coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

    if (hint) hint.textContent = coarse ? 'Pinch to zoom, swipe down to close.' : 'Zoom in, then drag to explore.';

    function fsElement() { return document.fullscreenElement || document.webkitFullscreenElement || null; }
    var fsSupported = !!(viewer.requestFullscreen || viewer.webkitRequestFullscreen) &&
      !!(document.fullscreenEnabled || document.webkitFullscreenEnabled);
    if (fsBtn) fsBtn.hidden = !fsSupported;

    // The copy must not repeat the page's SVG clip ids. WebKit resolves
    // url(#id) through one id map, so removing the copy when the viewer
    // closed dropped #sm2-heart-clip and the live heart shape stopped
    // clipping (the whole sky showed, e.g. in Room view, iPhone Safari).
    // Rename the copy's defs, point its own references at them, and give
    // elements clipped by a stylesheet rule an inline clip of their own.
    function uniqueCloneDefs(srcWrap, clone) {
      var map = {};
      var defs = clone.querySelectorAll('clipPath[id], mask[id], linearGradient[id], radialGradient[id], filter[id], pattern[id]');
      for (var i = 0; i < defs.length; i++) {
        map[defs[i].id] = defs[i].id + '--viewer';
        defs[i].id = map[defs[i].id];
      }
      if (!defs.length) return;
      var URL_REF = /url\(\s*["']?#([^"')\s]+)["']?\s*\)/g;
      var fix = function (v) {
        return v.replace(URL_REF, function (m, id) { return map[id] ? 'url(#' + map[id] + ')' : m; });
      };
      var attrs = ['clip-path', 'mask', 'filter', 'fill', 'stroke', 'style'];
      var els = clone.querySelectorAll('[clip-path], [mask], [filter], [fill], [stroke], [style]');
      for (var j = 0; j < els.length; j++) {
        for (var k = 0; k < attrs.length; k++) {
          var v = els[j].getAttribute(attrs[k]);
          if (v && v.indexOf('url(') !== -1) els[j].setAttribute(attrs[k], fix(v));
        }
      }
      var CLIPPED = '.sm2-celestial-map, .sm2-milkyway-overlay';
      var src = srcWrap.querySelectorAll(CLIPPED), dst = clone.querySelectorAll(CLIPPED);
      for (var n = 0; n < src.length && n < dst.length; n++) {
        var cp = getComputedStyle(src[n]).clipPath || '';
        if (cp.indexOf('url(') === -1) continue;
        var own = fix(cp);
        dst[n].style.setProperty('clip-path', own);
        dst[n].style.setProperty('-webkit-clip-path', own);
      }
    }

    // The frame moulding is a 9-slice border-image. Scaled up here, the
    // browser paints its corner and edge tiles separately and their
    // antialiased edges show as hairlines at the mitres. Paint the same nine
    // slices into one canvas laid over the box instead: one bitmap scales
    // without seams. `cs` is the computed style carrying the border-image,
    // `bw` the border widths [top, right, bottom, left] in CSS px.
    // `onPainted` runs once the canvas holds the frame, so the caller can
    // drop the original border-image without a blank frame in between.
    function paintFrameRing(cs, w, h, bw, onPainted) {
      var src = /url\(\s*["']?([^"')]+)["']?\s*\)/.exec(cs.getPropertyValue('border-image-source') || '');
      var sl = (cs.getPropertyValue('border-image-slice') || '').split(/\s+/).map(parseFloat).filter(function (n) { return n > 0; });
      if (!src || !sl.length || !w || !h || !(bw[0] || bw[1] || bw[2] || bw[3])) return null;
      while (sl.length < 4) sl.push(sl[sl.length === 3 ? 1 : 0]);
      // Native texture resolution (slice px per border px), capped for memory.
      var k = 1;
      for (var i = 0; i < 4; i++) if (bw[i] > 0) k = Math.max(k, sl[i] / bw[i]);
      k = Math.min(k, 4096 / Math.max(w, h));
      var c = document.createElement('canvas');
      c.width = Math.round(w * k);
      c.height = Math.round(h * k);
      c.setAttribute('aria-hidden', 'true');
      c.style.cssText = 'position:absolute;pointer-events:none;z-index:20;width:' + w + 'px;height:' + h + 'px;';
      var img = new Image();
      var draw = function () {
        var iw = img.naturalWidth, ih = img.naturalHeight;
        if (!iw || !ih) return;
        var W = c.width, H = c.height;
        var T = Math.round(bw[0] * k), R = Math.round(bw[1] * k), B = Math.round(bw[2] * k), L = Math.round(bw[3] * k);
        var sx = [0, sl[3], iw - sl[1], iw], sy = [0, sl[0], ih - sl[2], ih];
        var dx = [0, L, W - R, W], dy = [0, T, H - B, H];
        var ctx = c.getContext('2d');
        for (var r = 0; r < 3; r++) {
          for (var q = 0; q < 3; q++) {
            if (r === 1 && q === 1) continue;
            var sw = sx[q + 1] - sx[q], sh = sy[r + 1] - sy[r];
            var dw = dx[q + 1] - dx[q], dh = dy[r + 1] - dy[r];
            if (sw > 0 && sh > 0 && dw > 0 && dh > 0) ctx.drawImage(img, sx[q], sy[r], sw, sh, dx[q], dy[r], dw, dh);
          }
        }
        if (onPainted) onPainted();
      };
      img.onload = draw;
      img.src = src[1];
      if (img.complete && img.naturalWidth) { img.onload = null; draw(); }
      return c;
    }

    function buildClone() {
      var srcWrap = document.getElementById('sm2-frame-wrap');
      var srcFrame = document.getElementById('sm2-frame');
      if (!srcWrap || !srcFrame) return false;
      // offsetWidth ignores the sticky preview's scale transform, which is
      // what we want: the frame's real laid-out size.
      baseW = srcWrap.offsetWidth;
      baseH = srcWrap.offsetHeight;
      if (!baseW || !baseH) return false;
      var cs = getComputedStyle(srcFrame);

      var clone = srcWrap.cloneNode(true);
      clone.removeAttribute('id');
      uniqueCloneDefs(srcWrap, clone);
      // Room view parks a matrix3d on the live wrap; the viewer shows it flat.
      clone.style.removeProperty('transform');
      clone.style.removeProperty('transform-origin');
      clone.setAttribute('aria-hidden', 'true');
      clone.style.width = baseW + 'px';
      clone.style.height = baseH + 'px';
      // The page's preview is screen-sized; the viewer zooms to 5x.
      var clonePreview = clone.querySelector('.sm2-celestial-preview');
      if (clonePreview && _starPreviewFullUrl) clonePreview.src = _starPreviewFullUrl;
      var frame = clone.querySelector('.sm2-frame');
      if (frame) {
        frame.classList.remove('sm2-zooming');
        frame.style.setProperty('width', cs.width, 'important');
        frame.style.setProperty('height', cs.height, 'important');
      }
      // Canvases clone blank. Copy the pixels across (a tainted or lost
      // WebGL buffer just stays blank; the stamped <img> previews carry
      // the visible stars anyway).
      var srcCanvases = srcWrap.querySelectorAll('canvas');
      var dstCanvases = clone.querySelectorAll('canvas');
      for (var i = 0; i < srcCanvases.length && i < dstCanvases.length; i++) {
        var sc = srcCanvases[i], dc = dstCanvases[i];
        if (!sc.width || !sc.height) continue;
        dc.width = sc.width; dc.height = sc.height;
        try { dc.getContext('2d').drawImage(sc, 0, 0); } catch (e) { /* noop */ }
      }
      canvas.innerHTML = '';
      canvas.style.width = baseW + 'px';
      canvas.style.height = baseH + 'px';
      canvas.appendChild(clone);
      if (frame && srcWrap.getAttribute('data-finish') === 'framed') {
        var bw = ['top', 'right', 'bottom', 'left'].map(function (s) { return parseFloat(cs.getPropertyValue('border-' + s + '-width')) || 0; });
        var ring = paintFrameRing(cs, srcFrame.offsetWidth, srcFrame.offsetHeight, bw, function () {
          frame.style.setProperty('transition', 'none', 'important');
          frame.style.setProperty('border-image', 'none', 'important');
          frame.style.setProperty('border-color', 'transparent', 'important');
        });
        if (ring) {
          ring.style.left = srcFrame.offsetLeft + 'px';
          ring.style.top = srcFrame.offsetTop + 'px';
          clone.appendChild(ring);
        }
      }
      return true;
    }

    function measureFit() {
      var r = stage.getBoundingClientRect();
      var pad = r.width < 600 ? 24 : 64;
      fit = Math.min((r.width - pad) / baseW, (r.height - pad) / baseH);
      if (!(fit > 0)) fit = 1;
    }

    function clampPan() {
      var r = stage.getBoundingClientRect();
      var maxX = Math.max(0, (baseW * fit * zoom - r.width) / 2);
      var maxY = Math.max(0, (baseH * fit * zoom - r.height) / 2);
      tx = Math.max(-maxX, Math.min(maxX, tx));
      ty = Math.max(-maxY, Math.min(maxY, ty));
    }

    var zoomOutBtn = viewer.querySelector('[data-sm2-viewer-zoom="out"]');
    var zoomInBtn = viewer.querySelector('[data-sm2-viewer-zoom="in"]');
    var paintedZoom = null;
    function setTransform() {
      canvas.style.transform = 'translate(-50%, -50%) translate(' + tx + 'px, ' + (ty + dismissY) + 'px) scale(' + (fit * zoom) + ')';
    }
    function apply(animate) {
      clampPan();
      viewer.classList.toggle('is-animating', !!animate);
      setTransform();
      // Zoom UI only when the zoom moved: rewriting the label on every
      // pointer event invalidated layout mid-gesture.
      var pct = Math.round(zoom * 100);
      if (pct !== paintedZoom) {
        paintedZoom = pct;
        viewer.classList.toggle('is-zoomed', zoom > 1.001);
        if (level) level.textContent = pct + '%';
        if (zoomOutBtn) zoomOutBtn.disabled = zoom <= MIN + 0.001;
        if (zoomInBtn) zoomInBtn.disabled = zoom >= MAX - 0.001;
      }
    }

    // Zoom to `next`, keeping the stage point (px, py) — measured from the
    // stage centre — fixed under the finger/cursor.
    function zoomTo(next, px, py, animate) {
      next = Math.max(MIN, Math.min(MAX, next));
      var k = next / zoom;
      tx = px - (px - tx) * k;
      ty = py - (py - ty) * k;
      zoom = next;
      apply(animate);
    }

    function stagePoint(clientX, clientY) {
      var r = stage.getBoundingClientRect();
      return { x: clientX - r.left - r.width / 2, y: clientY - r.top - r.height / 2 };
    }

    function step(dir) {
      var next = zoom;
      if (dir > 0) {
        for (var i = 0; i < STEPS.length; i++) if (STEPS[i] > zoom + 0.01) { next = STEPS[i]; break; }
      } else {
        for (var j = STEPS.length - 1; j >= 0; j--) if (STEPS[j] < zoom - 0.01) { next = STEPS[j]; break; }
      }
      zoomTo(next, 0, 0, true);
    }

    function open(e) {
      if (!buildClone()) return;
      returnFocus = e && e.currentTarget ? e.currentTarget : document.activeElement;
      viewer.hidden = false;
      prevOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      zoom = 1; tx = 0; ty = 0;
      measureFit();
      apply(false);
      requestAnimationFrame(function () { viewer.classList.add('is-open'); });
      var closeBtn = viewer.querySelector('[data-sm2-viewer-close]');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
      document.addEventListener('keydown', onKey);
      window.addEventListener('resize', onResize);
    }

    function close() {
      if (viewer.hidden) return;
      if (fsElement() === viewer) {
        try { (document.exitFullscreen || document.webkitExitFullscreen).call(document); } catch (e) { /* noop */ }
      }
      viewer.classList.remove('is-open', 'is-zoomed', 'is-dragging', 'is-animating', 'is-fullscreen');
      viewer.hidden = true;
      canvas.innerHTML = '';
      document.documentElement.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
      pointers = {};
      if (returnFocus && returnFocus.focus && returnFocus.offsetParent !== null) {
        returnFocus.focus({ preventScroll: true });
      }
    }

    function onResize() {
      if (viewer.hidden) return;
      measureFit();
      apply(false);
    }

    function onKey(e) {
      if (e.key === 'Escape') {
        // In native full screen the browser eats the first Escape to leave
        // full screen; this one only fires when we are windowed.
        e.preventDefault();
        close();
      } else if (e.key === '+' || e.key === '=') {
        e.preventDefault(); step(1);
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault(); step(-1);
      } else if (e.key === '0') {
        e.preventDefault(); zoom = 1; tx = 0; ty = 0; apply(true);
      } else if (e.key === 'Tab') {
        // Keep focus inside the dialog.
        var f = viewer.querySelectorAll('button:not([disabled]):not([hidden])');
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }

    Array.prototype.forEach.call(openers, function (btn) {
      btn.addEventListener('click', open);
    });
    viewer.addEventListener('click', function (e) {
      var t = e.target.closest ? e.target.closest('[data-sm2-viewer-close],[data-sm2-viewer-zoom]') : null;
      if (!t) return;
      if (t.hasAttribute('data-sm2-viewer-close')) { close(); return; }
      var z = t.getAttribute('data-sm2-viewer-zoom');
      if (z === 'in') step(1);
      else if (z === 'out') step(-1);
      else if (z === 'reset') { zoom = 1; tx = 0; ty = 0; apply(true); }
    });

    if (fsBtn && fsSupported) {
      fsBtn.addEventListener('click', function () {
        try {
          if (fsElement()) (document.exitFullscreen || document.webkitExitFullscreen).call(document);
          else (viewer.requestFullscreen || viewer.webkitRequestFullscreen).call(viewer);
        } catch (e) { /* noop */ }
      });
      var onFs = function () {
        var on = fsElement() === viewer;
        viewer.classList.toggle('is-fullscreen', on);
        fsBtn.setAttribute('aria-label', on ? 'Exit full screen' : 'Enter full screen');
        // Stage size changes once the browser has finished the transition.
        requestAnimationFrame(onResize);
      };
      document.addEventListener('fullscreenchange', onFs);
      document.addEventListener('webkitfullscreenchange', onFs);
    }

    // ── Gestures: drag to pan, pinch to zoom, double-tap to toggle ─────────
    var pointers = {};
    var dismissY = 0;        // swipe-down-to-close offset (only at 100%)
    var dismissStart = null; // { x, y, t } of the finger that may be dismissing
    var pinchStart = null;   // { dist, zoom }
    var dragLast = null;     // { x, y }
    var lastTap = 0;
    var clickStart = null;   // mouse press { x, y }: a click without a drag toggles zoom

    function pointerList() {
      var out = [];
      for (var k in pointers) if (Object.prototype.hasOwnProperty.call(pointers, k)) out.push(pointers[k]);
      return out;
    }

    stage.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      try { stage.setPointerCapture(e.pointerId); } catch (err) { /* noop */ }
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      var list = pointerList();
      viewer.classList.remove('is-animating');
      if (list.length === 2) {
        if (dismissY) { dismissY = 0; setDim(0); apply(true); }
        dismissStart = null;
        pinchStart = {
          dist: Math.hypot(list[0].x - list[1].x, list[0].y - list[1].y) || 1,
          zoom: zoom
        };
        dragLast = null;
      } else if (list.length === 1) {
        dragLast = { x: e.clientX, y: e.clientY };
        dismissStart = zoom <= 1.001 ? { x: e.clientX, y: e.clientY, t: Date.now(), active: false } : null;
        var now = Date.now();
        // A mouse click toggles zoom on release (the stage shows a zoom-in
        // cursor, and a double-click was the only thing that zoomed on
        // desktop); double-tap stays the gesture for touch.
        if (e.pointerType === 'mouse') {
          clickStart = { x: e.clientX, y: e.clientY };
        } else if (now - lastTap < 300) {
          var p = stagePoint(e.clientX, e.clientY);
          if (zoom > 1.001) { zoom = 1; tx = 0; ty = 0; apply(true); }
          else zoomTo(2.5, p.x, p.y, true);
          lastTap = 0;
          dragLast = null;
        } else {
          lastTap = now;
        }
      }
    });

    stage.addEventListener('pointermove', function (e) {
      if (!pointers[e.pointerId]) return;
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      var list = pointerList();
      if (list.length >= 2 && pinchStart) {
        var d = Math.hypot(list[0].x - list[1].x, list[0].y - list[1].y) || 1;
        var mid = stagePoint((list[0].x + list[1].x) / 2, (list[0].y + list[1].y) / 2);
        zoomTo(pinchStart.zoom * d / pinchStart.dist, mid.x, mid.y, false);
      } else if (list.length === 1 && dragLast) {
        var dx = e.clientX - dragLast.x, dy = e.clientY - dragLast.y;
        if (Math.abs(dx) + Math.abs(dy) > 2) lastTap = 0;
        dragLast = { x: e.clientX, y: e.clientY };
        if (zoom > 1.001) {
          viewer.classList.add('is-dragging');
          tx += dx; ty += dy;
          apply(false);
        } else if (dismissStart) {
          // At 100% a mostly-downward drag pulls the print away: it follows
          // the finger and the backdrop fades, like Photos on iPhone.
          var ddx = e.clientX - dismissStart.x, ddy = e.clientY - dismissStart.y;
          if (!dismissStart.active && ddy > 8 && ddy > Math.abs(ddx) * 1.2) dismissStart.active = true;
          if (dismissStart.active) {
            dismissY = Math.max(0, ddy);
            if (!dismissRaf) {
              canvas.style.willChange = 'transform';
              viewer.classList.remove('is-animating');
              dismissRaf = requestAnimationFrame(function () {
                dismissRaf = 0;
                setTransform();
                setDim(Math.min(1, dismissY / 300));
              });
            }
          }
        }
      }
    });

    // The fade runs on a dedicated backdrop layer and on the bar/hint's own
    // opacity: an inherited custom property on the viewer restyled the whole
    // cloned print on every frame of the drag.
    // The backdrop is in the section markup, so the viewer always has its
    // background even if this script is an older cached copy.
    var backdrop = viewer.querySelector('.sm2-viewer__backdrop');
    if (!backdrop) {
      backdrop = document.createElement('div');
      backdrop.className = 'sm2-viewer__backdrop';
      backdrop.setAttribute('aria-hidden', 'true');
      viewer.insertBefore(backdrop, viewer.firstChild);
    }
    var barEl = viewer.querySelector('.sm2-viewer__bar');
    var dismissRaf = 0;
    function setDim(p) {
      backdrop.style.opacity = String(1 - p * 0.85);
      var o = String(Math.max(0, 1 - p * 1.4));
      if (barEl) barEl.style.opacity = o;
      if (hint) hint.style.opacity = o;
    }

    function endPointer(e) {
      if (!pointers[e.pointerId]) return;
      if (clickStart && e.type === 'pointerup' && !(dismissStart && dismissStart.active) &&
          Math.abs(e.clientX - clickStart.x) + Math.abs(e.clientY - clickStart.y) < 5) {
        var cp = stagePoint(e.clientX, e.clientY);
        if (zoom > 1.001) { zoom = 1; tx = 0; ty = 0; apply(true); }
        else zoomTo(2.5, cp.x, cp.y, true);
      }
      clickStart = null;
      if (dismissStart && dismissStart.active) {
        if (dismissRaf) { cancelAnimationFrame(dismissRaf); dismissRaf = 0; }
        setTimeout(function () { canvas.style.willChange = ''; }, 260);
        var dt = Math.max(1, Date.now() - dismissStart.t);
        var velocity = dismissY / dt; // px per ms
        dismissStart = null;
        if (dismissY > 110 || (dismissY > 40 && velocity > 0.6)) {
          // Carry on off the bottom, then close.
          dismissY = window.innerHeight;
          setDim(1);
          apply(true);
          setTimeout(function () { close(); dismissY = 0; setDim(0); }, 200);
        } else {
          dismissY = 0;
          setDim(0);
          apply(true);
        }
      }
      delete pointers[e.pointerId];
      var list = pointerList();
      if (list.length < 2) pinchStart = null;
      // Lifting one finger of a pinch hands the pan to the one that stays.
      dragLast = list.length === 1 ? { x: list[0].x, y: list[0].y } : null;
      if (!list.length) viewer.classList.remove('is-dragging');
    }
    stage.addEventListener('pointerup', endPointer);
    stage.addEventListener('pointercancel', endPointer);

    stage.addEventListener('wheel', function (e) {
      e.preventDefault();
      var p = stagePoint(e.clientX, e.clientY);
      // Trackpad pinch arrives as ctrl+wheel with small deltas; a mouse wheel
      // notch is ~100. Both map to a smooth exponential zoom.
      var factor = Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0012));
      zoomTo(zoom * factor, p.x, p.y, false);
    }, { passive: false });

    // Safari's proprietary pinch events would otherwise zoom the whole page.
    ['gesturestart', 'gesturechange'].forEach(function (t) {
      viewer.addEventListener(t, function (e) { e.preventDefault(); });
    });
  }

  // ── Mobile chrome: cart button on the preview ─────────────────────────
  // ≤900px the header bar is collapsed (sections/starmap-v2.liquid). The
  // cart button opens the theme's own drawer by clicking the hidden header
  // icon, and mirrors the header's count bubble, which the theme keeps
  // current after every add/remove.
  function bindMobileChrome() {
    var btn = document.getElementById('sm2-cart-btn');
    var badge = document.getElementById('sm2-cart-btn-count');
    var icon = document.getElementById('cart-icon-bubble');
    // Top-left: open the theme's menu drawer (fixed, so it works with the
    // header bar collapsed) instead of leaving the customizer. Without the
    // drawer the button stays what its markup is, a link home.
    var menuBtn = document.getElementById('sm2-home-btn');
    var drawerToggle = document.querySelector('header-drawer summary');
    if (menuBtn && drawerToggle) {
      menuBtn.addEventListener('click', function (e) {
        e.preventDefault();
        drawerToggle.click();
      });
    }

    if (!btn || !icon) return;
    btn.addEventListener('click', function () { icon.click(); });

    function syncCount() {
      if (!badge) return;
      var bubble = icon.querySelector('.cart-count-bubble');
      // The bubble holds the count twice: the visible digit
      // (span[aria-hidden]) and screen-reader text ("5 items"). Joining every
      // digit in it read 5 as 55. Take the visible number; with 100+ items
      // the theme shows a dot instead, so fall back to the first number.
      var n = 0;
      if (bubble) {
        var vis = bubble.querySelector('span[aria-hidden="true"]');
        var m = ((vis ? vis.textContent : bubble.textContent) || '').match(/\d+/);
        n = m ? parseInt(m[0], 10) : 0;
      }
      badge.hidden = n === 0;
      badge.textContent = n > 99 ? '99+' : String(n);
      btn.setAttribute('aria-label', n ? 'Open cart, ' + n + (n === 1 ? ' item' : ' items') : 'Open cart');
    }
    syncCount();
    if (window.MutationObserver) {
      new MutationObserver(syncCount).observe(icon, { childList: true, subtree: true, characterData: true });
    }
  }

  // The toolbar uses the same preference and controller as the storefront.
  function bindThemeToggle() {
    var btn = document.getElementById('sm2-toolbar-theme');
    if (btn && window.SkylitTheme) window.SkylitTheme.bind(btn);
  }

  function init() {
    seedDefaults();
    renderThemeTiles();
    bindEvents();
    bindMobilePreview();
    bindStepIndicator();
    // Handwriting sliders: fill, grab, rubber-band ends (skylit-motion.js).
    if (window.SkylitSlider) {
      ['sm2-hw-width', 'sm2-hw-modal-width', 'sm2-hw-size', 'sm2-hw-height'].forEach(function (id) {
        window.SkylitSlider.enhance(document.getElementById(id));
      });
    }
    bindToolbarFab();
    bindPrintViewer();
    bindRoomView();
    bindMobileChrome();
    bindThemeToggle();
    bindHandwriting();
    goToStep(1);
    syncGlobals();
    syncLegacyInputs();

    // Phase C: cart bootstrap. First scrape any existing _OrderID lines from
    // the live Shopify cart (so editMode detection works), then check the URL
    // for ?order_id= (edit) or ?saved= (resume); hydrate from admin if so.
    // Done sequentially — initFromUrl depends on cartOrderIds being populated.
    // Add-to-cart navigates to /cart, so pressing Back restores this page from
    // the back-forward cache with the pre-add cart snapshot still in state —
    // it would offer "Add to cart" for a design already in the cart. Re-read
    // the live cart on restore so edit mode and the label stay honest (the add
    // path purges by live cart state regardless).
    window.addEventListener('pageshow', function(event) {
      if (!event.persisted) return;
      // Back from the cart restores the page as it was when it left: the
      // button mid-"Added" and disabled. Hand it back first.
      var restoreBtn = document.getElementById('sm2-add-to-cart');
      atcMorph.reset(true);
      if (restoreBtn) {
        restoreBtn.disabled = false;
        if (/…$/.test(restoreBtn.textContent)) restoreBtn.textContent = 'Add to cart';
      }
      Cart.loadCartState().then(function() {
        state.editMode = state.cartOrderIds.indexOf(state.orderId) !== -1;
        var btn = document.getElementById('sm2-add-to-cart');
        if (btn && state.editMode) btn.textContent = 'Confirm edits';
        console.log('[sm2.cart] bfcache restore — cart re-read, editMode=' + state.editMode);
      });
    });

    Cart.loadCartState()
      .then(function() { return Cart.initFromUrl(); })
      .then(function() {
        // Arm autosave only after seed + URL hydrate complete, so the seed's
        // default state isn't itself written as a draft. Skip priming when a
        // resume modal is pending — otherwise we'd persist a stub server-side
        // order before the customer picks Resume or Start fresh. The modal's
        // branches prime autosave themselves.
        var sm2HasDraft = false;
        try { sm2HasDraft = !!localStorage.getItem('sm2_last_draft'); } catch (e) {}
        var sm2Params = new URLSearchParams(window.location.search);
        var sm2IsPreset = !!sm2Params.get('preset');
        var sm2HasUrlOrder = sm2Params.get('order_id') || sm2Params.get('saved') || sm2Params.get('preset');
        if (sm2IsPreset) {
          // A freshly-opened preset isn't a draft yet — arm autosave on the
          // first real interaction so browsing a preset doesn't persist a draft
          // (and fire a "new customisation" push) before the customer edits.
          Cart.primeAutosaveOnFirstInteraction();
        } else if (!sm2HasDraft || sm2HasUrlOrder) {
          Cart.primeAutosave();
        }
        // Offer to resume a previous unfinished draft when the user lands on
        // a clean URL (no ?order_id / ?saved / ?preset).
        Cart.resumeDraftIfAny();
      })
      .catch(function(e) { console.warn('[sm2.cart] boot failed', e); });

    // If Google Maps already fired before we loaded, bind now.
    if (window._sm2PlacesQueued) bindPlacesAutocomplete();

    // Seed a default milkyway render position — these are D3 projection
    // rotation angles (NOT lat/lng), copied verbatim from v1 starmap-render-engine.js:1287.
    milkywayConfig.center = [209.75, 53.48, -110];

    initCelestial();

    // Let Celestial set up its canvas, then kick the milkyway renderer.
    // applyGeoposToCelestial/applyDateToCelestial fire after to sync the
    // shader to the actual projection rotation once Celestial has settled.
    setTimeout(function() {
      try { ensureMilkywayContainer(); } catch (e) { console.error('[sm2.boot] ensureMW failed', e); }
      try { render({}); } catch (e) { console.error('[sm2.boot] render failed', e); }
      try { squareUpCelestialMap(); } catch (e) { console.error('[sm2.boot] squareUp failed', e); }
      // applyTheme() seeds milkywayConfig from THEMES[state.themeKey] and
      // kicks renderMilkyWay. Previously this happened as a side effect of
      // setState's broken condition (prev.themeKey was undefined for any
      // patch lacking themeKey, so applyTheme fired on every setState and
      // initialised the milkyway texture before boot ever reached this
      // point). Now that setState is properly gated, init must seed the
      // milkyway URL itself or the WebGL texture loads from the stale
      // milkywayConfig defaults.
      try { applyTheme(); } catch (e) { console.error('[sm2.boot] applyTheme failed', e); }
      var main = document.querySelector('.sm2-main');
      if (main) main.classList.add('sm2-ready');
      // Defer geopos/date push to Celestial until stars data has loaded —
      // calling them during boot can race the initial render and clear stars.
      setTimeout(function() {
        applyGeoposToCelestial();
        applyDateToCelestial();
        // Push the default element visibility (grid/labels/planets off) into
        // Celestial via applyQuiet — avoids the full reload() that was
        // wiping the star canvas.
        applyElements();
        applyBorder();
        applyBackground();
      }, 800);
    }, 500);
  }

  // ── Handwriting helpers (mirror locationmap.js) ──
  var SM_HW_VBW = 1000, SM_HW_VBH = 240;
  // Sample de-noising, mirrored by render_common.smooth_handwriting_points on
  // the print side. The pad captures in whole CSS pixels on a ~358px-wide
  // canvas mapped into a 1000-unit viewBox, so every point lands on a ~2.79
  // unit lattice. That is invisible in the small print preview but shows as a
  // zig-zag in the expanded pad and at print resolution. A short symmetric
  // moving average removes it; max excursion is under one lattice step, so
  // letterforms do not move. Endpoints are pinned and strokes shorter than the
  // kernel (dots, accents) pass through untouched. Applied at DRAW time only —
  // state.handwritingStrokes keeps the raw samples, so the stored order data
  // stays lossless and the renderer does its own identical pass.
  var SM_HW_KERNEL = [1, 2, 3, 2, 1];
  function smHwSmoothPts(pts) {
    if (!pts || !pts.length) return pts || [];
    var clean = [pts[0]];
    for (var i = 1; i < pts.length; i++) {
      var last = clean[clean.length - 1];
      if (Math.abs(pts[i][0] - last[0]) > 1e-6 || Math.abs(pts[i][1] - last[1]) > 1e-6) clean.push(pts[i]);
    }
    if (clean.length < SM_HW_KERNEL.length) return clean;
    var half = SM_HW_KERNEL.length >> 1;
    var out = [clean[0]];
    for (var k = 1; k < clean.length - 1; k++) {
      var sx = 0, sy = 0, ws = 0;
      for (var j = 0; j < SM_HW_KERNEL.length; j++) {
        var idx = k + j - half;
        if (idx >= 0 && idx < clean.length) {
          sx += clean[idx][0] * SM_HW_KERNEL[j];
          sy += clean[idx][1] * SM_HW_KERNEL[j];
          ws += SM_HW_KERNEL[j];
        }
      }
      out.push([sx / ws, sy / ws]);
    }
    out.push(clean[clean.length - 1]);
    return out;
  }

  function smHwStrokesToPathD(strokes) {
    var out = [];
    for (var i = 0; i < strokes.length; i++) {
      var s = strokes[i];
      if (!s.pts || s.pts.length < 1) continue;
      s = { pts: smHwSmoothPts(s.pts), w: s.w };
      var d = 'M ' + s.pts[0][0].toFixed(1) + ' ' + s.pts[0][1].toFixed(1);
      if (s.pts.length === 1) {
        // A lone moveto is NOT stroked, so the dot on an i or j, a full stop,
        // any quick tap, silently vanished from this preview and from every
        // render. A zero-length segment makes the round linecap paint it. The
        // pad canvas always drew these (ctx.lineTo(x+0.01, y+0.01)), which is
        // why they looked fine while drawing and were missing everywhere else.
        d += ' L ' + s.pts[0][0].toFixed(1) + ' ' + s.pts[0][1].toFixed(1);
      } else if (s.pts.length <= 2) {
        for (var j = 1; j < s.pts.length; j++) d += ' L ' + s.pts[j][0].toFixed(1) + ' ' + s.pts[j][1].toFixed(1);
      } else {
        // Midpoint quadratic smoothing — mirrors the pad/thumbnail/renderer.
        for (var j = 1; j < s.pts.length - 1; j++) {
          var cpt = s.pts[j], npt = s.pts[j + 1];
          d += ' Q ' + cpt[0].toFixed(1) + ' ' + cpt[1].toFixed(1) + ' ' +
               ((cpt[0] + npt[0]) / 2).toFixed(1) + ' ' + ((cpt[1] + npt[1]) / 2).toFixed(1);
        }
        d += ' L ' + s.pts[s.pts.length - 1][0].toFixed(1) + ' ' + s.pts[s.pts.length - 1][1].toFixed(1);
      }
      out.push({ d: d, w: s.w || 4 });
    }
    return out;
  }
  function smHwHasContent() {
    return state.handwritingEnabled && state.handwritingStrokes && state.handwritingStrokes.length > 0;
  }
  function smHwHTML() {
    var paths = smHwStrokesToPathD(state.handwritingStrokes);
    if (!paths.length) return '';
    var inner = '';
    for (var i = 0; i < paths.length; i++) {
      inner += '<path d="' + paths[i].d + '" stroke="currentColor" stroke-width="' + paths[i].w +
        '" stroke-linecap="round" stroke-linejoin="round" fill="none"/>';
    }
    var pct = Math.round((state.handwritingSize || 1) * 100);
    var hFactor = Math.max(0.4, Math.min(1.4, state.handwritingHeight || 1));
    var marginBottomPx = Math.round(4 + (hFactor - 1) * 28);
    // Left-anchored compositions (panel/header): the drawing pad is 1000
    // units wide but customers rarely start at its left edge, so the box's
    // own blank lead-in pushed the ink right of the details column. Shift
    // the viewBox origin to the ink's left edge (minus half the stroke
    // width) — a pure translate, so stroke size is unchanged. Mirrored in
    // render_star_map.py so the print matches.
    var vbx = 0;
    if (state.composition === 'panel' || state.composition === 'header') {
      var minX = Infinity, maxW = 0;
      for (var si = 0; si < state.handwritingStrokes.length; si++) {
        var st = state.handwritingStrokes[si];
        if (st.w > maxW) maxW = st.w;
        for (var pi = 0; pi < (st.pts || []).length; pi++) if (st.pts[pi][0] < minX) minX = st.pts[pi][0];
      }
      if (isFinite(minX)) vbx = Math.max(0, Math.round(minX - (maxW || 4) / 2));
    }
    var svg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="' + vbx + ' 0 ' + SM_HW_VBW + ' ' + SM_HW_VBH +
      '" preserveAspectRatio="xMidYMid meet" style="width:100%;height:auto;display:block;">' + inner + '</svg>';
    // Horizontal alignment is NOT inlined: .sm2-preview-handwriting in
    // starmap-v2.css centres it for classic/boxed and pins it left for the
    // panel/header compositions (an inline margin:0 auto used to win over
    // the composition rules and shoved handwriting right of the details).
    return '<div class="sm2-preview-handwriting" style="margin-bottom:' + marginBottomPx + 'px;width:' + pct + '%">' + svg + '</div>';
  }

  function bindHandwriting() {
    var row = document.getElementById('sm2-hw-row');
    var toggleCbx = document.getElementById('sm2-hw-toggle');
    var panel = document.getElementById('sm2-hw-panel');
    var canvas = document.getElementById('sm2-hw-canvas');
    var widthInput = document.getElementById('sm2-hw-width');
    var widthInputModal = document.getElementById('sm2-hw-modal-width');
    var sizeInput = document.getElementById('sm2-hw-size');
    var sizeVal = document.getElementById('sm2-hw-size-val');
    var heightInput = document.getElementById('sm2-hw-height');
    var heightVal = document.getElementById('sm2-hw-height-val');
    var undoBtn = document.getElementById('sm2-hw-undo');
    var clearBtn = document.getElementById('sm2-hw-clear');
    var msgInput = document.getElementById('sm2-message-input');
    if (!row || !toggleCbx || !panel || !canvas) return;

    var ctx = canvas.getContext('2d');
    var current = null;
    var activePointerId = null;
    var redrawScheduled = false;
    var dpr = Math.max(1, window.devicePixelRatio || 1);
    var defaultWidth = 4;
    var modalOpen = false;
    var palmRejectOn = false;
    var eraserOn = false;
    // Move mode -- drag the whole message around inside the pad. moveBase is
    // an immutable clone taken at drag start; each move applies the total
    // drag delta to that base (never cumulatively to live state), so pointer
    // jitter cannot drift the artwork. Ported from moon-handwriting.js.
    var moveOn = false;
    var moveBase = null;
    var moveStartVB = null;
    var hwHistory = [];
    var hwRedoStack = [];
    function cloneStrokes(arr) {
      return (arr || []).map(function(s) { return { w: s.w, pts: s.pts.slice() }; });
    }
    // Handwriting strokes / size / height / enabled-toggle bypass setState
    // and mutate state.handwriting* directly, so the autosave trigger that
    // setState would normally fire never runs. Each handwriting mutation
    // calls this so a draft persists strokes within ~2.5s of the last edit.
    function scheduleHwAutosave() {
      if (Cart && Cart.scheduleAutosaveDraft) Cart.scheduleAutosaveDraft();
    }
    function snapshotHwHistory() {
      hwHistory.push(cloneStrokes(state.handwritingStrokes));
      if (hwHistory.length > 50) hwHistory.shift();
      // Any new mutation invalidates the redo branch — standard editor behaviour.
      hwRedoStack.length = 0;
      syncRedoUI();
    }
    function undoHw() {
      if (!hwHistory.length) return;
      // Stash current state so redo can restore it.
      hwRedoStack.push(cloneStrokes(state.handwritingStrokes));
      if (hwRedoStack.length > 50) hwRedoStack.shift();
      state.handwritingStrokes = hwHistory.pop();
      redraw(); updateCaption(); syncGlobals(); syncRedoUI();
      scheduleHwAutosave();
    }
    function redoHw() {
      if (!hwRedoStack.length) return;
      // Stash current state so a subsequent undo can step back here.
      hwHistory.push(cloneStrokes(state.handwritingStrokes));
      if (hwHistory.length > 50) hwHistory.shift();
      state.handwritingStrokes = hwRedoStack.pop();
      redraw(); updateCaption(); syncGlobals(); syncRedoUI();
      scheduleHwAutosave();
    }
    function syncRedoUI() {
      var canUndo = hwHistory.length > 0;
      var canRedo = hwRedoStack.length > 0;
      ['sm2-hw-undo', 'sm2-hw-modal-undo'].forEach(function(id) {
        var b = document.getElementById(id);
        if (b) b.disabled = !canUndo;
      });
      ['sm2-hw-redo', 'sm2-hw-modal-redo'].forEach(function(id) {
        var b = document.getElementById(id);
        if (b) b.disabled = !canRedo;
      });
    }
    // Brush radius in viewBox units (canvas is 1000 wide). Roughly 2.5% of
    // canvas width — small enough to nibble at parts of a stroke without
    // wiping the whole letter.
    function brushSliderVal() {
      var v = parseFloat((widthInput && widthInput.value) || (widthInputModal && widthInputModal.value));
      return isFinite(v) && v > 0 ? v : 6;
    }
    function eraseRadiusVB() { return brushSliderVal() * 4; }
    // Legacy reference kept so older code paths still resolve a sane number.
    var ERASE_RADIUS_VB = 24;
    function syncBrushInputs(src) {
      var v = src.value;
      if (widthInput && widthInput !== src) widthInput.value = v;
      if (widthInputModal && widthInputModal !== src) widthInputModal.value = v;
    }
    if (widthInput) widthInput.addEventListener('input', function() { syncBrushInputs(widthInput); scheduleRedraw(); });
    if (widthInputModal) widthInputModal.addEventListener('input', function() { syncBrushInputs(widthInputModal); scheduleRedraw(); });

    // Photoshop-style brush eraser: walks each stroke point-by-point,
    // dropping points inside the brush, and splits strokes at the gaps so
    // partially-erased strokes survive as multiple shorter strokes.
    function eraseAtVB(vbx, vby) {
      var r = eraseRadiusVB();
      var r2 = r * r;
      var changed = false;
      var out = [];
      for (var i = 0; i < state.handwritingStrokes.length; i++) {
        var s = state.handwritingStrokes[i];
        if (!s.pts || !s.pts.length) continue;
        var run = [];
        var sawErase = false;
        for (var j = 0; j < s.pts.length; j++) {
          var dx = s.pts[j][0] - vbx, dy = s.pts[j][1] - vby;
          if (dx * dx + dy * dy <= r2) {
            sawErase = true;
            if (run.length >= 2) out.push({ w: s.w, pts: run });
            run = [];
          } else {
            run.push(s.pts[j]);
          }
        }
        if (!sawErase) {
          out.push(s);
        } else {
          changed = true;
          if (run.length >= 2) out.push({ w: s.w, pts: run });
        }
      }
      if (changed) {
        state.handwritingStrokes = out;
        redraw(); updateCaption(); syncGlobals();
        scheduleHwAutosave();
      }
    }

    function scheduleRedraw() {
      if (redrawScheduled) return;
      redrawScheduled = true;
      requestAnimationFrame(function() { redrawScheduled = false; redraw(); });
    }
    function sizeCanvas() {
      var rect = canvas.getBoundingClientRect();
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      redraw();
    }
    function rectXYToVB(rect, clientX, clientY) {
      var x = (clientX - rect.left) / rect.width * SM_HW_VBW;
      var y = (clientY - rect.top) / rect.height * SM_HW_VBH;
      return [Math.max(0, Math.min(SM_HW_VBW, x)), Math.max(0, Math.min(SM_HW_VBH, y))];
    }
    function vbToCss(rect, p) {
      return [p[0] / SM_HW_VBW * rect.width, p[1] / SM_HW_VBH * rect.height];
    }
    var lastEraserVB = null;
    function redraw() {
      var rect = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, rect.width, rect.height);
      var wrap = canvas.parentElement;
      if (wrap) wrap.classList.toggle('has-strokes', state.handwritingStrokes.length > 0 || !!current);
      ctx.strokeStyle = '#222';
      ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      var allStrokes = state.handwritingStrokes.concat(current ? [current] : []);
      for (var i = 0; i < allStrokes.length; i++) {
        var s = allStrokes[i];
        if (!s.pts || !s.pts.length) continue;
        // Draw the smoothed samples, not the raw ones, so the pad shows what
        // actually ships (see smHwSmoothPts). The expanded pad is wide enough
        // that the capture lattice was plainly visible here.
        s = { pts: smHwSmoothPts(s.pts), w: s.w };
        var widthPx = s.w * Math.min(rect.width / SM_HW_VBW, rect.height / SM_HW_VBH);
        ctx.lineWidth = Math.max(1, widthPx);
        ctx.beginPath();
        var p0 = vbToCss(rect, s.pts[0]);
        ctx.moveTo(p0[0], p0[1]);
        if (s.pts.length === 1) {
          ctx.lineTo(p0[0] + 0.01, p0[1] + 0.01);
        } else if (s.pts.length === 2) {
          var pl = vbToCss(rect, s.pts[1]);
          ctx.lineTo(pl[0], pl[1]);
        } else {
          // Midpoint quadratic smoothing — same curve construction as the
          // preview SVG (smHwStrokesToPathD), the cart thumbnail and the
          // print renderer, so the pad shows exactly what ships. Ported
          // from locationmap.js.
          for (var j = 1; j < s.pts.length - 1; j++) {
            var cp = vbToCss(rect, s.pts[j]);
            var np = vbToCss(rect, s.pts[j + 1]);
            ctx.quadraticCurveTo(cp[0], cp[1], (cp[0] + np[0]) / 2, (cp[1] + np[1]) / 2);
          }
          var pe = vbToCss(rect, s.pts[s.pts.length - 1]);
          ctx.lineTo(pe[0], pe[1]);
        }
        ctx.stroke();
      }
      if (eraserOn && lastEraserVB) {
        var c = vbToCss(rect, lastEraserVB);
        var rPx = eraseRadiusVB() * (rect.width / SM_HW_VBW);
        ctx.save();
        ctx.beginPath();
        ctx.arc(c[0], c[1], rPx, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(180,60,60,0.18)';
        ctx.fill();
        ctx.lineWidth = 2;
        ctx.strokeStyle = 'rgba(180,60,60,0.95)';
        ctx.stroke();
        ctx.restore();
      }
    }
    function commitCurrent() {
      if (current && current.pts.length) {
        snapshotHwHistory();
        state.handwritingStrokes.push(current);
      }
      current = null;
      updateCaption();
      syncGlobals();
      scheduleHwAutosave();
    }

    // -- Center / Move (ported from moon-handwriting.js) ------------------
    function strokesBBox(strokes) {
      var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (var i = 0; i < strokes.length; i++) {
        var pts = strokes[i].pts || [];
        for (var j = 0; j < pts.length; j++) {
          if (pts[j][0] < minX) minX = pts[j][0];
          if (pts[j][0] > maxX) maxX = pts[j][0];
          if (pts[j][1] < minY) minY = pts[j][1];
          if (pts[j][1] > maxY) maxY = pts[j][1];
        }
      }
      return minX === Infinity ? null : { minX: minX, minY: minY, maxX: maxX, maxY: maxY };
    }
    function translateStrokes(strokes, dx, dy) {
      return strokes.map(function(s) {
        return { w: s.w, pts: s.pts.map(function(p) { return [p[0] + dx, p[1] + dy]; }) };
      });
    }
    // Horizontal re-center: the vertical position (baseline relationship)
    // is the customer's own; only the left/right balance is corrected.
    function centerStrokes() {
      var box = strokesBBox(state.handwritingStrokes);
      if (!box) return;
      var dx = (SM_HW_VBW - (box.maxX - box.minX)) / 2 - box.minX;
      if (Math.abs(dx) < 0.5) return;
      snapshotHwHistory();
      state.handwritingStrokes = translateStrokes(state.handwritingStrokes, dx, 0);
      redraw();
      updateCaption();
      syncGlobals();
      scheduleHwAutosave();
    }

    function onPointerDown(e) {
      if (!state.handwritingEnabled) return;
      if (e.isPrimary === false) return;
      if (modalOpen && palmRejectOn && e.pointerType !== 'pen') return;
      e.preventDefault();
      var rectE = canvas.getBoundingClientRect();
      if (moveOn) {
        if (!state.handwritingStrokes.length) return;
        activePointerId = e.pointerId;
        try { canvas.setPointerCapture(e.pointerId); } catch (_) {}
        snapshotHwHistory();
        moveBase = state.handwritingStrokes.map(function(s) { return { w: s.w, pts: s.pts.slice() }; });
        moveStartVB = rectXYToVB(rectE, e.clientX, e.clientY);
        if (canvas.parentElement) canvas.parentElement.classList.add('move-dragging');
        return;
      }
      if (eraserOn) {
        activePointerId = e.pointerId;
        try { canvas.setPointerCapture(e.pointerId); } catch (_) {}
        snapshotHwHistory();
        var pE = rectXYToVB(rectE, e.clientX, e.clientY);
        lastEraserVB = pE;
        eraseAtVB(pE[0], pE[1]);
        scheduleRedraw();
        return;
      }
      if (current) commitCurrent();
      activePointerId = e.pointerId;
      try { canvas.setPointerCapture(e.pointerId); } catch (_) {}
      var w = parseFloat(widthInput && widthInput.value) || defaultWidth;
      if (e.pointerType === 'pen' && e.pressure > 0) w = w * (0.6 + 0.8 * e.pressure);
      current = { w: w, pts: [rectXYToVB(rectE, e.clientX, e.clientY)] };
      scheduleRedraw();
    }
    function onPointerMove(e) {
      if (e.pointerId !== activePointerId) return;
      if (modalOpen && palmRejectOn && e.pointerType !== 'pen') return;
      if (moveOn) {
        if (!moveBase || !moveStartVB) return;
        if (e.cancelable) e.preventDefault();
        var rectMv = canvas.getBoundingClientRect();
        var curVB = rectXYToVB(rectMv, e.clientX, e.clientY);
        var dxm = curVB[0] - moveStartVB[0];
        var dym = curVB[1] - moveStartVB[1];
        // Clamp so the message's bounding box never leaves the pad.
        var boxM = strokesBBox(moveBase);
        if (boxM) {
          dxm = Math.max(-boxM.minX, Math.min(SM_HW_VBW - boxM.maxX, dxm));
          dym = Math.max(-boxM.minY, Math.min(SM_HW_VBH - boxM.maxY, dym));
        }
        state.handwritingStrokes = translateStrokes(moveBase, dxm, dym);
        scheduleRedraw();
        return;
      }
      if (eraserOn) {
        if (e.cancelable) e.preventDefault();
        var rectM = canvas.getBoundingClientRect();
        var evs = (typeof e.getCoalescedEvents === 'function') ? e.getCoalescedEvents() : [e];
        for (var k = 0; k < evs.length; k++) {
          var pM = rectXYToVB(rectM, evs[k].clientX, evs[k].clientY);
          lastEraserVB = pM;
          eraseAtVB(pM[0], pM[1]);
        }
        scheduleRedraw();
        return;
      }
      if (!current) return;
      if (e.cancelable) e.preventDefault();
      var rect = canvas.getBoundingClientRect();
      var events = (typeof e.getCoalescedEvents === 'function') ? e.getCoalescedEvents() : [e];
      for (var i = 0; i < events.length; i++) {
        var np = rectXYToVB(rect, events[i].clientX, events[i].clientY);
        // Decimate: skip samples closer than ~1.5 viewBox units to the last
        // point. Coalesced pointer streams carry sensor jitter at sub-unit
        // spacing which reads as jagged ink; the midpoint-quad smoothing in
        // the renderers works best on sparser, cleaner points. This also
        // evens out the inline-pad vs expand-modal feel: the small canvas
        // samples coarsely, the big one densely — decimating in viewBox
        // units gives both the same effective point spacing. (Ported from
        // locationmap.js.)
        var lp = current.pts[current.pts.length - 1];
        if (lp) {
          var ddx = np[0] - lp[0], ddy = np[1] - lp[1];
          if (ddx * ddx + ddy * ddy < 2.25) continue;
        }
        current.pts.push(np);
      }
      scheduleRedraw();
    }
    function onPointerUp() {
      if (moveOn) {
        if (moveBase) {
          moveBase = null; moveStartVB = null;
          activePointerId = null;
          document.querySelectorAll('.sm2-hw-canvas-wrap').forEach(function(w) {
            w.classList.remove('move-dragging');
          });
          updateCaption();
      syncGlobals();
      scheduleHwAutosave();
        }
        return;
      }
      if (eraserOn) { activePointerId = null; lastEraserVB = null; scheduleRedraw(); return; }
      if (!current) return;
      activePointerId = null;
      commitCurrent();
    }

    canvas.addEventListener('pointerdown', onPointerDown);
    // Non-passive so we can preventDefault — fast Apple Pencil drags
    // otherwise initiate native text-selection on the surrounding modal.
    window.addEventListener('pointermove', onPointerMove, { passive: false });
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    // Block iOS native gestures (selection, callout, drag) during a stroke
    // anywhere inside the modal — pointer events alone don't suppress these
    // when they originate from a fast pencil swipe outside the canvas rect.
    function blockIfDrawing(e) { if ((current || moveBase || (eraserOn && activePointerId !== null)) && e.cancelable) e.preventDefault(); }
    document.addEventListener('selectstart', blockIfDrawing);
    document.addEventListener('gesturestart', blockIfDrawing);
    document.addEventListener('touchmove', blockIfDrawing, { passive: false });
    canvas.style.touchAction = 'none';

    if (undoBtn) undoBtn.addEventListener('click', undoHw);
    var redoBtn = document.getElementById('sm2-hw-redo');
    if (redoBtn) redoBtn.addEventListener('click', redoHw);
    if (clearBtn) clearBtn.addEventListener('click', function() {
      if (state.handwritingStrokes.length) snapshotHwHistory();
      state.handwritingStrokes = []; redraw(); updateCaption(); syncGlobals();
      scheduleHwAutosave();
    });

    var eraserBtn = document.getElementById('sm2-hw-eraser');
    var eraserBtnModal = document.getElementById('sm2-hw-modal-eraser');
    function syncEraserUI() {
      var pressed = eraserOn ? 'true' : 'false';
      if (eraserBtn) eraserBtn.setAttribute('aria-pressed', pressed);
      if (eraserBtnModal) eraserBtnModal.setAttribute('aria-pressed', pressed);
      // Mark wrap so CSS can swap cursor + visual hint.
      var wraps = [canvas.parentElement];
      for (var i = 0; i < wraps.length; i++) {
        if (wraps[i]) wraps[i].classList.toggle('eraser-on', eraserOn);
      }
    }
    function toggleEraser() { eraserOn = !eraserOn; if (!eraserOn) lastEraserVB = null; if (eraserOn && moveOn) setMove(false); syncEraserUI(); updateEraserCursorVisibility(); scheduleRedraw(); }
    if (eraserBtn) eraserBtn.addEventListener('click', toggleEraser);
    if (eraserBtnModal) eraserBtnModal.addEventListener('click', toggleEraser);

    // Center + Move controls -- inline panel and expand modal share state,
    // mirroring the eraser pattern. Move and Eraser are mutually exclusive.
    var centerBtn = document.getElementById('sm2-hw-center');
    var centerBtnModal = document.getElementById('sm2-hw-modal-center');
    if (centerBtn) centerBtn.addEventListener('click', centerStrokes);
    if (centerBtnModal) centerBtnModal.addEventListener('click', centerStrokes);

    var moveBtn = document.getElementById('sm2-hw-move');
    var moveBtnModal = document.getElementById('sm2-hw-modal-move');
    function setMove(on) {
      moveOn = !!on;
      if (!moveOn) { moveBase = null; moveStartVB = null; }
      var pressedM = moveOn ? 'true' : 'false';
      if (moveBtn) moveBtn.setAttribute('aria-pressed', pressedM);
      if (moveBtnModal) moveBtnModal.setAttribute('aria-pressed', pressedM);
      document.querySelectorAll('.sm2-hw-canvas-wrap').forEach(function(w) {
        w.classList.toggle('move-on', moveOn);
      });
    }
    function toggleMove() {
      if (!moveOn && eraserOn) toggleEraser();
      setMove(!moveOn);
    }
    if (moveBtn) moveBtn.addEventListener('click', toggleMove);
    if (moveBtnModal) moveBtnModal.addEventListener('click', toggleMove);


    var eraserCursorEl = document.createElement('div');
    eraserCursorEl.className = 'sm2-hw-eraser-cursor';
    var pointerInside = false;
    function attachEraserCursor() {
      if (eraserCursorEl.parentElement !== document.body) document.body.appendChild(eraserCursorEl);
    }
    attachEraserCursor();
    function updateEraserCursorVisibility() {
      eraserCursorEl.classList.toggle('visible', !!eraserOn);
    }
    function moveEraserCursor(clientX, clientY) {
      var r = canvas.getBoundingClientRect();
      var diameter = Math.max(16, 2 * ERASE_RADIUS_VB * (r.width / SM_HW_VBW));
      eraserCursorEl.style.width = diameter + 'px';
      eraserCursorEl.style.height = diameter + 'px';
      eraserCursorEl.style.left = clientX + 'px';
      eraserCursorEl.style.top = clientY + 'px';
    }
    function showEraserCursorAt(e) {
      if (!eraserOn) return;
      attachEraserCursor();
      moveEraserCursor(e.clientX, e.clientY);
      pointerInside = true;
      updateEraserCursorVisibility();
    }
    canvas.addEventListener('pointerdown', showEraserCursorAt, true);
    canvas.addEventListener('pointermove', showEraserCursorAt, true);
    canvas.addEventListener('pointerenter', showEraserCursorAt, true);

    function syncSizeUI() {
      if (sizeInput) {
        sizeInput.value = String(Math.round((state.handwritingSize || 0.75) * 100));
        if (sizeVal) sizeVal.textContent = sizeInput.value + '%';
      }
      if (heightInput) {
        heightInput.value = String(Math.round((state.handwritingHeight || 1) * 100));
        if (heightVal) heightVal.textContent = heightInput.value + '%';
      }
    }
    if (sizeInput) sizeInput.addEventListener('input', function() {
      state.handwritingSize = Math.max(0.2, Math.min(1.1, parseInt(sizeInput.value, 10) / 100));
      if (sizeVal) sizeVal.textContent = sizeInput.value + '%';
      updateCaption(); syncGlobals();
      scheduleHwAutosave();
    });
    if (heightInput) heightInput.addEventListener('input', function() {
      state.handwritingHeight = Math.max(0.4, Math.min(1.4, parseInt(heightInput.value, 10) / 100));
      if (heightVal) heightVal.textContent = heightInput.value + '%';
      updateCaption(); syncGlobals();
      scheduleHwAutosave();
    });

    function syncToggleUI() {
      var hw = !!state.handwritingEnabled;
      toggleCbx.checked = hw;
      panel.style.display = hw ? '' : 'none';
      // Typed/Handwritten pill switch: either the textarea+hint or the
      // canvas panel is visible, never both. The Inspiration button only
      // writes into the textarea, so it hides with it.
      row.querySelectorAll('[data-hw-mode]').forEach(function(btn) {
        btn.classList.toggle('active', (btn.getAttribute('data-hw-mode') === 'handwritten') === hw);
      });
      if (msgInput) {
        msgInput.disabled = hw;
        msgInput.style.display = hw ? 'none' : '';
      }
      var msgHint = document.getElementById('sm2-message-hint');
      if (msgHint) msgHint.style.display = hw ? 'none' : '';
      var inspBtn = document.getElementById('sm2-open-inspiration');
      if (inspBtn) inspBtn.style.display = hw ? 'none' : '';
      // Emoji pill only writes into the typed textarea — hide it with
      // Inspiration. Empty string restores the stylesheet display, which
      // keeps the ≤768px desktop-only hide intact.
      var emojiBtn = document.getElementById('sm2-open-emoji');
      if (emojiBtn) emojiBtn.style.display = hw ? 'none' : '';
      if (hw) requestAnimationFrame(sizeCanvas);
    }
    toggleCbx.addEventListener('change', function() {
      state.handwritingEnabled = !!toggleCbx.checked;
      syncToggleUI();
      updateCaption();
      syncGlobals();
      scheduleHwAutosave();
    });
    row.querySelectorAll('[data-hw-mode]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var hw = btn.getAttribute('data-hw-mode') === 'handwritten';
        if (hw === !!state.handwritingEnabled) return;
        toggleCbx.checked = hw;
        toggleCbx.dispatchEvent(new Event('change'));
      });
    });

    var modal = document.getElementById('sm2-hw-modal');
    var modalWrap = document.getElementById('sm2-hw-modal-wrap');
    var modalBackdrop = document.getElementById('sm2-hw-modal-backdrop');
    var modalDone = document.getElementById('sm2-hw-modal-done');
    var modalUndo = document.getElementById('sm2-hw-modal-undo');
    var modalClear = document.getElementById('sm2-hw-modal-clear');
    var expandBtn = document.getElementById('sm2-hw-expand');
    var inlineWrap = canvas.parentElement;
    var palmToggleBtn = document.getElementById('sm2-hw-palm-toggle');
    var palmToggleLabel = document.getElementById('sm2-hw-palm-toggle-label');
    function updatePalmRejectionUI() {
      if (!modal) return;
      modal.classList.toggle('pen-locked', palmRejectOn);
      if (palmToggleBtn) palmToggleBtn.setAttribute('aria-pressed', palmRejectOn ? 'true' : 'false');
      if (palmToggleLabel) palmToggleLabel.textContent = 'Palm rejection: ' + (palmRejectOn ? 'on' : 'off');
    }
    if (palmToggleBtn) palmToggleBtn.addEventListener('click', function() {
      palmRejectOn = !palmRejectOn; updatePalmRejectionUI();
    });
    function openModal() {
      if (!modal || !modalWrap) return;
      // Strip eraser marker from wherever the canvas was, before moving.
      if (canvas.parentElement) canvas.parentElement.classList.remove('eraser-on');
      modalWrap.appendChild(canvas);
      attachEraserCursor();
      syncEraserUI();
      modal.classList.add('active');
      modal.setAttribute('aria-hidden', 'false');
      modalOpen = true;
      updatePalmRejectionUI();
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(function() { sizeCanvas(); });
    }
    function closeModal() {
      if (!modal || !inlineWrap) return;
      if (canvas.parentElement) canvas.parentElement.classList.remove('eraser-on');
      inlineWrap.appendChild(canvas);
      attachEraserCursor();
      syncEraserUI();
      modal.classList.remove('active');
      modal.setAttribute('aria-hidden', 'true');
      modalOpen = false;
      document.body.style.overflow = '';
      requestAnimationFrame(function() { sizeCanvas(); });
    }
    if (expandBtn) expandBtn.addEventListener('click', openModal);
    if (modalDone) modalDone.addEventListener('click', closeModal);
    if (modalBackdrop) modalBackdrop.addEventListener('click', closeModal);
    if (modalUndo) modalUndo.addEventListener('click', undoHw);
    var modalRedo = document.getElementById('sm2-hw-modal-redo');
    if (modalRedo) modalRedo.addEventListener('click', redoHw);
    syncRedoUI();
    if (modalClear) modalClear.addEventListener('click', function() {
      if (state.handwritingStrokes.length) snapshotHwHistory();
      state.handwritingStrokes = []; redraw(); updateCaption(); syncGlobals();
      scheduleHwAutosave();
    });
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && modal && modal.classList.contains('active')) closeModal();
    });

    syncToggleUI();
    syncSizeUI();
    // Hydration path: panel just became visible. Canvas needs sizeCanvas
    // (resets backing store + paints) AFTER layout flushes, otherwise
    // getBoundingClientRect returns 0×0 and strokes don't appear until the
    // user clicks Expand. Double rAF is the most reliable way to wait for
    // the post-display:none → display:block layout pass.
    window.sm2HandwritingSyncUI = function() {
      syncToggleUI(); syncSizeUI();
      requestAnimationFrame(function() {
        sizeCanvas();
        requestAnimationFrame(sizeCanvas);
      });
    };
    window.addEventListener('resize', function() { if (state.handwritingEnabled) sizeCanvas(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      if (!window.__SM2_HEADLESS) init();
    });
  } else {
    if (!window.__SM2_HEADLESS) init();
  }
})();

/* ── Message inspiration modal ─────────────────────────────────────────
   Self-contained IIFE. Opens from #sm2-open-inspiration, populates
   #sm2-message-input on apply, and dispatches an 'input' event so the
   long-line nudge (updateMessageNudge) re-evaluates. Content ported
   from v1's star-map inspiration modal with an added 'engagement'
   category for v2's engagement-star-map product.
   ───────────────────────────────────────────────────────────────────── */
(function () {
  var INSPIRATION = {
    anniversary: [
      "Written In The Stars\nThe Night We Met",
      "Under This Sky\nOur Love Story Began",
      "The Stars Aligned\nForever And Always",
      "A Night To Remember\nOur Anniversary",
      "Every Star Shines\nFor Our Love"
    ],
    love: [
      "I Love You\nTo The Stars And Back",
      "You Are My Star\nMy Universe",
      "Written In The Stars\nTold By You",
      "The Night Sky Knows\nHow Much I Love You",
      "Our Love Shines Brighter\nThan Any Star"
    ],
    engagement: [
      "The Night You Said Yes\nBeneath These Stars",
      "Under This Sky\nYou Said Forever",
      "The Stars Aligned\nThe Day We Said Yes",
      "A Promise Written\nIn The Stars",
      "Our Forever Began\nUnder This Sky"
    ],
    wedding: [
      "Under These Stars\nWe Said I Do",
      "The Night We Became One\nForever Yours",
      "Written In The Stars\nOur Wedding Day",
      "Two Hearts United\nBeneath This Sky",
      "The Stars Witnessed\nOur Love Story Begin"
    ],
    baby: [
      "A Star Was Born\nWelcome To The World",
      "The Night You Arrived\nOur Lives Changed Forever",
      "Born Under These Stars\nOur Little Miracle",
      "The Stars Welcomed You\nWith Open Arms",
      "A New Star Shines\nIn Our Universe"
    ],
    birthday: [
      "Another Trip Around The Sun\nHappy Birthday",
      "The Stars Celebrate\nYour Special Day",
      "Born Under Lucky Stars\nHappy Birthday",
      "The Sky On Your Birthday\nA Gift From The Stars",
      "Celebrate Under The Stars\nAnother Year Of Wonder"
    ],
    family: [
      "Our Family\nWritten In The Stars",
      "Together Under\nThe Same Stars",
      "Family Is Everything\nOur Stars Align",
      "Bound By Love\nGuided By Stars",
      "Our Universe\nOur Family"
    ],
    memorial: [
      "Forever In Our Hearts\nForever In The Stars",
      "The Stars Remember\nThose We Love",
      "Gone But Not Forgotten\nShining Above",
      "A Star Shines Bright\nIn Your Memory",
      "We Look At These Stars\nAnd Think Of You"
    ],
    poetic: [
      "We Are All Made\nOf Stardust",
      "The Stars Are The Streetlights\nOf Eternity",
      "Reach For The Stars\nThey Are Closer Than You Think",
      "In The Silence Of Night\nThe Stars Speak",
      "Dance With The Stars\nDream With The Sky"
    ]
  };

  var CATEGORY_ORDER = [
    { key: 'anniversary', label: 'Anniversary' },
    { key: 'love',        label: 'Love' },
    { key: 'engagement',  label: 'Engagement' },
    { key: 'wedding',     label: 'Wedding' },
    { key: 'baby',        label: 'Baby' },
    { key: 'birthday',    label: 'Birthday' },
    { key: 'family',      label: 'Family' },
    { key: 'memorial',    label: 'Memorial' },
    { key: 'poetic',      label: 'Poetic' }
  ];

  var selected = null;

  function $(id) { return document.getElementById(id); }

  function renderCategories() {
    var wrap = $('sm2-insp-categories');
    if (!wrap) return;
    wrap.innerHTML = '';
    CATEGORY_ORDER.forEach(function (c, i) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sm2-insp-cat-btn' + (i === 0 ? ' active' : '');
      btn.setAttribute('role', 'tab');
      btn.dataset.category = c.key;
      btn.textContent = c.label;
      btn.addEventListener('click', function () {
        wrap.querySelectorAll('.sm2-insp-cat-btn').forEach(function (b) {
          b.classList.remove('active');
        });
        btn.classList.add('active');
        renderItems(c.key);
      });
      wrap.appendChild(btn);
    });
  }

  function renderItems(category) {
    var container = $('sm2-insp-items');
    var applyBtn = $('sm2-insp-apply');
    if (!container) return;
    var items = INSPIRATION[category] || [];
    container.innerHTML = '';
    selected = null;
    if (applyBtn) applyBtn.disabled = true;
    items.forEach(function (text) {
      var item = document.createElement('div');
      item.className = 'sm2-insp-item';
      item.setAttribute('role', 'option');
      item.innerHTML = '<p></p>';
      item.querySelector('p').textContent = text;
      item.addEventListener('click', function () {
        container.querySelectorAll('.sm2-insp-item').forEach(function (el) {
          el.classList.remove('selected');
        });
        item.classList.add('selected');
        selected = text;
        if (applyBtn) applyBtn.disabled = false;
      });
      container.appendChild(item);
    });
  }

  function open() {
    var modal = $('sm2-insp-modal');
    if (!modal) return;
    renderCategories();
    renderItems(CATEGORY_ORDER[0].key);
    modal.classList.add('active');
  }

  function close() {
    var modal = $('sm2-insp-modal');
    if (modal) modal.classList.remove('active');
  }

  function apply() {
    if (!selected) return;
    var ta = $('sm2-message-input');
    if (ta) {
      ta.value = selected;
      // Dispatch input so the existing long-line nudge re-evaluates and any
      // state-sync (font scaling, preview render) picks up the new value.
      ta.dispatchEvent(new Event('input', { bubbles: true }));
      ta.focus();
    }
    close();
  }

  function init() {
    var openBtn = $('sm2-open-inspiration');
    if (!openBtn) return;
    openBtn.addEventListener('click', function (e) {
      e.preventDefault();
      open();
    });
    var modal = $('sm2-insp-modal');
    if (modal) {
      modal.querySelectorAll('[data-insp-close]').forEach(function (el) {
        el.addEventListener('click', close);
      });
    }
    var applyBtn = $('sm2-insp-apply');
    if (applyBtn) applyBtn.addEventListener('click', apply);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal && modal.classList.contains('active')) {
        close();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      if (!window.__SM2_HEADLESS) init();
    });
  } else {
    if (!window.__SM2_HEADLESS) init();
  }
})();

// ============================================
// TOOLTIP TAP-TO-TOGGLE
// Hover-only tooltips flash and vanish on phones: the tap applies WebKit's
// simulated :hover, but the same tap clicks the parent tile, whose handler
// mutates the DOM — and WebKit drops simulated hover on DOM mutation.
// Tapping the "?" now toggles .sm2-tooltip-open instead. Capture phase so
// stopPropagation runs BEFORE the delegated tile-select handlers — a tap
// on the "?" must open the tooltip, not also select/deselect the tile.
// Mirrors the family-moon fix in hor-moon-myjs.js.
// ============================================
document.addEventListener('click', function (e) {
    var icon = e.target.closest ? e.target.closest('.sm2-tooltip-icon') : null;
    var open = document.querySelectorAll('.sm2-tooltip-icon.sm2-tooltip-open');
    var i;
    if (!icon) {
        for (i = 0; i < open.length; i++) open[i].classList.remove('sm2-tooltip-open');
        return;
    }
    e.stopPropagation();
    var wasOpen = icon.classList.contains('sm2-tooltip-open');
    for (i = 0; i < open.length; i++) open[i].classList.remove('sm2-tooltip-open');
    if (!wasOpen) icon.classList.add('sm2-tooltip-open');
}, true);

/* ── Emoji picker — ported from the moon customizer ─────────────────────
   Self-contained IIFE. Opens from #sm2-open-emoji (desktop-only pill next
   to Inspiration), reuses the sm2-insp-modal chrome and category-button
   styling, inserts the chosen emoji at the caret in #sm2-message-input and
   dispatches 'input' so the long-line nudge + preview re-evaluate. Emoji
   sets match the moon customizer's picker.
   ───────────────────────────────────────────────────────────────────── */
(function () {
  var EMOJIS = {
    smileys: ['😀', '😃', '😄', '😁', '😊', '😍', '🥰', '😘', '😗', '😙', '😚', '☺️', '😌', '😉', '😋', '😎', '🤗', '🤩', '🥳', '😏', '😬', '😐', '😑', '😶', '🙄', '😴'],
    nature: ['🌟', '⭐', '✨', '💫', '🌙', '🌛', '🌜', '🌝', '🌞', '☀️', '🌈', '☁️', '⛅', '🌤', '🌥', '🌦', '🌧', '⛈', '🌩', '🌨', '❄️', '☃️', '⛄', '🌬', '💨', '🌪'],
    symbols: ['💖', '💗', '💓', '💞', '💕', '💘', '💝', '💟', '❤️', '🧡', '💛', '💚', '💙', '💜', '🤎', '🖤', '🤍', '💯', '💢', '💥', '💫', '💦', '💨', '🕳', '💬', '👁️‍🗨️'],
    activities: ['🎉', '🎊', '🎈', '🎀', '🎁', '🎂', '🎄', '🎃', '🎆', '🎇', '🧨', '✨', '🎋', '🎍', '🎎', '🎏', '🎐', '🎑', '🧧', '🎗', '🎟', '🎫', '🎖', '🏆', '🏅', '🥇']
  };
  var CATEGORY_ORDER = [
    { key: 'smileys',    label: 'Faces' },
    { key: 'nature',     label: 'Nature' },
    { key: 'symbols',    label: 'Symbols' },
    { key: 'activities', label: 'Activities' }
  ];

  function $(id) { return document.getElementById(id); }

  function renderCategories() {
    var wrap = $('sm2-emoji-categories');
    if (!wrap) return;
    wrap.innerHTML = '';
    CATEGORY_ORDER.forEach(function (c, i) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sm2-insp-cat-btn' + (i === 0 ? ' active' : '');
      btn.setAttribute('role', 'tab');
      btn.dataset.category = c.key;
      btn.textContent = c.label;
      btn.addEventListener('click', function () {
        wrap.querySelectorAll('.sm2-insp-cat-btn').forEach(function (b) {
          b.classList.remove('active');
        });
        btn.classList.add('active');
        renderGrid(c.key);
      });
      wrap.appendChild(btn);
    });
  }

  function renderGrid(category) {
    var grid = $('sm2-emoji-grid');
    if (!grid) return;
    grid.innerHTML = '';
    (EMOJIS[category] || []).forEach(function (emoji) {
      var cell = document.createElement('button');
      cell.type = 'button';
      cell.className = 'sm2-emoji-cell';
      cell.setAttribute('role', 'option');
      cell.textContent = emoji;
      cell.addEventListener('click', function () {
        var ta = $('sm2-message-input');
        if (ta) {
          var pos = ta.selectionStart != null ? ta.selectionStart : ta.value.length;
          ta.value = ta.value.slice(0, pos) + emoji + ta.value.slice(ta.selectionEnd != null ? ta.selectionEnd : pos);
          ta.focus();
          ta.selectionStart = ta.selectionEnd = pos + emoji.length;
          ta.dispatchEvent(new Event('input', { bubbles: true }));
        }
        close();
      });
      grid.appendChild(cell);
    });
  }

  function open() {
    var modal = $('sm2-emoji-modal');
    if (!modal) return;
    renderCategories();
    renderGrid(CATEGORY_ORDER[0].key);
    modal.classList.add('active');
  }

  function close() {
    var modal = $('sm2-emoji-modal');
    if (modal) modal.classList.remove('active');
  }

  function init() {
    var openBtn = $('sm2-open-emoji');
    if (!openBtn) return;
    openBtn.addEventListener('click', function (e) {
      e.preventDefault();
      open();
    });
    var modal = $('sm2-emoji-modal');
    if (modal) {
      modal.querySelectorAll('[data-emoji-close]').forEach(function (el) {
        el.addEventListener('click', close);
      });
    }
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal && modal.classList.contains('active')) {
        close();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
